  import React, { useState, useEffect, useRef } from 'react';
  
import { supabase } from './supabase';
import { dispararActualizacionEnSegundoPlano } from './servicios/actualizarPresupuestosTotales';
import { 
  Settings, Users, Building2, Package, Layers, TrendingUp, 
  DollarSign, UserCheck, Briefcase, Plus, Edit, Trash2, 
  Save, X, Eye, Search, Filter, Calendar, FileText, User, 
  Clock, AlertCircle, CheckCircle2, XCircle, Info, Upload,
  ChevronDown, Tag, MapPin, Phone, Mail, CreditCard,
  BarChart3, Activity, Database, Wrench, Shield, AlertTriangle, RotateCcw
} from 'lucide-react';
import ProveedoresCambioTab from './ProveedoresCambioTab.jsx';

// Dropdown unificado para Admin (acceso global)
const AdminDropdown = ({ value, onChange, options, placeholder, disabled = false }) => {
  const [isOpen, setIsOpen] = React.useState(false);
  const [selectedOption, setSelectedOption] = React.useState(null);
  const dropdownRef = React.useRef(null);

  React.useEffect(() => {
    const selected = options.find((opt) => opt.value === value);
    setSelectedOption(selected);
  }, [value, options]);

  React.useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div ref={dropdownRef} style={{ position: 'relative', width: '100%', maxWidth: '100%' }}>
      <button
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        style={{
          width: '100%',
          padding: '14px 16px',
          border: '2px solid #f1f5f9',
          borderRadius: '12px',
          background: disabled ? '#f8fafc' : 'white',
          color: disabled ? '#94a3b8' : '#1e293b',
          fontSize: '16px',
          fontWeight: '500',
          textAlign: 'left',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          cursor: disabled ? 'not-allowed' : 'pointer',
          outline: 'none',
          transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
          minHeight: '44px',
          touchAction: 'manipulation',
          boxShadow: '0 4px 16px rgba(0,0,0,0.04)'
        }}
      >
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {selectedOption ? selectedOption.label : placeholder}
        </span>
        <ChevronDown size={16} style={{ transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s ease' }} />
      </button>
      {isOpen && (
        <div
          style={{
            position: 'absolute',
            top: 'calc(100% + 6px)',
            left: 0,
            right: 0,
            background: 'white',
            border: '1px solid #e2e8f0',
            borderRadius: '12px',
            boxShadow: '0 12px 30px rgba(2,6,23,0.1)',
            zIndex: 50,
            maxHeight: 240,
            overflowY: 'auto'
          }}
        >
          {options.map((opt) => (
            <div
              key={opt.value}
              onClick={() => { onChange(opt.value); setIsOpen(false); }}
              style={{
                padding: '12px 14px',
                cursor: 'pointer',
                fontSize: '14px',
                color: '#0f172a',
                background: value === opt.value ? '#eff6ff' : 'white'
              }}
            >
              {opt.label}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Configuración anti-zoom
if (typeof document !== 'undefined') {
  let metaViewport = document.querySelector('meta[name="viewport"]');
  if (!metaViewport) {
    metaViewport = document.createElement('meta');
    metaViewport.name = 'viewport';
    document.head.appendChild(metaViewport);
  }
  metaViewport.content = 'width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no, viewport-fit=cover';
}

// Colores por vista
const getVistaColor = () => {
  return {
    gradient: 'linear-gradient(135deg, #64748b 0%, #475569 100%)',
    solid: '#64748b',
    light: '#64748b10',
    description: 'Configuración del sistema'
  };
};

// Modal personalizado mejorado
function CustomModal({ isOpen, onClose, type = 'info', title, message, onConfirm, confirmText = 'Aceptar', cancelText = 'Cancelar', isMobile }) {
  // Cerrar con ESC
  useEffect(() => {
    const onEsc = (e) => { if (e.key === 'Escape') onClose?.(); };
    document.addEventListener('keydown', onEsc);
    return () => document.removeEventListener('keydown', onEsc);
  }, [onClose]);

  if (!isOpen) return null;

  const getColors = () => {
    switch (type) {
      case 'confirm':
        return { bg: '#fef3cd', border: '#f59e0b', text: '#92400e', button: '#f59e0b' };
      case 'error':
        return { bg: '#fef2f2', border: '#ef4444', text: '#dc2626', button: '#ef4444' };
      case 'success':
        return { bg: '#f0fdf4', border: '#22c55e', text: '#166534', button: '#22c55e' };
      default:
        return { bg: '#f0f9ff', border: '#3b82f6', text: '#1d4ed8', button: '#3b82f6' };
    }
  };

  const colors = getColors();

  return (
    <div onClick={onClose} style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.6)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 3000,
      padding: '20px',
      animation: 'fadeIn 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
    }}>
      <div onClick={(e) => e.stopPropagation()} style={{
        background: 'white',
        borderRadius: '16px',
        padding: '24px',
        width: '100%',
        maxWidth: isMobile ? '90%' : '400px',
        boxShadow: '0 25px 80px rgba(0,0,0,0.3)',
        border: `2px solid ${colors.border}`,
        animation: 'scaleIn 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
      }}>
        <div style={{
          textAlign: 'center',
          marginBottom: '20px'
        }}>
          <div style={{
            width: '64px',
            height: '64px',
            margin: '0 auto 16px',
            borderRadius: '32px',
            background: colors.bg,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            {type === 'confirm' && <Trash2 size={32} color={colors.text} />}
            {type === 'error' && <XCircle size={32} color={colors.text} />}
            {type === 'success' && <CheckCircle2 size={32} color={colors.text} />}
            {type === 'info' && <Info size={32} color={colors.text} />}
          </div>
          <h3 style={{
            margin: '0 0 8px 0',
            fontSize: '18px',
            fontWeight: '600',
            color: colors.text
          }}>
            {title}
          </h3>
          <div style={{
            margin: 0,
            fontSize: '14px',
            color: '#64748b',
            lineHeight: '1.6',
            textAlign: 'center'
          }}
          dangerouslySetInnerHTML={{ __html: message }}
          />
        </div>

        <div style={{
          display: 'flex',
          gap: '12px',
          justifyContent: type === 'confirm' ? 'space-between' : 'center'
        }}>
          {type === 'confirm' && (
            <button
              onClick={onClose}
              style={{
                flex: 1,
                padding: '12px 20px',
                border: '1px solid #d1d5db',
                borderRadius: '8px',
                background: 'white',
                color: '#64748b',
                fontSize: '14px',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)'
              }}
              className="button-hover"
            >
              {cancelText}
            </button>
          )}
          <button
            onClick={() => {
              if (type === 'confirm' && onConfirm) {
                onConfirm();
              }
              onClose();
            }}
            style={{
              flex: type === 'confirm' ? 1 : 'auto',
              padding: '12px 20px',
              border: 'none',
              borderRadius: '8px',
              background: colors.button,
              color: 'white',
              fontSize: '14px',
              fontWeight: '600',
              cursor: 'pointer',
              minWidth: type !== 'confirm' ? '120px' : 'auto',
              transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)'
            }}
            className="button-hover"
          >
            {confirmText}
          </button>
        </div>
      </div>
    </div>
  );
}

function Admin({ constructora }) {
  const [activeTab, setActiveTab] = useState('conceptos-entrada');
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  
  // Estados para alertas personalizadas
  const [customModal, setCustomModal] = useState({
    isOpen: false,
    type: 'info',
    title: '',
    message: '',
    onConfirm: null
  });

  // ESC handler obligatorio
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        setCustomModal(prev => ({ ...prev, isOpen: false }));
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, []);

  // Prevenir scroll del fondo cuando hay modales abiertos
  useEffect(() => {
    if (customModal.isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [customModal.isOpen]);

  // Scroll reset al cargar
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const showCustomAlert = (type, title, message, onConfirm = null) => {
    setCustomModal({
      isOpen: true,
      type,
      title,
      message,
      onConfirm
    });
  };

  // Categorías de configuración
  const categories = [
    {
      id: 'conceptos',
      title: 'Conceptos',
      description: 'Gestiona conceptos de entrada y salida',
      icon: Tag,
      color: '#2563eb',
      tabs: [
        { id: 'conceptos-entrada', label: 'Entrada', icon: TrendingUp },
        { id: 'conceptos-salida', label: 'Salida', icon: Package }
      ]
    },
    {
      id: 'proyectos',
      title: 'Estructura de Proyectos',
      description: 'Configura etapas y subetapas de obra',
      icon: Building2,
      color: '#2563eb',
      tabs: [
        { id: 'etapas', label: 'Etapas', icon: Layers },
        { id: 'subetapas', label: 'Subetapas', icon: Activity }
      ]
    },
    {
      id: 'proveedores',
      title: 'Proveedores',
      description: 'Administra proveedores de cambio y servicios',
      icon: Briefcase,
      color: '#2563eb',
      tabs: [
        { id: 'proveedores-servicios', label: 'Servicios', icon: Wrench },
        { id: 'proveedores-cambio', label: 'Cambio', icon: DollarSign }
      ]
    },
    {
      id: 'personal',
      title: 'Personal y Empresa',
      description: 'Gestiona trabajadores y datos corporativos',
      icon: Users,
      color: '#2563eb',
      tabs: [
        { id: 'trabajadores', label: 'Trabajadores', icon: UserCheck },
        { id: 'datos-constructora', label: 'Empresa', icon: Shield }
      ]
    },
    {
      id: 'economico',
      title: 'Datos Económicos',
      description: 'Índices CAC y configuración financiera',
      icon: BarChart3,
      color: '#2563eb',
      tabs: [
        { id: 'indices-cac', label: 'Índices CAC', icon: TrendingUp }
      ]
    }
  ];

  // Encontrar la categoría activa
  const activeCategory = categories.find(cat => 
    cat.tabs.some(tab => tab.id === activeTab)
  );

  const renderTabContent = () => {
    switch (activeTab) {
      case 'conceptos-entrada':
        return <ConceptosEntradaTab constructora={constructora} showCustomAlert={showCustomAlert} />;
      case 'conceptos-salida':
        return <ConceptosSalidaTab constructora={constructora} showCustomAlert={showCustomAlert} />;
      case 'indices-cac':
        return <IndicesCacTab constructora={constructora} showCustomAlert={showCustomAlert} />;
      case 'etapas':
        return <EtapasTab constructora={constructora} showCustomAlert={showCustomAlert} />;
      case 'subetapas':
        return <SubetapasTab constructora={constructora} showCustomAlert={showCustomAlert} />;
      case 'proveedores-servicios':
        return <ProveedoresServiciosTab constructora={constructora} showCustomAlert={showCustomAlert} />;
      case 'proveedores-cambio':
        return <ProveedoresCambioTab constructora={constructora} showCustomAlert={showCustomAlert} />;
      case 'trabajadores':
        return <TrabajadoresTab constructora={constructora} showCustomAlert={showCustomAlert} />;
      case 'datos-constructora':
        return <DatosConstructoraTab constructora={constructora} showCustomAlert={showCustomAlert} />;
      default:
        return <ConceptosEntradaTab constructora={constructora} showCustomAlert={showCustomAlert} />;
    }
  };

  return (
    <div style={{
      padding: isMobile ? '16px 12px 120px 12px' : '20px',
      maxWidth: '100%',
      margin: '0 auto',
      background: '#f8fafc',
      minHeight: '100vh',
      animation: 'fadeInUp 0.5s cubic-bezier(0.4, 0, 0.2, 1)'
    }}>
      {/* Header unificado */}
      <div className="header-section" style={{
        marginBottom: '24px',
        animation: 'slideInRight 0.6s cubic-bezier(0.4, 0, 0.2, 1)'
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: '16px',
          marginBottom: '16px',
          minHeight: '56px'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1, minWidth: '200px' }}>
            <div style={{
              width: '40px',
              height: '40px',
              background: 'linear-gradient(135deg, #2563eb 0%, #2563eb 100%)',              borderRadius: '12px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 4px 16px rgba(100, 116, 139, 0.25)',
              flexShrink: 0
            }}>
              <Settings size={20} color="white" />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <h1 style={{
                fontSize: isMobile ? '20px' : '24px',
                fontWeight: '700',
                color: '#0f172a',
                margin: 0,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}>
                Administración
              </h1>
              <p style={{
                fontSize: '14px',
                color: '#64748b',
                margin: 0,
                fontWeight: '400',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}>
                Configuración del sistema de {constructora?.nombre}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Categorías principales */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(280px, 1fr))',
        gap: '16px',
        marginBottom: '24px',
        alignItems: 'stretch'
      }}>
        {categories.map((category, index) => {
          const IconComponent = category.icon;
          const isActive = activeCategory?.id === category.id;
          
          return (
            <div key={category.id} style={{
              background: 'white',
              borderRadius: '16px',
              padding: '20px',
              border: `2px solid ${isActive ? category.color : '#f1f5f9'}`,
              boxShadow: isActive ? `0 8px 25px ${category.color}20` : '0 4px 16px rgba(0,0,0,0.04)',
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              cursor: 'pointer',
              animation: `slideInRight 0.${6 + index}s cubic-bezier(0.4, 0, 0.2, 1)`
            }}
            onMouseEnter={(e) => {
              if (!isActive) {
                e.currentTarget.style.borderColor = category.color;
                e.currentTarget.style.boxShadow = `0 8px 25px ${category.color}15`;
              }
            }}
            onMouseLeave={(e) => {
              if (!isActive) {
                e.currentTarget.style.borderColor = '#f1f5f9';
                e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.04)';
              }
            }}
            >
              {/* Header de categoría */}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                marginBottom: '16px'
              }}>
                <div style={{
                  width: '32px',
                  height: '32px',
                  background: `${category.color}15`,
                  borderRadius: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <IconComponent size={18} color={category.color} />
                </div>
                <div style={{ flex: 1 }}>
                  <h3 style={{
                    margin: 0,
                    fontSize: '16px',
                    fontWeight: '600',
                    color: '#1e293b'
                  }}>
                    {category.title}
                  </h3>
                  <p style={{
                    margin: 0,
                    fontSize: '12px',
                    color: '#64748b',
                    lineHeight: '1.3'
                  }}>
                    {category.description}
                  </p>
                </div>
              </div>

              {/* Tabs de la categoría */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: category.tabs.length === 1 ? '1fr' : 'repeat(2, 1fr)',
                gap: '8px'
              }}>
                {category.tabs.map((tab) => {
                  const TabIcon = tab.icon;
                  const isTabActive = activeTab === tab.id;

                  return (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id)}
                      style={{
                        padding: '12px 16px',
                        border: `1px solid ${isTabActive ? category.color : '#e2e8f0'}`,
                        borderRadius: '8px',
                        background: isTabActive ? `${category.color}10` : 'white',
                        color: isTabActive ? category.color : '#64748b',
                        fontSize: '13px',
                        fontWeight: '600',
                        cursor: 'pointer',
                        transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '6px',
                        touchAction: 'manipulation'
                      }}
                      onMouseEnter={(e) => {
                        if (!isTabActive) {
                          e.target.style.borderColor = category.color;
                          e.target.style.background = `${category.color}05`;
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!isTabActive) {
                          e.target.style.borderColor = '#e2e8f0';
                          e.target.style.background = 'white';
                        }
                      }}
                    >
                      <TabIcon size={14} />
                      {tab.label}
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      {/* Contenido activo */}
      <div style={{
        background: 'white',
        borderRadius: '16px',
        overflow: 'hidden',
        boxShadow: '0 4px 16px rgba(0,0,0,0.04)',
        border: '1px solid #f1f5f9',
        animation: 'fadeInUp 0.8s cubic-bezier(0.4, 0, 0.2, 1)'
      }}>
        {renderTabContent()}
      </div>

      {/* Modal personalizado para alertas */}
      <CustomModal
        isOpen={customModal.isOpen}
        onClose={() => setCustomModal(prev => ({ ...prev, isOpen: false }))}
        type={customModal.type}
        title={customModal.title}
        message={customModal.message}
        onConfirm={customModal.onConfirm}
        isMobile={isMobile}
      />

      <style jsx>{`
        /* Configuración anti-zoom */
        input[type="text"],
        input[type="number"],
        input[type="date"],
        input[type="email"],
        input[type="tel"],
        input[type="search"],
        input[type="month"],
        textarea,
        select {
          font-size: 16px !important;
          touch-action: manipulation;
          -webkit-appearance: none !important;
          appearance: none !important;
        }

        input:focus,
        textarea:focus,
        select:focus {
          font-size: 16px !important;
          outline: none;
          -webkit-appearance: none !important;
          appearance: none !important;
        }

        @media (max-width: 768px) {
          input, textarea, select, button {
            font-size: 16px !important;
            -webkit-appearance: none !important;
            appearance: none !important;
          }
        }

        /* Animaciones estándar */
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        @keyframes slideInRight {
          from {
            opacity: 0;
            transform: translateX(20px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }
        
        @keyframes scaleIn {
          from {
            opacity: 0;
            transform: scale(0.95);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }
        
        .loading-pulse {
          animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
        }
        
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }
        
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        
        /* Hover effects */
        .button-hover:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
        }
        
        .action-button:hover {
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
        }
      `}</style>
    </div>
  );
}
// Hook personalizado para CRUD genérico con alertas personalizadas
function useCRUD(tableName, constructoraId, showCustomAlert) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const cargarItems = async () => {
    try {
      const { data, error } = await supabase
        .from(tableName)
        .select('*')
        .eq('constructora_id', constructoraId)
        .eq('activo', true)
        .order('nombre');
      
      if (error) {
        console.error(`Error cargando ${tableName}:`, error);
      } else {
        setItems(data);
      }
    } catch (err) {
      console.error('Error:', err);
    }
    setLoading(false);
  };

  const agregarItem = async (itemData) => {
    try {
      const { data, error } = await supabase
        .from(tableName)
        .insert([{
          constructora_id: constructoraId,
          ...itemData,
          activo: true
        }])
        .select();
      
      if (error) {
        console.error(`Error guardando en ${tableName}:`, error);
        showCustomAlert('error', 'Error', 'Error al guardar: ' + error.message);
        return false;
      } else {
        cargarItems();
        showCustomAlert('success', 'Éxito', 'Elemento guardado correctamente');
        return true;
      }
    } catch (err) {
      console.error('Error:', err);
      showCustomAlert('error', 'Error', 'Error: ' + err.message);
      return false;
    }
  };

  const editarItem = async (id, itemData) => {
    try {
      const { data, error } = await supabase
        .from(tableName)
        .update(itemData)
        .eq('id', id)
        .select();
      
      if (error) {
        console.error(`Error editando en ${tableName}:`, error);
        showCustomAlert('error', 'Error', 'Error al editar: ' + error.message);
        return false;
      } else {
        cargarItems();
        showCustomAlert('success', 'Éxito', 'Elemento actualizado correctamente');
        return true;
      }
    } catch (err) {
      console.error('Error:', err);
      showCustomAlert('error', 'Error', 'Error: ' + err.message);
      return false;
    }
  };

  const eliminarItem = async (id, nombre) => {
    showCustomAlert('confirm', 
      '¿Eliminar elemento?', 
      `¿Estás seguro de eliminar "${nombre}"?\n\nEsta acción no se puede deshacer.`,
      async () => {
        try {
          const { error } = await supabase
            .from(tableName)
            .update({ activo: false })
            .eq('id', id);
          
          if (error) {
            console.error(`Error eliminando en ${tableName}:`, error);
            showCustomAlert('error', 'Error', 'Error al eliminar: ' + error.message);
            return false;
          } else {
            cargarItems();
            showCustomAlert('success', 'Éxito', 'Elemento eliminado correctamente');
            return true;
          }
        } catch (err) {
          console.error('Error:', err);
          showCustomAlert('error', 'Error', 'Error: ' + err.message);
          return false;
        }
      }
    );
  };

  useEffect(() => {
    if (constructoraId) {
      cargarItems();
    }
  }, [constructoraId, tableName]);

  return { items, loading, cargarItems, agregarItem, editarItem, eliminarItem };
}
// Componente genérico para tablas simples mejorado
function TablaSimple({ titulo, placeholder, tableName, constructora, showCustomAlert, icon: IconComponent, description }) {
  const { items, loading, agregarItem, editarItem, eliminarItem } = useCRUD(tableName, constructora?.id, showCustomAlert);
  const [showModal, setShowModal] = useState(false);
  const [editandoItem, setEditandoItem] = useState(null);
  const [nuevoItem, setNuevoItem] = useState('');
  const [guardando, setGuardando] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const abrirModal = (item = null) => {
    if (item) {
      setEditandoItem(item);
      setNuevoItem(item.nombre);
    } else {
      setEditandoItem(null);
      setNuevoItem('');
    }
    setShowModal(true);
  };

  const handleGuardar = async () => {
    if (!nuevoItem.trim()) {
      showCustomAlert('error', 'Error', 'Por favor ingresa un nombre');
      return;
    }

    setGuardando(true);
    let success;
    
    if (editandoItem) {
      success = await editarItem(editandoItem.id, { nombre: nuevoItem.trim() });
    } else {
      success = await agregarItem({ nombre: nuevoItem.trim() });
    }
    
    if (success) {
      setNuevoItem('');
      setEditandoItem(null);
      setShowModal(false);
    }
    setGuardando(false);
  };

  return (
    <div style={{padding: isMobile ? '20px 16px' : '24px 32px'}}>
      {/* Header mejorado */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: '24px',
        flexWrap: 'wrap',
        gap: '16px'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1 }}>
          {IconComponent && (
            <div style={{
              width: '32px',
              height: '32px',
              background: '#64748b15',
              borderRadius: '8px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <IconComponent size={18} color="#64748b" />
            </div>
          )}
          <div>
            <h2 style={{
              fontSize: isMobile ? '18px' : '20px',
              fontWeight: '600',
              color: '#1e293b',
              margin: '0 0 4px 0'
            }}>
              {titulo}
            </h2>
            {description && (
              <p style={{
                fontSize: '13px',
                color: '#64748b',
                margin: 0
              }}>
                {description}
              </p>
            )}
          </div>
        </div>
        
        <button 
          onClick={() => abrirModal()}
          style={{
            background: 'linear-gradient(135deg, #2563eb 0%, #2563eb 100%)',
            color: 'white',
            border: 'none',
            padding: isMobile ? '10px 16px' : '12px 20px',
            borderRadius: '10px',
            fontSize: isMobile ? '13px' : '14px',
            fontWeight: '600',
            cursor: 'pointer',
            boxShadow: '0 4px 16px rgba(37, 99, 235, 0.25)',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)'
          }}
          className="button-hover"
        >
          <Plus size={16} />
          Añadir
        </button>
      </div>
      
      {loading ? (
        <div style={{
          textAlign: 'center',
          padding: '60px 20px',
          color: '#64748b'
        }}>
          <div style={{
            display: 'inline-block',
            width: '32px',
            height: '32px',
            border: '3px solid #e2e8f0',
            borderTop: '3px solid #64748b',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            marginBottom: '16px'
          }}></div>
          <p style={{ fontSize: '15px', margin: 0 }}>Cargando...</p>
        </div>
      ) : items.length === 0 ? (
        <div style={{
          background: '#f8fafc',
          borderRadius: '12px',
          padding: isMobile ? '40px 20px' : '60px 40px',
          textAlign: 'center',
          border: '2px dashed #cbd5e1'
        }}>
          <div style={{
            width: '64px',
            height: '64px',
            background: '#e2e8f0',
            borderRadius: '32px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 20px'
          }}>
            {IconComponent ? <IconComponent size={32} color="#64748b" /> : <Database size={32} color="#64748b" />}
          </div>
          <h3 style={{
            fontSize: '18px',
            fontWeight: '600',
            color: '#1e293b',
            marginBottom: '8px'
          }}>
            No hay elementos
          </h3>
          <p style={{
            fontSize: '14px',
            color: '#64748b',
            marginBottom: '24px'
          }}>
            Comienza agregando tu primer elemento
          </p>
                      <button
              onClick={() => abrirModal()}
              style={{
                background: 'linear-gradient(135deg, #2563eb 0%, #2563eb 100%)',
                color: 'white',
                border: 'none',
                padding: '12px 24px',
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: '600',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                margin: '0 auto'
              }}
              className="button-hover"
            >
              <Plus size={16} />
              Añadir primero
            </button>
        </div>
      ) : (
        <div style={{
          background: 'white',
          border: '1px solid #f1f5f9',
          borderRadius: '12px',
          overflow: 'hidden'
        }}>
          <div style={{overflowX: 'auto'}}>
            <table style={{
              width: '100%',
              borderCollapse: 'collapse',
              minWidth: isMobile ? '400px' : '600px'
            }}>
              <thead>
                <tr style={{
                  background: '#f8fafc',
                  borderBottom: '1px solid #e2e8f0'
                }}>
                  <th style={{
                    padding: isMobile ? '12px 16px' : '16px 20px',
                    textAlign: 'left',
                    fontWeight: '600',
                    color: '#374151',
                    fontSize: isMobile ? '13px' : '14px'
                  }}>
                    Nombre
                  </th>
                  <th style={{
                    padding: isMobile ? '12px 16px' : '16px 20px',
                    textAlign: 'left',
                    fontWeight: '600',
                    color: '#374151',
                    fontSize: isMobile ? '13px' : '14px'
                  }}>
                    Fecha
                  </th>
                  <th style={{
                    padding: isMobile ? '12px 16px' : '16px 20px',
                    textAlign: 'center',
                    fontWeight: '600',
                    color: '#374151',
                    fontSize: isMobile ? '13px' : '14px',
                    width: isMobile ? '120px' : '160px'
                  }}>
                    Acciones
                  </th>
                </tr>
              </thead>
              <tbody>
                {items.map((item, index) => (
                  <tr key={item.id} style={{
                    borderBottom: index < items.length - 1 ? '1px solid #f1f5f9' : 'none',
                    transition: 'background-color 0.2s ease'
                  }}
                  onMouseEnter={(e) => {
                    if (!isMobile) e.target.parentElement.style.backgroundColor = '#f8fafc';
                  }}
                  onMouseLeave={(e) => {
                    if (!isMobile) e.target.parentElement.style.backgroundColor = 'transparent';
                  }}
                  >
                    <td style={{
                      padding: isMobile ? '12px 16px' : '16px 20px',
                      color: '#1e293b',
                      fontSize: isMobile ? '13px' : '14px',
                      fontWeight: '500'
                    }}>
                      {item.nombre}
                    </td>
                    <td style={{
                      padding: isMobile ? '12px 16px' : '16px 20px',
                      color: '#64748b',
                      fontSize: isMobile ? '12px' : '13px'
                    }}>
                      {new Date(item.created_at).toLocaleDateString('es-ES')}
                    </td>
                    <td style={{
                      padding: isMobile ? '12px 16px' : '16px 20px',
                      textAlign: 'center'
                    }}>
                      <div style={{
                        display: 'flex',
                        gap: '8px',
                        justifyContent: 'center'
                      }}>
                        <button 
                          onClick={() => abrirModal(item)}
                          style={{
                            background: '#eff6ff',
                            border: '1px solid #3b82f6',
                            borderRadius: '6px',
                            padding: isMobile ? '6px 8px' : '8px 12px',
                            color: '#3b82f6',
                            fontSize: isMobile ? '11px' : '12px',
                            fontWeight: '500',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px',
                            transition: 'all 0.2s ease'
                          }}
                          onMouseEnter={(e) => {
                            e.target.style.background = '#dbeafe';
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.background = '#eff6ff';
                          }}
                        >
                          <Edit size={12} />
                          Editar
                        </button>
                        <button 
                          onClick={() => eliminarItem(item.id, item.nombre)}
                          style={{
                            background: '#fef2f2',
                            border: '1px solid #ef4444',
                            borderRadius: '6px',
                            padding: isMobile ? '6px 8px' : '8px 12px',
                            color: '#ef4444',
                            fontSize: isMobile ? '11px' : '12px',
                            fontWeight: '500',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px',
                            transition: 'all 0.2s ease'
                          }}
                          onMouseEnter={(e) => {
                            e.target.style.background = '#fee2e2';
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.background = '#fef2f2';
                          }}
                        >
                          <Trash2 size={12} />
                          Eliminar
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Modal mejorado */}
      {showModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.6)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: isMobile ? '20px' : '0',
          animation: 'fadeIn 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
        }}>
          <div style={{
            background: 'white',
            borderRadius: '16px',
            padding: isMobile ? '20px' : '24px',
            width: isMobile ? '100%' : '400px',
            maxWidth: '90vw',
            boxShadow: '0 25px 80px rgba(0,0,0,0.3)',
            animation: 'scaleIn 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              marginBottom: '20px'
            }}>
              {IconComponent && (
                <div style={{
                  width: '32px',
                  height: '32px',
                  background: '#64748b15',
                  borderRadius: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <IconComponent size={18} color="#64748b" />
                </div>
              )}
              <h3 style={{
                margin: 0,
                fontSize: isMobile ? '18px' : '20px',
                fontWeight: '700',
                color: '#1e293b'
              }}>
                {editandoItem ? 'Editar' : 'Añadir nuevo'}
              </h3>
            </div>
            
            <input
              type="text"
              placeholder={placeholder}
              value={nuevoItem}
              onChange={(e) => setNuevoItem(e.target.value)}
              style={{
                width: '100%',
                padding: '14px 16px',
                border: '2px solid #f1f5f9',
                borderRadius: '10px',
                fontSize: '16px',
                marginBottom: '20px',
                boxSizing: 'border-box',
                outline: 'none',
                transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                touchAction: 'manipulation'
              }}
              onKeyPress={(e) => e.key === 'Enter' && handleGuardar()}
              onFocus={(e) => {
                e.target.style.borderColor = '#64748b';
                e.target.style.boxShadow = '0 0 0 3px rgba(100, 116, 139, 0.1)';
              }}
              onBlur={(e) => {
                e.target.style.borderColor = '#f1f5f9';
                e.target.style.boxShadow = 'none';
              }}
            />
            
            <div style={{
              display: 'flex',
              gap: '12px',
              justifyContent: 'flex-end'
            }}>
              <button
                onClick={() => {
                  setShowModal(false);
                  setEditandoItem(null);
                  setNuevoItem('');
                }}
                style={{
                  padding: '10px 20px',
                  border: '1px solid #d1d5db',
                  borderRadius: '8px',
                  background: 'white',
                  color: '#64748b',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)'
                }}
                className="button-hover"
              >
                Cancelar
              </button>
              <button
                onClick={handleGuardar}
                disabled={guardando}
                style={{
                  padding: '10px 20px',
                  border: 'none',
                  borderRadius: '8px',
                  background: guardando ? '#94a3b8' : 'linear-gradient(135deg, #2563eb 0%, #2563eb 100%)',
                  color: 'white',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: guardando ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)'
                }}
                className={!guardando ? "button-hover" : ""}
              >
                {guardando ? (
                  <>
                    <div className="loading-pulse" style={{
                      width: '16px',
                      height: '16px',
                      border: '2px solid rgba(255,255,255,0.3)',
                      borderTop: '2px solid white',
                      borderRadius: '50%',
                      animation: 'spin 1s linear infinite'
                    }}></div>
                    Guardando...
                  </>
                ) : (
                  <>
                    <Save size={16} />
                    {editandoItem ? 'Actualizar' : 'Guardar'}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
// Conceptos de Entrada - Versión mejorada
function ConceptosEntradaTab({ constructora, showCustomAlert }) {
  const [conceptos, setConceptos] = useState([]);
  const [todosLosConceptos, setTodosLosConceptos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showModal3Opciones, setShowModal3Opciones] = useState(false);
  const [editandoConcepto, setEditandoConcepto] = useState(null);
  const [formData, setFormData] = useState({
    nombre: '',
    caja_destino_default: 'proyecto',
    caja_origen_default: 'cliente'
  });
  const [guardando, setGuardando] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [filtroActivos, setFiltroActivos] = useState(true);
  const [opcionesEdicion, setOpcionesEdicion] = useState({});

  // Prevenir scroll del fondo cuando hay modales abiertos
  useEffect(() => {
    if (showModal || showModal3Opciones) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [showModal, showModal3Opciones]);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const cargarTodosLosConceptos = async () => {
    try {
      console.log('Cargando todos los conceptos para constructora:', constructora.id);
      
      const { data: todosConceptos, error: errorTodas } = await supabase
        .from('conceptos_entrada')
        .select('*')
        .eq('constructora_id', constructora.id);

      if (errorTodas) {
        console.error('Error cargando todos los conceptos:', errorTodas);
      } else {
        console.log('Todos los conceptos cargados:', todosConceptos?.length || 0);
        console.log('Conceptos activos:', todosConceptos?.filter(c => c.deleted_at === null).length || 0);
        console.log('Conceptos eliminados:', todosConceptos?.filter(c => c.deleted_at !== null).length || 0);
        setTodosLosConceptos(todosConceptos);
      }
    } catch (err) {
      console.error('Error:', err);
    }
  };

  const cargarConceptos = async () => {
    try {
      setLoading(true);
      console.log('Cargando conceptos, filtroActivos:', filtroActivos);
      
      // Cargar conceptos filtrados (activos o eliminados)
      let query = supabase
        .from('conceptos_entrada')
        .select('*')
        .eq('constructora_id', constructora.id);

      if (filtroActivos) {
        // Mostrar solo conceptos activos (deleted_at es null)
        query = query.is('deleted_at', null);
      } else {
        // Mostrar solo conceptos eliminados (deleted_at no es null)
        query = query.not('deleted_at', 'is', null);
      }

      const { data: conceptosFiltrados, error: errorFiltrados } = await query.order('nombre', { ascending: true });

      if (errorFiltrados) {
        console.error('Error cargando conceptos filtrados:', errorFiltrados);
      } else {
        console.log('Conceptos cargados:', conceptosFiltrados?.length || 0);
        console.log('Conceptos:', conceptosFiltrados);
        setConceptos(conceptosFiltrados || []);
      }
    } catch (err) {
      console.error('Error:', err);
    }
    setLoading(false);
  };

  // Cargar todos los conceptos solo cuando cambia la constructora
  useEffect(() => {
    if (constructora?.id) {
      cargarTodosLosConceptos();
    }
  }, [constructora]);

  // Cargar conceptos filtrados cuando cambia el filtro
  useEffect(() => {
    if (constructora?.id) {
      cargarConceptos();
    }
  }, [constructora, filtroActivos]);

  // Recargar todos los conceptos cuando cambia el filtro para actualizar contadores
  useEffect(() => {
    if (constructora?.id) {
      cargarTodosLosConceptos();
    }
  }, [constructora, filtroActivos]);

  const abrirModal = (concepto = null) => {
    if (concepto) {
      setEditandoConcepto(concepto);
      setFormData({
        nombre: concepto.nombre,
        caja_destino_default: concepto.caja_destino_default || 'proyecto',
        caja_origen_default: concepto.caja_origen_default || 'cliente'
      });
    } else {
      setEditandoConcepto(null);
      setFormData({
        nombre: '',
        caja_destino_default: 'proyecto',
        caja_origen_default: 'cliente'
      });
    }
    setShowModal(true);
  };

  const guardarConcepto = async () => {
    if (!formData.nombre.trim()) {
      showCustomAlert('error', 'Error', 'Por favor ingresa un nombre para el concepto');
      return;
    }

    try {
      setGuardando(true);

      if (editandoConcepto) {
        // Verificar si hay cambio de nombre
        if (formData.nombre.trim() !== editandoConcepto.nombre.trim()) {
          // Analizar impacto antes de editar
          const impactoResponse = await fetch('http://localhost:4001/api/conceptos-entrada/analizar-impacto', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              concepto_id: editandoConcepto.id,
              accion: 'editar',
              cambios: { nombre: formData.nombre.trim() }
            })
          });

          if (!impactoResponse.ok) {
            throw new Error('Error analizando impacto');
          }

          const impactoData = await impactoResponse.json();
          
          if (impactoData.impacto && impactoData.impacto.entradas && impactoData.impacto.entradas.total_entradas > 0) {
            // Mostrar modal de 3 opciones
            setOpcionesEdicion({
              concepto_id: editandoConcepto.id,
              cambios: formData,
              impacto: impactoData.impacto,
              recomendaciones: impactoData.recomendaciones
            });
            setShowModal3Opciones(true);
            setShowModal(false);
            return;
          }
        }

        // Edición directa
        const editarResponse = await fetch('http://localhost:4001/api/conceptos-entrada/editar', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            concepto_id: editandoConcepto.id,
            cambios: formData,
            opciones: {
              usuarioId: null
            }
          })
        });

        if (!editarResponse.ok) {
          throw new Error('Error editando concepto');
        }

        showCustomAlert('success', 'Éxito', 'Concepto actualizado correctamente');
      } else {
        // Crear nuevo concepto
        const { data, error } = await supabase
          .from('conceptos_entrada')
          .insert({
            constructora_id: constructora.id,
            nombre: formData.nombre.trim(),
            caja_destino_default: formData.caja_destino_default,
            caja_origen_default: formData.caja_origen_default,
            activo: true,
            created_by: null
          })
          .select()
          .single();

        if (error) {
          throw new Error('Error creando concepto: ' + error.message);
        }

        showCustomAlert('success', 'Éxito', 'Concepto creado correctamente');
      }

      setShowModal(false);
      
      // Actualizar datos inmediatamente para mejor UX
      await Promise.all([
        cargarConceptos(),
        cargarTodosLosConceptos()
      ]);
    } catch (error) {
      console.error('Error:', error);
      showCustomAlert('error', 'Error', error.message);
    } finally {
      setGuardando(false);
    }
  };

  const ejecutarOpcionEdicion = async (opcion) => {
    try {
      setGuardando(true);
      const { concepto_id, cambios } = opcionesEdicion;

      let mensaje = '';
      
      if (opcion === 'incluir-pasados') {
        // Para "Incluir Pasados": editar directamente el concepto existente
        const editarResponse = await fetch('http://localhost:4001/api/conceptos-entrada/editar', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            concepto_id: concepto_id,
            cambios: cambios,
            opciones: { 
              afectarRegistrosPasados: true,
              usuarioId: null
            }
          })
        });

        if (!editarResponse.ok) {
          throw new Error('Error editando concepto');
        }

        mensaje = 'Concepto actualizado. Todas las entradas (pasadas y futuras) usarán el nuevo nombre.';
      } else {
        // Para "Solo Futuros" y "Crear Nuevo": crear nuevo concepto y desactivar el viejo
        const crearNuevoResponse = await fetch('http://localhost:4001/api/conceptos-entrada/crear-nuevo', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            concepto_id_viejo: concepto_id,
            nuevo_concepto: cambios,
            usuario_id: null,
            opcion: opcion
          })
        });

        if (!crearNuevoResponse.ok) {
          throw new Error('Error creando nuevo concepto');
        }

        switch (opcion) {
          case 'solo-futuros':
            mensaje = 'Nuevo concepto creado. Las entradas existentes mantienen el nombre anterior.';
            break;
          case 'crear-nuevo':
            mensaje = 'Nuevo concepto creado y concepto anterior desactivado.';
            break;
          default:
            mensaje = 'Nuevo concepto creado correctamente.';
        }
      }

      showCustomAlert('success', 'Éxito', mensaje);

      setShowModal3Opciones(false);
      
      // Actualizar datos inmediatamente para mejor UX
      await Promise.all([
        cargarConceptos(),
        cargarTodosLosConceptos()
      ]);
    } catch (error) {
      console.error('Error:', error);
      showCustomAlert('error', 'Error', error.message);
    } finally {
      setGuardando(false);
    }
  };

  const eliminarConcepto = async (id, nombre) => {
    try {
      console.log('Analizando impacto antes de eliminar:', nombre, 'ID:', id);
      
      // Analizar impacto antes de eliminar
      const impactoResponse = await fetch('http://localhost:4001/api/conceptos-entrada/analizar-impacto', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          concepto_id: id,
          accion: 'eliminar'
        })
      });

      if (!impactoResponse.ok) {
        throw new Error('Error analizando impacto');
      }

      const impactoData = await impactoResponse.json();
      console.log('Análisis de impacto:', impactoData);
      
      // Construir mensaje de confirmación con HTML
      let mensaje = `¿Estás seguro de que quieres eliminar el concepto <strong>"${nombre}"</strong>?`;
      
      if (impactoData.impacto && impactoData.impacto.entradas && impactoData.impacto.entradas.total_entradas > 0) {
        mensaje += `<br><br><strong>ADVERTENCIA:</strong> Este concepto tiene <strong>${impactoData.impacto.entradas.total_entradas} entrada(s)</strong> asociada(s).`;
        mensaje += `<br><br><strong>Comportamiento:</strong>`;
        mensaje += `<br>• El concepto se marcará como eliminado`;
        mensaje += `<br>• Las entradas existentes mantendrán su referencia al concepto`;
        mensaje += `<br>• Las entradas seguirán apareciendo en reportes históricos`;
        mensaje += `<br>• Para nuevas entradas, deberás usar otro concepto`;
      } else {
        mensaje += `<br><br>Este concepto no tiene entradas asociadas.`;
      }
      
      mensaje += `<br><br>¿Deseas continuar con la eliminación?`;
      
      console.log('Mostrando confirmación de eliminación con mensaje:', mensaje);
      
      showCustomAlert('confirm', 'Confirmar eliminación', mensaje, async () => {
        console.log('Usuario confirmó eliminación, ejecutando...');
        await ejecutarEliminacion(id, nombre);
      });
    } catch (error) {
      console.error('Error en eliminarConcepto:', error);
      showCustomAlert('error', 'Error', error.message);
    }
  };



  const ejecutarEliminacion = async (id, nombre) => {
    try {
      console.log('Ejecutando eliminación del concepto:', id);
      
      const response = await fetch('http://localhost:4001/api/conceptos-entrada/eliminar', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          concepto_id: id,
          confirmar: true,
          opciones: {
            usuario_id: null
          }
        })
      });

      if (!response.ok) {
        throw new Error('Error eliminando concepto');
      }

      const result = await response.json();
      console.log('Respuesta del servidor:', result);

      // Actualizar datos inmediatamente
      console.log('Actualizando datos...');
      await Promise.all([
        cargarConceptos(),
        cargarTodosLosConceptos()
      ]);
      console.log('Datos actualizados');

      showCustomAlert('success', 'Éxito', `Concepto "${nombre}" eliminado correctamente.`);
    } catch (error) {
      console.error('Error en ejecutarEliminacion:', error);
      showCustomAlert('error', 'Error', error.message);
    }
  };

  const restaurarConcepto = async (id, nombre) => {
    try {
      console.log('Restaurando concepto:', nombre, 'ID:', id);
      
      const response = await fetch('http://localhost:4001/api/conceptos-entrada/restaurar', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          concepto_id: id,
          opciones: {
            usuario_id: null
          }
        })
      });

      if (!response.ok) {
        throw new Error('Error restaurando concepto');
      }

      const result = await response.json();
      console.log('Respuesta del servidor (restaurar):', result);

      // Actualizar datos inmediatamente
      console.log('Actualizando datos después de restaurar...');
      await Promise.all([
        cargarConceptos(),
        cargarTodosLosConceptos()
      ]);
      console.log('Datos actualizados después de restaurar');

      showCustomAlert('success', 'Éxito', 'Concepto restaurado correctamente. Ahora aparece en la lista de activos.');
    } catch (error) {
      console.error('Error en restaurarConcepto:', error);
      showCustomAlert('error', 'Error', error.message);
    }
  };

  const getDestinoTexto = (destino) => {
    switch(destino) {
      case 'empresa': return 'Caja Empresa';
      case 'proyecto': return 'Caja Proyecto';
      case 'mano_obra': return 'Caja Mano Obra';
      default: return destino;
    }
  };

  const getOrigenTexto = (origen) => {
    switch(origen) {
      case 'cliente': return 'Cliente';
      case 'empresa': return 'Caja empresa';
      case 'proyecto': return 'Caja proyecto';
      case 'mano_obra': return 'Caja mano de obra';
      default: return origen;
    }
  };

  const getDestinoColor = (destino) => {
    switch(destino) {
      case 'empresa': return { bg: '#dcfce7', color: '#166534' };
      case 'proyecto': return { bg: '#dbeafe', color: '#1d4ed8' };
      case 'mano_obra': return { bg: '#f3e8ff', color: '#7c3aed' };
      default: return { bg: '#f1f5f9', color: '#64748b' };
    }
  };

  const getOrigenColor = (origen) => {
    switch(origen) {
      case 'cliente': return { bg: '#e0f2fe', color: '#0369a1' };
      case 'empresa': return { bg: '#fef3c7', color: '#92400e' };
      case 'proyecto': return { bg: '#dbeafe', color: '#1d4ed8' };
      case 'mano_obra': return { bg: '#f3e8ff', color: '#7c3aed' };
      default: return { bg: '#f1f5f9', color: '#64748b' };
    }
  };

  // Dropdown personalizado reutilizable (copiado de Cobros y adaptado)
  const CustomDropdown = ({ value, onChange, options, placeholder, disabled = false }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [selectedOption, setSelectedOption] = useState(null);
    const dropdownRef = useRef(null);

    useEffect(() => {
      const selected = options.find(opt => opt.value === value);
      setSelectedOption(selected);
    }, [value, options]);

    useEffect(() => {
      const handleClickOutside = (event) => {
        if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
          setIsOpen(false);
        }
      };
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    return (
      <div ref={dropdownRef} style={{ position: 'relative', width: '100%', maxWidth: '100%' }}>
        <button
          type="button"
          onClick={() => !disabled && setIsOpen(!isOpen)}
          disabled={disabled}
          style={{
            width: '100%',
            padding: '14px 16px',
            border: '2px solid #f1f5f9',
            borderRadius: '12px',
            background: disabled ? '#f8fafc' : 'white',
            color: disabled ? '#94a3b8' : '#1e293b',
            fontSize: '16px',
            fontWeight: '500',
            textAlign: 'left',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            cursor: disabled ? 'not-allowed' : 'pointer',
            outline: 'none',
            transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
            minHeight: '44px',
            touchAction: 'manipulation',
            boxShadow: '0 4px 16px rgba(0,0,0,0.04)'
          }}
          onFocus={(e) => {
            e.target.style.borderColor = '#3b82f6';
            e.target.style.boxShadow = '0 0 0 3px rgba(59, 130, 246, 0.1)';
          }}
          onBlur={(e) => {
            e.target.style.borderColor = '#f1f5f9';
            e.target.style.boxShadow = '0 4px 16px rgba(0,0,0,0.04)';
          }}
        >
          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {selectedOption ? selectedOption.label : placeholder}
          </span>
          <ChevronDown size={16} style={{ transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s ease' }} />
        </button>
        {isOpen && (
          <div
            style={{
              position: 'absolute',
              top: 'calc(100% + 6px)',
              left: 0,
              right: 0,
              background: 'white',
              border: '1px solid #e2e8f0',
              borderRadius: '12px',
              boxShadow: '0 12px 30px rgba(2,6,23,0.1)',
              zIndex: 50,
              maxHeight: 240,
              overflowY: 'auto'
            }}
          >
            {options.map((opt) => (
              <div
                key={opt.value}
                onClick={() => { onChange(opt.value); setIsOpen(false); }}
                style={{
                  padding: '12px 14px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  color: '#0f172a',
                  background: value === opt.value ? '#eff6ff' : 'white'
                }}
              >
                {opt.label}
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };
  // Calcular contadores en tiempo real
  const conceptosActivos = todosLosConceptos.filter(c => c.deleted_at === null).length;
  const conceptosEliminados = todosLosConceptos.filter(c => c.deleted_at !== null).length;
  return (
    <div style={{padding: isMobile ? '20px 16px' : '24px 32px'}}>
      {/* Header mejorado */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: '24px',
        flexWrap: 'wrap',
        gap: '16px'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1 }}>
          <div style={{
            width: '32px',
            height: '32px',
            background: '#10b98115',
            borderRadius: '8px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <TrendingUp size={18} color="#10b981" />
          </div>
          <div>
            <h2 style={{
              fontSize: isMobile ? '18px' : '20px',
              fontWeight: '600',
              color: '#1e293b',
              margin: '0 0 4px 0'
            }}>
              Conceptos de Entrada
            </h2>
            <p style={{
              fontSize: '13px',
              color: '#64748b',
              margin: 0
            }}>
              Configura los conceptos y sus cajas por defecto
            </p>
          </div>
        </div>
        
        <button 
          onClick={() => abrirModal()}
          style={{
            background: 'linear-gradient(135deg, #2563eb 0%, #2563eb 100%)',
            color: 'white',
            border: 'none',
            padding: isMobile ? '10px 16px' : '12px 20px',
            borderRadius: '10px',
            fontSize: isMobile ? '13px' : '14px',
            fontWeight: '600',
            cursor: 'pointer',
            boxShadow: '0 4px 16px rgba(16, 185, 129, 0.25)',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)'
          }}
          className="button-hover"
        >
          <Plus size={16} />
          Añadir concepto
        </button>
      </div>

      {/* Tabs */}
      <div style={{
        display: 'flex',
        borderBottom: '2px solid #e5e7eb',
        marginBottom: '24px',
        gap: '0',
        position: 'relative'
      }}>
        <button
          onClick={() => setFiltroActivos(true)}
          style={{
            background: 'transparent',
            border: 'none',
            padding: '12px 24px',
            fontSize: '14px',
            fontWeight: '600',
            cursor: 'pointer',
            color: filtroActivos ? '#10b981' : '#6b7280',
            borderBottom: filtroActivos ? '2px solid #10b981' : '2px solid transparent',
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            flex: 1,
            textAlign: 'center',
            position: 'relative',
            zIndex: 2
          }}
          onMouseEnter={(e) => {
            if (!filtroActivos) {
              e.target.style.color = '#10b981';
              e.target.style.borderBottom = '2px solid #10b981';
            }
          }}
          onMouseLeave={(e) => {
            if (!filtroActivos) {
              e.target.style.color = '#6b7280';
              e.target.style.borderBottom = '2px solid transparent';
            }
          }}
        >
          Activos ({conceptosActivos})
        </button>
        <button
          onClick={() => setFiltroActivos(false)}
          style={{
            background: 'transparent',
            border: 'none',
            padding: '12px 24px',
            fontSize: '14px',
            fontWeight: '600',
            cursor: 'pointer',
            color: !filtroActivos ? '#ef4444' : '#6b7280',
            borderBottom: !filtroActivos ? '2px solid #ef4444' : '2px solid transparent',
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            flex: 1,
            textAlign: 'center',
            position: 'relative',
            zIndex: 2
          }}
          onMouseEnter={(e) => {
            if (filtroActivos) {
              e.target.style.color = '#ef4444';
              e.target.style.borderBottom = '2px solid #ef4444';
            }
          }}
          onMouseLeave={(e) => {
            if (filtroActivos) {
              e.target.style.color = '#6b7280';
              e.target.style.borderBottom = '2px solid transparent';
            }
          }}
        >
          Eliminados ({conceptosEliminados})
        </button>
      </div>
      
      <div style={{
        opacity: loading ? 0.6 : 1,
        transition: 'opacity 0.3s ease-in-out'
      }}>
        {loading ? (
          <div style={{
            textAlign: 'center',
            padding: '60px 20px',
            color: '#64748b'
          }}>
            <div style={{
              display: 'inline-block',
              width: '32px',
              height: '32px',
              border: '3px solid #e2e8f0',
              borderTop: '3px solid #10b981',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite',
              marginBottom: '16px'
            }}></div>
            <p style={{ fontSize: '15px', margin: 0 }}>Cargando conceptos...</p>
          </div>
        ) : conceptos.length === 0 ? (
        <div style={{
          background: '#f0fdf4',
          borderRadius: '12px',
          padding: isMobile ? '40px 20px' : '60px 40px',
          textAlign: 'center',
          border: '2px dashed #bbf7d0'
        }}>
          <div style={{
            width: '64px',
            height: '64px',
            background: '#dcfce7',
            borderRadius: '32px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 20px'
          }}>
            <TrendingUp size={32} color="#10b981" />
          </div>
          <h3 style={{
            fontSize: '18px',
            fontWeight: '600',
            color: '#166534',
            marginBottom: '8px'
          }}>
            No hay conceptos de entrada
          </h3>
          <p style={{
            fontSize: '14px',
            color: '#059669',
            marginBottom: '24px'
          }}>
            Configura los conceptos como Honorarios, Adelantos, etc.
          </p>
          <button
            onClick={() => abrirModal()}
            style={{
              background: 'linear-gradient(135deg, #2563eb 0%, #2563eb 100%)',
              color: 'white',
              border: 'none',
              padding: '12px 24px',
              borderRadius: '8px',
              fontSize: '14px',
              fontWeight: '600',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              margin: '0 auto'
            }}
            className="button-hover"
          >
            <Plus size={16} />
            Crear Primer Concepto
          </button>
        </div>
      ) : (
        <div style={{
          background: 'white',
          border: '1px solid #f1f5f9',
          borderRadius: '12px',
          overflow: 'hidden'
        }}>
          <div style={{overflowX: 'auto'}}>
            <table style={{
              width: '100%',
              borderCollapse: 'collapse',
              minWidth: isMobile ? '600px' : '800px'
            }}>
              <thead>
                <tr style={{
                  background: '#f8fafc',
                  borderBottom: '1px solid #e2e8f0'
                }}>
                  <th style={{
                    padding: isMobile ? '12px 16px' : '16px 20px',
                    textAlign: 'left',
                    fontWeight: '600',
                    color: '#374151',
                    fontSize: isMobile ? '12px' : '14px'
                  }}>
                    Nombre
                  </th>
                  <th style={{
                    padding: isMobile ? '12px 16px' : '16px 20px',
                    textAlign: 'left',
                    fontWeight: '600',
                    color: '#374151',
                    fontSize: isMobile ? '12px' : '14px'
                  }}>
                    Origen Default
                  </th>
                  <th style={{
                    padding: isMobile ? '12px 16px' : '16px 20px',
                    textAlign: 'left',
                    fontWeight: '600',
                    color: '#374151',
                    fontSize: isMobile ? '12px' : '14px'
                  }}>
                    Destino Default
                  </th>
                  <th style={{
                    padding: isMobile ? '12px 16px' : '16px 20px',
                    textAlign: 'center',
                    fontWeight: '600',
                    color: '#374151',
                    fontSize: isMobile ? '12px' : '14px',
                    width: isMobile ? '120px' : '160px'
                  }}>
                    Acciones
                  </th>
                </tr>
              </thead>
              <tbody>
                {conceptos.map((concepto, index) => {
                  const destinoColor = getDestinoColor(concepto.caja_destino_default);
                  const origenColor = getOrigenColor(concepto.caja_origen_default);
                  
                  return (
                    <tr key={concepto.id} style={{
                      borderBottom: index < conceptos.length - 1 ? '1px solid #f1f5f9' : 'none',
                      transition: 'background-color 0.2s ease'
                    }}
                    onMouseEnter={(e) => {
                      if (!isMobile) e.target.parentElement.style.backgroundColor = '#f8fafc';
                    }}
                    onMouseLeave={(e) => {
                      if (!isMobile) e.target.parentElement.style.backgroundColor = 'transparent';
                    }}
                    >
                      <td style={{
                        padding: isMobile ? '12px 16px' : '16px 20px',
                        color: '#1e293b',
                        fontSize: isMobile ? '13px' : '14px',
                        fontWeight: '600'
                      }}>
                        {concepto.nombre}
                      </td>
                      <td style={{
                        padding: isMobile ? '12px 16px' : '16px 20px',
                        fontSize: isMobile ? '12px' : '13px'
                      }}>
                        <span style={{
                          background: origenColor.bg,
                          color: origenColor.color,
                          padding: '4px 8px',
                          borderRadius: '6px',
                          fontSize: isMobile ? '11px' : '12px',
                          fontWeight: '500'
                        }}>
                          {getOrigenTexto(concepto.caja_origen_default)}
                        </span>
                      </td>
                      <td style={{
                        padding: isMobile ? '12px 16px' : '16px 20px',
                        fontSize: isMobile ? '12px' : '13px'
                      }}>
                        <span style={{
                          background: destinoColor.bg,
                          color: destinoColor.color,
                          padding: '4px 8px',
                          borderRadius: '6px',
                          fontSize: isMobile ? '11px' : '12px',
                          fontWeight: '500'
                        }}>
                          {getDestinoTexto(concepto.caja_destino_default)}
                        </span>
                      </td>
                      <td style={{
                        padding: isMobile ? '12px 16px' : '16px 20px',
                        textAlign: 'center'
                      }}>
                        <div style={{
                          display: 'flex',
                          gap: '8px',
                          justifyContent: 'center'
                        }}>
                          {filtroActivos ? (
                            <>
                              <button 
                                onClick={() => abrirModal(concepto)}
                                style={{
                                  background: '#eff6ff',
                                  border: '1px solid #3b82f6',
                                  borderRadius: '6px',
                                  padding: isMobile ? '6px 8px' : '8px 12px',
                                  color: '#3b82f6',
                                  fontSize: isMobile ? '11px' : '12px',
                                  fontWeight: '500',
                                  cursor: 'pointer',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '4px',
                                  transition: 'all 0.2s ease'
                                }}
                                onMouseEnter={(e) => {
                                  e.target.style.background = '#dbeafe';
                                }}
                                onMouseLeave={(e) => {
                                  e.target.style.background = '#eff6ff';
                                }}
                              >
                                <Edit size={12} />
                                Editar
                              </button>
                              <button 
                                onClick={() => eliminarConcepto(concepto.id, concepto.nombre)}
                                style={{
                                  background: '#fef2f2',
                                  border: '1px solid #ef4444',
                                  borderRadius: '6px',
                                  padding: isMobile ? '6px 8px' : '8px 12px',
                                  color: '#ef4444',
                                  fontSize: isMobile ? '11px' : '12px',
                                  fontWeight: '500',
                                  cursor: 'pointer',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '4px',
                                  transition: 'all 0.2s ease'
                                }}
                                onMouseEnter={(e) => {
                                  e.target.style.background = '#fee2e2';
                                }}
                                onMouseLeave={(e) => {
                                  e.target.style.background = '#fef2f2';
                                }}
                              >
                                <Trash2 size={12} />
                                Eliminar
                              </button>
                            </>
                          ) : (
                            <button 
                              onClick={() => restaurarConcepto(concepto.id, concepto.nombre)}
                              style={{
                                background: '#f0fdf4',
                                border: '1px solid #22c55e',
                                borderRadius: '6px',
                                padding: isMobile ? '6px 8px' : '8px 12px',
                                color: '#22c55e',
                                fontSize: isMobile ? '11px' : '12px',
                                fontWeight: '500',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '4px',
                                transition: 'all 0.2s ease'
                              }}
                              onMouseEnter={(e) => {
                                e.target.style.background = '#dcfce7';
                              }}
                              onMouseLeave={(e) => {
                                e.target.style.background = '#f0fdf4';
                              }}
                            >
                              <CheckCircle2 size={12} />
                              Restaurar
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
                        </table>
          </div>
        </div>
        )}
      </div>

      {/* Modal para crear/editar concepto */}
      {showModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.6)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: isMobile ? '20px' : '0',
          animation: 'fadeIn 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
        }}>
          <div style={{
            background: 'white',
            borderRadius: '16px',
            padding: isMobile ? '20px' : '24px',
            width: isMobile ? '100%' : '500px',
            maxWidth: '90vw',
            boxShadow: '0 25px 80px rgba(0,0,0,0.3)',
            animation: 'scaleIn 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              marginBottom: '20px'
            }}>
              <div style={{
                width: '32px',
                height: '32px',
                background: '#10b98115',
                borderRadius: '8px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <TrendingUp size={18} color="#10b981" />
              </div>
              <h3 style={{
                margin: 0,
                fontSize: isMobile ? '18px' : '20px',
                fontWeight: '700',
                color: '#1e293b'
              }}>
                {editandoConcepto ? 'Editar' : 'Nuevo'} Concepto de Entrada
              </h3>
            </div>
            
            <div style={{ display: 'grid', gap: '16px' }}>
              <div>
                <label style={{
                  display: 'block',
                  marginBottom: '8px',
                  fontSize: '14px',
                  fontWeight: '600',
                  color: '#374151'
                }}>
                  Nombre del concepto *
                </label>
                <input
                  type="text"
                  placeholder="Ej: Honorarios, Adelanto de obra, etc."
                  value={formData.nombre}
                  onChange={(e) => setFormData({...formData, nombre: e.target.value})}
                  style={{
                    width: '100%',
                    padding: '14px 16px',
                    border: '2px solid #f1f5f9',
                    borderRadius: '10px',
                    fontSize: '16px',
                    boxSizing: 'border-box',
                    outline: 'none',
                    transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                    touchAction: 'manipulation'
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = '#10b981';
                    e.target.style.boxShadow = '0 0 0 3px rgba(16, 185, 129, 0.1)';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = '#f1f5f9';
                    e.target.style.boxShadow = 'none';
                  }}
                />
              </div>

              <div style={{
                display: 'grid',
                gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
                gap: '16px'
              }}>
                <div>
                  <label style={{
                    display: 'block',
                    marginBottom: '8px',
                    fontSize: '14px',
                    fontWeight: '600',
                    color: '#374151'
                  }}>
                    Origen por defecto *
                  </label>
                  <AdminDropdown
                    value={formData.caja_origen_default}
                    onChange={(value) => setFormData({ ...formData, caja_origen_default: value })}
                    options={[
                      { value: 'cliente', label: 'Cliente (pago externo)' },
                      { value: 'empresa', label: 'Caja empresa' },
                      { value: 'proyecto', label: 'Caja proyecto' },
                      { value: 'mano_obra', label: 'Caja mano de obra' }
                    ]}
                    placeholder="Selecciona origen"
                  />
                </div>

                <div>
                  <label style={{
                    display: 'block',
                    marginBottom: '8px',
                    fontSize: '14px',
                    fontWeight: '600',
                    color: '#374151'
                  }}>
                    Destino por defecto *
                  </label>
                  <AdminDropdown
                    value={formData.caja_destino_default}
                    onChange={(value) => setFormData({ ...formData, caja_destino_default: value })}
                    options={[
                      { value: 'proyecto', label: 'Caja proyecto' },
                      { value: 'empresa', label: 'Caja empresa' },
                      { value: 'mano_obra', label: 'Caja mano de obra' }
                    ]}
                    placeholder="Selecciona destino"
                  />
                </div>
              </div>

              <div style={{
                background: '#f0f9ff',
                padding: '16px',
                borderRadius: '10px',
                border: '1px solid #0ea5e9'
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  marginBottom: '8px'
                }}>
                  <Info size={16} color="#0369a1" />
                  <span style={{
                    fontSize: '14px',
                    fontWeight: '600',
                    color: '#0369a1'
                  }}>
                    Información Útil
                  </span>
                </div>
                <p style={{
                  fontSize: '13px',
                  color: '#0c4a6e',
                  margin: 0,
                  lineHeight: '1.4'
                }}>
                  Si configuras "Honorarios" con origen "Cliente" y destino "Caja Empresa", 
                  cuando selecciones este concepto en Entradas se preseleccionarán automáticamente esas cajas.
                </p>
              </div>
            </div>
            
            <div style={{
              display: 'flex',
              gap: '12px',
              justifyContent: 'flex-end',
              marginTop: '24px'
            }}>
              <button
                onClick={() => {
                  setShowModal(false);
                  setEditandoConcepto(null);
                  setFormData({
                    nombre: '',
                    caja_destino_default: 'proyecto',
                    caja_origen_default: 'cliente'
                  });
                }}
                style={{
                  padding: '10px 20px',
                  border: '1px solid #d1d5db',
                  borderRadius: '8px',
                  background: 'white',
                  color: '#64748b',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)'
                }}
                className="button-hover"
              >
                Cancelar
              </button>
              <button
                onClick={guardarConcepto}
                disabled={guardando}
                style={{
                  padding: '10px 20px',
                  border: 'none',
                  borderRadius: '8px',
                  background: guardando ? '#94a3b8' : 'linear-gradient(135deg, #2563eb 0%, #2563eb 100%)',                  color: 'white',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: guardando ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)'
                }}
                className={!guardando ? "button-hover" : ""}
              >
                {guardando ? (
                  <>
                    <div className="loading-pulse" style={{
                      width: '16px',
                      height: '16px',
                      border: '2px solid rgba(255,255,255,0.3)',
                      borderTop: '2px solid white',
                      borderRadius: '50%',
                      animation: 'spin 1s linear infinite'
                    }}></div>
                    Guardando...
                  </>
                ) : (
                  <>
                    <Save size={16} />
                    {editandoConcepto ? 'Actualizar' : 'Guardar'}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Modal de 3 opciones */}
      {showModal3Opciones && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.6)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 3000,
          padding: '20px',
          animation: 'fadeIn 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
        }}>
          <div style={{
            background: 'white',
            borderRadius: '16px',
            padding: '24px',
            width: '100%',
            maxWidth: '500px',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
            border: '1px solid #e5e7eb'
          }}>
            <div style={{ textAlign: 'center', marginBottom: '24px' }}>
              <div style={{
                background: '#fef3c7',
                color: '#d97706',
                width: '48px',
                height: '48px',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 16px'
              }}>
                <AlertCircle size={24} />
              </div>
              <h3 style={{ margin: '0 0 8px 0', fontSize: '18px', fontWeight: '600', color: '#1f2937' }}>
                Cambio de nombre detectado
              </h3>
              <p style={{ margin: 0, color: '#6b7280', fontSize: '14px', lineHeight: '1.5' }}>
                Este concepto tiene {opcionesEdicion.impacto?.entradas?.total_entradas || 0} entrada(s) asociada(s). ¿Cómo quieres aplicar el cambio?
              </p>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginBottom: '24px' }}>
              <button
                onClick={() => ejecutarOpcionEdicion('incluir-pasados')}
                disabled={guardando}
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  border: '1px solid #3b82f6',
                  borderRadius: '8px',
                  background: '#eff6ff',
                  color: '#3b82f6',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: guardando ? 'not-allowed' : 'pointer',
                  transition: 'all 0.2s',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px'
                }}
                onMouseEnter={(e) => !guardando && (e.target.style.background = '#dbeafe')}
                onMouseLeave={(e) => !guardando && (e.target.style.background = '#eff6ff')}
              >
                <CheckCircle2 size={16} />
                Incluir Pasados
              </button>
              
              <button
                onClick={() => ejecutarOpcionEdicion('solo-futuros')}
                disabled={guardando}
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  border: '1px solid #f59e0b',
                  borderRadius: '8px',
                  background: '#fffbeb',
                  color: '#f59e0b',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: guardando ? 'not-allowed' : 'pointer',
                  transition: 'all 0.2s',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px'
                }}
                onMouseEnter={(e) => !guardando && (e.target.style.background = '#fef3c7')}
                onMouseLeave={(e) => !guardando && (e.target.style.background = '#fffbeb')}
              >
                <AlertCircle size={16} />
                Solo Futuros
              </button>
              
              <button
                onClick={() => ejecutarOpcionEdicion('crear-nuevo')}
                disabled={guardando}
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  border: '1px solid #10b981',
                  borderRadius: '8px',
                  background: '#ecfdf5',
                  color: '#10b981',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: guardando ? 'not-allowed' : 'pointer',
                  transition: 'all 0.2s',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px'
                }}
                onMouseEnter={(e) => !guardando && (e.target.style.background = '#d1fae5')}
                onMouseLeave={(e) => !guardando && (e.target.style.background = '#ecfdf5')}
              >
                <Plus size={16} />
                Crear Nuevo
              </button>
            </div>

            <div style={{ display: 'flex', justifyContent: 'center' }}>
              <button
                onClick={() => setShowModal3Opciones(false)}
                disabled={guardando}
                style={{
                  padding: '10px 20px',
                  border: '1px solid #d1d5db',
                  borderRadius: '8px',
                  background: 'white',
                  color: '#64748b',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: guardando ? 'not-allowed' : 'pointer',
                  transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)'
                }}
              >
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function ConceptosSalidaTab({ constructora, showCustomAlert }) {
  const [conceptos, setConceptos] = useState([]);
  const [todosLosConceptos, setTodosLosConceptos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showModal3Opciones, setShowModal3Opciones] = useState(false);
  const [filtroActivos, setFiltroActivos] = useState(true);
  const [editandoConcepto, setEditandoConcepto] = useState(null);
  const [opcionesEdicion, setOpcionesEdicion] = useState(null);
  const [formData, setFormData] = useState({
    nombre: '',
    caja_origen_default: 'empresa'
  });
  const [guardando, setGuardando] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  // Prevenir scroll del fondo cuando hay modales abiertos
  useEffect(() => {
    if (showModal || showModal3Opciones) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [showModal, showModal3Opciones]);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const cargarTodosLosConceptos = async () => {
    try {
      console.log('Cargando todos los conceptos de salida para constructora:', constructora.id);
      
      const { data: todosConceptos, error: errorTodas } = await supabase
        .from('conceptos_salida')
        .select('*')
        .eq('constructora_id', constructora.id);

      if (errorTodas) {
        console.error('Error cargando todos los conceptos de salida:', errorTodas);
      } else {
        console.log('Todos los conceptos de salida cargados:', todosConceptos?.length || 0);
        console.log('Conceptos activos:', todosConceptos?.filter(c => c.deleted_at === null).length || 0);
        console.log('Conceptos eliminados:', todosConceptos?.filter(c => c.deleted_at !== null).length || 0);
        setTodosLosConceptos(todosConceptos);
      }
    } catch (err) {
      console.error('Error:', err);
    }
  };

  const cargarConceptos = async () => {
    try {
      setLoading(true);
      console.log('Cargando conceptos de salida, filtroActivos:', filtroActivos);
      
      // Cargar conceptos filtrados (activos o eliminados)
      let query = supabase
        .from('conceptos_salida')
        .select('*')
        .eq('constructora_id', constructora.id);

      if (filtroActivos) {
        // Mostrar solo conceptos activos (deleted_at es null)
        query = query.is('deleted_at', null);
      } else {
        // Mostrar solo conceptos eliminados (deleted_at no es null)
        query = query.not('deleted_at', 'is', null);
      }

      const { data: conceptosFiltrados, error: errorFiltrados } = await query.order('nombre', { ascending: true });

      if (errorFiltrados) {
        console.error('Error cargando conceptos de salida filtrados:', errorFiltrados);
      } else {
        console.log('Conceptos de salida cargados:', conceptosFiltrados?.length || 0);
        console.log('Conceptos:', conceptosFiltrados);
        setConceptos(conceptosFiltrados || []);
      }
    } catch (err) {
      console.error('Error:', err);
    }
    setLoading(false);
  };

  // Cargar todos los conceptos solo cuando cambia la constructora
  useEffect(() => {
    if (constructora?.id) {
      cargarTodosLosConceptos();
    }
  }, [constructora]);

  // Cargar conceptos filtrados cuando cambia el filtro
  useEffect(() => {
    if (constructora?.id) {
      cargarConceptos();
    }
  }, [constructora, filtroActivos]);

  // Recargar todos los conceptos cuando cambia el filtro para actualizar contadores
  useEffect(() => {
    if (constructora?.id) {
      cargarTodosLosConceptos();
    }
  }, [constructora, filtroActivos]);

  const abrirModal = (concepto = null) => {
    if (concepto) {
      setEditandoConcepto(concepto);
      setFormData({
        nombre: concepto.nombre,
        caja_origen_default: concepto.caja_origen_default || 'empresa'
      });
    } else {
      setEditandoConcepto(null);
      setFormData({
        nombre: '',
        caja_origen_default: 'empresa'
      });
    }
    setShowModal(true);
  };

  const guardarConcepto = async () => {
    if (!formData.nombre.trim()) {
      showCustomAlert('error', 'Error', 'Por favor ingresa un nombre para el concepto');
      return;
    }

    try {
      setGuardando(true);

      if (editandoConcepto) {
        // Verificar si hay cambio de nombre
        if (formData.nombre.trim() !== editandoConcepto.nombre.trim()) {
          // Analizar impacto antes de editar
          const impactoResponse = await fetch('http://localhost:4001/api/conceptos-salida/analizar-impacto', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              concepto_id: editandoConcepto.id,
              accion: 'editar',
              cambios: { nombre: formData.nombre.trim() }
            })
          });

          if (!impactoResponse.ok) {
            throw new Error('Error analizando impacto');
          }

          const impactoData = await impactoResponse.json();
          console.log('🔍 Analisis concepto salida completo:', impactoData);
          console.log('🔍 Total salidas:', impactoData.impacto?.salidas?.total_salidas);
          console.log('🔍 Total presupuestos:', impactoData.impacto?.presupuestos?.total_presupuestos);
          
          // Si hay impacto, mostrar opciones
          if (impactoData.impacto && (
            impactoData.impacto.salidas?.total_salidas > 0 ||
            (impactoData.impacto.presupuestos?.total_presupuestos || 0) > 0 ||
            (impactoData.impacto.presupuestos_iniciales?.total_presupuestos || 0) > 0
          )) {
            console.log('🔍 Mostrando modal de opciones para concepto salida');
            setOpcionesEdicion({
              concepto_id: editandoConcepto.id,
              cambios: formData,
              impacto: impactoData.impacto
            });
            setShowModal3Opciones(true);
            setShowModal(false);
            return;
          }
        }

        // Si no hay cambio de nombre o no hay impacto, editar directamente
        const response = await fetch('http://localhost:4001/api/conceptos-salida/editar', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            concepto_id: editandoConcepto.id,
            cambios: formData,
            opciones: {
              usuario_id: null
            }
          })
        });

        if (!response.ok) {
          throw new Error('Error editando concepto');
        }

        const result = await response.json();
        console.log('Respuesta del servidor:', result);
      } else {
        // Crear nuevo concepto
        const { data: nuevoConcepto, error: errorCrear } = await supabase
          .from('conceptos_salida')
          .insert({
            nombre: formData.nombre.trim(),
            caja_origen_default: formData.caja_origen_default,
            constructora_id: constructora.id,
            created_by: null
          })
          .select()
          .single();

        if (errorCrear) {
          throw new Error('Error creando concepto: ' + errorCrear.message);
        }

        console.log('Concepto creado:', nuevoConcepto);
      }

      // Actualizar datos
      console.log('Actualizando datos...');
      await Promise.all([
        cargarConceptos(),
        cargarTodosLosConceptos()
      ]);
      console.log('Datos actualizados');

      setShowModal(false);
      showCustomAlert('success', 'Éxito', editandoConcepto ? 'Concepto actualizado correctamente' : 'Concepto creado correctamente');
    } catch (error) {
      console.error('Error:', error);
      showCustomAlert('error', 'Error', error.message);
    } finally {
      setGuardando(false);
    }
  };

  const ejecutarOpcionEdicion = async (opcion) => {
    try {
      const { concepto_id, cambios, impacto } = opcionesEdicion;
      console.log('Ejecutando opción de edición:', opcion);

      if (opcion === 'incluir-pasados') {
        // Editar directamente afectando registros pasados
        const editarResponse = await fetch('http://localhost:4001/api/conceptos-salida/editar', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            concepto_id: concepto_id,
            cambios: cambios,
            opciones: {
              afectarRegistrosPasados: true,
              usuario_id: null
            }
          })
        });

        if (!editarResponse.ok) {
          throw new Error('Error editando concepto');
        }

        const result = await editarResponse.json();
        console.log('Respuesta del servidor (editar):', result);
      } else {
        // Para "Solo Futuros" y "Crear Nuevo" - crear nuevo concepto y desactivar el viejo
        const crearNuevoResponse = await fetch('http://localhost:4001/api/conceptos-salida/crear-nuevo', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            concepto_id_viejo: concepto_id,
            nuevo_concepto: cambios,
            usuario_id: null,
            opcion: opcion
          })
        });

        if (!crearNuevoResponse.ok) {
          throw new Error('Error creando nuevo concepto');
        }

        const result = await crearNuevoResponse.json();
        console.log('Respuesta del servidor (crear nuevo):', result);
      }

      // Actualizar datos
      console.log('Actualizando datos...');
      await Promise.all([
        cargarConceptos(),
        cargarTodosLosConceptos()
      ]);
      console.log('Datos actualizados');

      setShowModal3Opciones(false);
      showCustomAlert('success', 'Éxito', 'Operación completada correctamente');
    } catch (error) {
      console.error('Error en ejecutarOpcionEdicion:', error);
      showCustomAlert('error', 'Error', error.message);
    }
  };

  const eliminarConcepto = async (id, nombre) => {
    try {
      console.log('Analizando impacto antes de eliminar:', nombre, 'ID:', id);
      
      // Analizar impacto antes de eliminar
      const impactoResponse = await fetch('http://localhost:4001/api/conceptos-salida/analizar-impacto', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          concepto_id: id,
          accion: 'eliminar'
        })
      });

      if (!impactoResponse.ok) {
        throw new Error('Error analizando impacto');
      }

      const impactoData = await impactoResponse.json();
      console.log('Análisis de impacto:', impactoData);
      
      // Construir mensaje de confirmación con HTML
      let mensaje = `¿Estás seguro de que quieres eliminar el concepto <strong>"${nombre}"</strong>?`;
      
      if (impactoData.impacto && impactoData.impacto.salidas && impactoData.impacto.salidas.total_salidas > 0) {
        mensaje += `<br><br><strong>ADVERTENCIA:</strong> Este concepto tiene <strong>${impactoData.impacto.salidas.total_salidas} salida(s)</strong> asociada(s).`;
        mensaje += `<br><br><strong>Comportamiento:</strong>`;
        mensaje += `<br>• El concepto se marcará como eliminado`;
        mensaje += `<br>• Las salidas existentes mantendrán su referencia al concepto`;
        mensaje += `<br>• Las salidas seguirán apareciendo en reportes históricos`;
        mensaje += `<br>• Para nuevas salidas, deberás usar otro concepto`;
      } else {
        mensaje += `<br><br>Este concepto no tiene salidas asociadas.`;
      }
      
      mensaje += `<br><br>¿Deseas continuar con la eliminación?`;
      
      console.log('Mostrando confirmación de eliminación con mensaje:', mensaje);
      
      showCustomAlert('confirm', 'Confirmar eliminación', mensaje, async () => {
        console.log('Usuario confirmó eliminación, ejecutando...');
        await ejecutarEliminacion(id, nombre);
      });
    } catch (error) {
      console.error('Error en eliminarConcepto:', error);
      showCustomAlert('error', 'Error', error.message);
    }
  };

  const ejecutarEliminacion = async (id, nombre) => {
    try {
      console.log('Ejecutando eliminación del concepto:', id);
      
      const response = await fetch('http://localhost:4001/api/conceptos-salida/eliminar', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          concepto_id: id,
          confirmar: true,
          opciones: {
            usuario_id: null
          }
        })
      });

      if (!response.ok) {
        throw new Error('Error eliminando concepto');
      }

      const result = await response.json();
      console.log('Respuesta del servidor:', result);

      // Actualizar datos inmediatamente
      console.log('Actualizando datos...');
      await Promise.all([
        cargarConceptos(),
        cargarTodosLosConceptos()
      ]);
      console.log('Datos actualizados');

      showCustomAlert('success', 'Éxito', `Concepto "${nombre}" eliminado correctamente.`);
    } catch (error) {
      console.error('Error en ejecutarEliminacion:', error);
      showCustomAlert('error', 'Error', error.message);
    }
  };

  const restaurarConcepto = async (id, nombre) => {
    try {
      console.log('Restaurando concepto:', nombre, 'ID:', id);
      
      const response = await fetch('http://localhost:4001/api/conceptos-salida/restaurar', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          concepto_id: id,
          opciones: {
            usuario_id: null
          }
        })
      });

      if (!response.ok) {
        throw new Error('Error restaurando concepto');
      }

      const result = await response.json();
      console.log('Respuesta del servidor (restaurar):', result);

      // Actualizar datos inmediatamente
      console.log('Actualizando datos después de restaurar...');
      await Promise.all([
        cargarConceptos(),
        cargarTodosLosConceptos()
      ]);
      console.log('Datos actualizados después de restaurar');

      showCustomAlert('success', 'Éxito', 'Concepto restaurado correctamente. Ahora aparece en la lista de activos.');
    } catch (error) {
      console.error('Error en restaurarConcepto:', error);
      showCustomAlert('error', 'Error', error.message);
    }
  };

  const getOrigenTexto = (origen) => {
    switch (origen) {
      case 'empresa': return 'Empresa';
      case 'proyecto': return 'Proyecto';
      case 'mano_obra': return 'Mano de Obra';
      default: return origen;
    }
  };

  const getOrigenColor = (origen) => {
    switch (origen) {
      case 'empresa': return '#3b82f6';
      case 'proyecto': return '#10b981';
      case 'mano_obra': return '#f59e0b';
      default: return '#64748b';
    }
  };
  // Calcular contadores
  const conceptosActivos = todosLosConceptos.filter(c => c.deleted_at === null).length;
  const conceptosEliminados = todosLosConceptos.filter(c => c.deleted_at !== null).length;
  return (
    <div style={{padding: isMobile ? '20px 16px' : '24px 32px'}}>
      {/* Header mejorado */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: '24px',
        flexWrap: 'wrap',
        gap: '16px'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1 }}>
          <div style={{
            width: '32px',
            height: '32px',
            background: '#10b98115',
            borderRadius: '8px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <Package size={18} color="#10b981" />
          </div>
          <div>
            <h2 style={{
              fontSize: isMobile ? '18px' : '20px',
              fontWeight: '600',
              color: '#1e293b',
              margin: '0 0 4px 0'
            }}>
              Conceptos de Salida
            </h2>
            <p style={{
              fontSize: '13px',
              color: '#64748b',
              margin: 0
            }}>
              Configura los conceptos y sus cajas por defecto
            </p>
          </div>
        </div>
        
        <button 
          onClick={() => abrirModal()}
          style={{
            background: 'linear-gradient(135deg, #2563eb 0%, #2563eb 100%)',
            color: 'white',
            border: 'none',
            padding: isMobile ? '10px 16px' : '12px 20px',
            borderRadius: '10px',
            fontSize: isMobile ? '13px' : '14px',
            fontWeight: '600',
            cursor: 'pointer',
            boxShadow: '0 4px 16px rgba(37, 99, 235, 0.25)',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)'
          }}
          className="button-hover"
        >
          <Plus size={16} />
          Añadir concepto
        </button>
      </div>

      {/* Tabs */}
      <div style={{
        display: 'flex',
        borderBottom: '2px solid #e5e7eb',
        marginBottom: '24px',
        gap: '0',
        position: 'relative'
      }}>
        <button
          onClick={() => setFiltroActivos(true)}
          style={{
            background: 'transparent',
            border: 'none',
            padding: '12px 24px',
            fontSize: '14px',
            fontWeight: '600',
            cursor: 'pointer',
            color: filtroActivos ? '#10b981' : '#6b7280',
            borderBottom: filtroActivos ? '2px solid #10b981' : '2px solid transparent',
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            flex: 1,
            textAlign: 'center',
            position: 'relative',
            zIndex: 2
          }}
          onMouseEnter={(e) => {
            if (!filtroActivos) {
              e.target.style.color = '#10b981';
            }
          }}
          onMouseLeave={(e) => {
            if (!filtroActivos) {
              e.target.style.color = '#6b7280';
            }
          }}
        >
          Activos ({conceptosActivos})
        </button>
        <button
          onClick={() => setFiltroActivos(false)}
          style={{
            background: 'transparent',
            border: 'none',
            padding: '12px 24px',
            fontSize: '14px',
            fontWeight: '600',
            cursor: 'pointer',
            color: !filtroActivos ? '#ef4444' : '#6b7280',
            borderBottom: !filtroActivos ? '2px solid #ef4444' : '2px solid transparent',
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            flex: 1,
            textAlign: 'center',
            position: 'relative',
            zIndex: 2
          }}
          onMouseEnter={(e) => {
            if (filtroActivos) {
              e.target.style.color = '#ef4444';
            }
          }}
          onMouseLeave={(e) => {
            if (filtroActivos) {
              e.target.style.color = '#6b7280';
            }
          }}
        >
          Eliminados ({conceptosEliminados})
        </button>
      </div>

      {/* Tabla mejorada */}
      <div style={{
        background: 'white',
        borderRadius: '16px',
        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
        overflow: 'hidden',
        border: '1px solid #f1f5f9',
        opacity: loading ? 0.6 : 1,
        transition: 'opacity 0.3s ease'
      }}>
        {loading ? (
          <div style={{
            padding: '60px 20px',
            textAlign: 'center',
            color: '#64748b'
          }}>
            <div style={{
              width: '40px',
              height: '40px',
              border: '3px solid #f1f5f9',
              borderTop: '3px solid #10b981',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite',
              margin: '0 auto 16px auto'
            }} />
            <p style={{ margin: 0, fontSize: '14px' }}>Cargando conceptos...</p>
          </div>
        ) : conceptos.length === 0 ? (
          <div style={{
            padding: '60px 20px',
            textAlign: 'center',
            color: '#64748b'
          }}>
            <Package size={48} color="#cbd5e1" style={{ marginBottom: '16px' }} />
            <p style={{ margin: 0, fontSize: '16px', fontWeight: '500', marginBottom: '8px' }}>
              {filtroActivos ? 'No hay conceptos activos' : 'No hay conceptos eliminados'}
            </p>
            <p style={{ margin: 0, fontSize: '14px', color: '#94a3b8' }}>
              {filtroActivos ? 'Crea tu primer concepto de salida' : 'Los conceptos eliminados aparecerán aquí'}
            </p>
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{
              width: '100%',
              borderCollapse: 'collapse'
            }}>
              <thead>
                <tr style={{
                  background: '#f8fafc',
                  borderBottom: '1px solid #e2e8f0'
                }}>
                  <th style={{
                    padding: isMobile ? '12px 16px' : '16px 20px',
                    textAlign: 'left',
                    fontWeight: '600',
                    color: '#374151',
                    fontSize: isMobile ? '12px' : '14px'
                  }}>
                    Nombre
                  </th>
                  <th style={{
                    padding: isMobile ? '12px 16px' : '16px 20px',
                    textAlign: 'left',
                    fontWeight: '600',
                    color: '#374151',
                    fontSize: isMobile ? '12px' : '14px'
                  }}>
                    Origen Default
                  </th>
                  <th style={{
                    padding: isMobile ? '12px 16px' : '16px 20px',
                    textAlign: 'center',
                    fontWeight: '600',
                    color: '#374151',
                    fontSize: isMobile ? '12px' : '14px',
                    width: isMobile ? '120px' : '160px'
                  }}>
                    Acciones
                  </th>
                </tr>
              </thead>
              <tbody>
                {conceptos.map((concepto, index) => (
                  <tr key={concepto.id} style={{
                    borderBottom: '1px solid #f1f5f9',
                    transition: 'all 0.2s ease',
                    background: index % 2 === 0 ? 'white' : '#fafafa'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = '#f0fdf4';
                    e.currentTarget.style.transform = 'translateY(-1px)';
                    e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.1)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = index % 2 === 0 ? 'white' : '#fafafa';
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                  >
                    <td style={{
                      padding: isMobile ? '12px 16px' : '16px 20px',
                      color: '#1e293b',
                      fontSize: isMobile ? '13px' : '14px',
                      fontWeight: '600'
                    }}>
                      {concepto.nombre}
                    </td>
                    <td style={{
                      padding: isMobile ? '12px 16px' : '16px 20px',
                      fontSize: isMobile ? '12px' : '13px'
                    }}>
                      <span style={{
                        background: getOrigenColor(concepto.caja_origen_default) + '15',
                        color: getOrigenColor(concepto.caja_origen_default),
                        padding: '4px 8px',
                        borderRadius: '6px',
                        fontSize: isMobile ? '11px' : '12px',
                        fontWeight: '500'
                      }}>
                        {getOrigenTexto(concepto.caja_origen_default)}
                      </span>
                    </td>
                    <td style={{
                      padding: isMobile ? '12px 16px' : '16px 20px',
                      textAlign: 'center'
                    }}>
                      {filtroActivos ? (
                        <div style={{
                          display: 'flex',
                          gap: '8px',
                          justifyContent: 'center'
                        }}>
                          <button 
                            onClick={() => abrirModal(concepto)}
                            style={{
                              background: '#eff6ff',
                              border: '1px solid #3b82f6',
                              borderRadius: '6px',
                              padding: isMobile ? '6px 8px' : '8px 12px',
                              color: '#3b82f6',
                              fontSize: isMobile ? '11px' : '12px',
                              fontWeight: '500',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '4px',
                              transition: 'all 0.2s ease'
                            }}
                            onMouseEnter={(e) => {
                              e.target.style.background = '#dbeafe';
                            }}
                            onMouseLeave={(e) => {
                              e.target.style.background = '#eff6ff';
                            }}
                          >
                            <Edit size={12} />
                            Editar
                          </button>
                          <button 
                            onClick={() => eliminarConcepto(concepto.id, concepto.nombre)}
                            style={{
                              background: '#fef2f2',
                              border: '1px solid #ef4444',
                              borderRadius: '6px',
                              padding: isMobile ? '6px 8px' : '8px 12px',
                              color: '#ef4444',
                              fontSize: isMobile ? '11px' : '12px',
                              fontWeight: '500',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '4px',
                              transition: 'all 0.2s ease'
                            }}
                            onMouseEnter={(e) => {
                              e.target.style.background = '#fee2e2';
                            }}
                            onMouseLeave={(e) => {
                              e.target.style.background = '#fef2f2';
                            }}
                          >
                            <Trash2 size={12} />
                            Eliminar
                          </button>
                        </div>
                      ) : (
                        <button 
                          onClick={() => restaurarConcepto(concepto.id, concepto.nombre)}
                          style={{
                            background: '#f0fdf4',
                            border: '1px solid #22c55e',
                            borderRadius: '6px',
                            padding: isMobile ? '6px 8px' : '8px 12px',
                            color: '#22c55e',
                            fontSize: isMobile ? '11px' : '12px',
                            fontWeight: '500',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px',
                            transition: 'all 0.2s ease'
                          }}
                          onMouseEnter={(e) => {
                            e.target.style.background = '#dcfce7';
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.background = '#f0fdf4';
                          }}
                        >
                          <Package size={12} />
                          Restaurar
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modal de edición/creación */}
      {showModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.6)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 2000,
          padding: '20px'
        }}>
          <div style={{
            background: 'white',
            borderRadius: '16px',
            padding: '24px',
            width: '100%',
            maxWidth: isMobile ? '90%' : '400px',
            boxShadow: '0 25px 80px rgba(0,0,0,0.3)'
          }}>
            <h3 style={{
              margin: '0 0 20px 0',
              fontSize: '18px',
              fontWeight: '600',
              color: '#1f2937'
            }}>
              {editandoConcepto ? 'Editar Concepto' : 'Nuevo Concepto'}
            </h3>
            
            <div style={{ marginBottom: '16px' }}>
              <label style={{
                display: 'block',
                marginBottom: '8px',
                fontSize: '14px',
                fontWeight: '600',
                color: '#374151'
              }}>
                Nombre del concepto
              </label>
              <input
                type="text"
                value={formData.nombre}
                onChange={(e) => setFormData({...formData, nombre: e.target.value})}
                placeholder="Ej: Materiales, Mano de obra, etc."
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '8px',
                  fontSize: '14px',
                  boxSizing: 'border-box'
                }}
              />
            </div>

            <div style={{ marginBottom: '24px' }}>
              <label style={{
                display: 'block',
                marginBottom: '8px',
                fontSize: '14px',
                fontWeight: '600',
                color: '#374151'
              }}>
                Origen por defecto
              </label>
              <select
                value={formData.caja_origen_default}
                onChange={(e) => setFormData({...formData, caja_origen_default: e.target.value})}
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '8px',
                  fontSize: '14px',
                  boxSizing: 'border-box',
                  background: 'white',
                  WebkitAppearance: 'none',
                  appearance: 'none',
                  outline: 'none'
                }}
              >
                <option value="cliente">Cliente (pago externo)</option>
                <option value="empresa">Caja empresa</option>
                <option value="proyecto">Caja proyecto</option>
                <option value="mano_obra">Caja mano de obra</option>
              </select>
            </div>

            <div style={{
              display: 'flex',
              gap: '12px',
              justifyContent: 'flex-end'
            }}>
              <button
                onClick={() => setShowModal(false)}
                style={{
                  padding: '12px 20px',
                  border: '1px solid #d1d5db',
                  borderRadius: '8px',
                  background: 'white',
                  color: '#64748b',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer'
                }}
              >
                Cancelar
              </button>
              <button
                onClick={guardarConcepto}
                disabled={guardando}
                style={{
                  padding: '12px 20px',
                  border: 'none',
                  borderRadius: '8px',
                  background: guardando ? '#9ca3af' : 'linear-gradient(135deg, #2563eb 0%, #2563eb 100%)',
                  color: 'white',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: guardando ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}
              >
                {guardando ? (
                  <>
                    <div style={{
                      width: '16px',
                      height: '16px',
                      border: '2px solid transparent',
                      borderTop: '2px solid white',
                      borderRadius: '50%',
                      animation: 'spin 1s linear infinite'
                    }} />
                    Guardando...
                  </>
                ) : (
                  <>
                    <Save size={16} />
                    {editandoConcepto ? 'Actualizar' : 'Crear'}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de 3 opciones */}
      {showModal3Opciones && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.6)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 3000,
          padding: '20px',
          animation: 'fadeIn 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
        }}>
          <div style={{
            background: 'white',
            borderRadius: '16px',
            padding: '24px',
            width: '100%',
            maxWidth: '500px',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
            border: '1px solid #e5e7eb'
          }}>
            <div style={{ textAlign: 'center', marginBottom: '24px' }}>
              <div style={{
                background: '#fef3c7',
                color: '#d97706',
                width: '48px',
                height: '48px',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 16px'
              }}>
                <AlertCircle size={24} />
              </div>
              <h3 style={{ margin: '0 0 8px 0', fontSize: '18px', fontWeight: '600', color: '#1f2937' }}>
                Cambio de nombre detectado
              </h3>
              <p style={{ margin: 0, color: '#6b7280', fontSize: '14px', lineHeight: '1.5' }}>
                Este concepto tiene {opcionesEdicion.impacto?.salidas?.total_salidas || 0} salida(s) asociada(s)
                {(opcionesEdicion?.impacto?.presupuestos?.total_presupuestos || 0) > 0 && (
                  <>
                    . Además, está asociado a {opcionesEdicion?.impacto?.presupuestos?.total_presupuestos || 0} presupuesto(s)
                    {(() => {
                      const iniciales = opcionesEdicion?.impacto?.presupuestos?.total_presupuestos_iniciales || 0;
                      const directos = opcionesEdicion?.impacto?.presupuestos?.total_presupuestos_directos || 0;
                      if (iniciales > 0 && directos > 0) {
                        return ` (${iniciales} presupuestos iniciales y ${directos} presupuestos directos)`;
                      } else if (iniciales > 0) {
                        return ` (${iniciales} presupuestos iniciales)`;
                      } else if (directos > 0) {
                        return ` (${directos} presupuestos directos)`;
                      }
                      return '';
                    })()}
                  </>
                )
                }. ¿Cómo quieres aplicar el cambio?
              </p>
              {(opcionesEdicion?.impacto?.presupuestos?.total_presupuestos || 0) > 0 && (
                <div style={{
                  background: '#fef3c7',
                  border: '1px solid #f59e0b',
                  borderRadius: '8px',
                  padding: '12px',
                  marginTop: '16px',
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '8px'
                }}>
                  <AlertTriangle size={16} color="#d97706" style={{ flexShrink: 0, marginTop: '2px' }} />
                  <div style={{ fontSize: '13px', color: '#92400e', lineHeight: '1.4' }}>
                    <strong>Advertencia:</strong> Si cambias el nombre del concepto, los presupuestos ya no podrán identificar correctamente los gastos asociados a este concepto. Esto puede afectar el seguimiento de gastos en los presupuestos.
                  </div>
                </div>
              )}
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginBottom: '24px' }}>
              <button
                onClick={() => ejecutarOpcionEdicion('incluir-pasados')}
                disabled={guardando}
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  border: '1px solid #3b82f6',
                  borderRadius: '8px',
                  background: '#eff6ff',
                  color: '#3b82f6',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: guardando ? 'not-allowed' : 'pointer',
                  transition: 'all 0.2s',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px'
                }}
                onMouseEnter={(e) => !guardando && (e.target.style.background = '#dbeafe')}
                onMouseLeave={(e) => !guardando && (e.target.style.background = '#eff6ff')}
              >
                <CheckCircle2 size={16} />
                Incluir Pasados
              </button>
              
              <button
                onClick={() => ejecutarOpcionEdicion('solo-futuros')}
                disabled={guardando}
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  border: '1px solid #f59e0b',
                  borderRadius: '8px',
                  background: '#fffbeb',
                  color: '#f59e0b',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: guardando ? 'not-allowed' : 'pointer',
                  transition: 'all 0.2s',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px'
                }}
                onMouseEnter={(e) => !guardando && (e.target.style.background = '#fef3c7')}
                onMouseLeave={(e) => !guardando && (e.target.style.background = '#fffbeb')}
              >
                <AlertCircle size={16} />
                Solo Futuros
              </button>
              
              <button
                onClick={() => ejecutarOpcionEdicion('crear-nuevo')}
                disabled={guardando}
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  border: '1px solid #10b981',
                  borderRadius: '8px',
                  background: '#ecfdf5',
                  color: '#10b981',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: guardando ? 'not-allowed' : 'pointer',
                  transition: 'all 0.2s',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px'
                }}
                onMouseEnter={(e) => !guardando && (e.target.style.background = '#d1fae5')}
                onMouseLeave={(e) => !guardando && (e.target.style.background = '#ecfdf5')}
              >
                <Plus size={16} />
                Crear Nuevo
              </button>
            </div>

            <div style={{ display: 'flex', justifyContent: 'center' }}>
              <button
                onClick={() => setShowModal3Opciones(false)}
                disabled={guardando}
                style={{
                  padding: '10px 20px',
                  border: '1px solid #d1d5db',
                  borderRadius: '8px',
                  background: 'white',
                  color: '#64748b',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: guardando ? 'not-allowed' : 'pointer',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => !guardando && (e.target.style.background = '#f9fafb')}
                onMouseLeave={(e) => !guardando && (e.target.style.background = 'white')}
              >
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
function EtapasTab({ constructora, showCustomAlert }) {
  const [etapas, setEtapas] = useState([]);
  const [todosLosEtapas, setTodosLosEtapas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showModal3Opciones, setShowModal3Opciones] = useState(false);
  const [filtroActivos, setFiltroActivos] = useState(true);
  const [opcionesEdicion, setOpcionesEdicion] = useState(null);
  const [guardando, setGuardando] = useState(false);
  const [editandoEtapa, setEditandoEtapa] = useState(null);
  const [formData, setFormData] = useState({
    nombre: ''
  });
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Prevenir scroll del fondo cuando hay modales abiertos
  useEffect(() => {
    if (showModal || showModal3Opciones) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [showModal, showModal3Opciones]);

  const cargarTodosLosEtapas = async () => {
    try {
      const { data, error } = await supabase
        .from('etapas_obra')
        .select('*')
        .eq('constructora_id', constructora.id)
        .order('nombre');

      if (error) {
        console.error('Error cargando todas las etapas:', error);
      } else {
        setTodosLosEtapas(data || []);
      }
    } catch (err) {
      console.error('Error:', err);
    }
  };

  const cargarEtapas = async () => {
    try {
      const { data, error } = await supabase
        .from('etapas_obra')
        .select('*')
        .eq('constructora_id', constructora.id)
        .order('nombre');

      if (error) {
        console.error('Error cargando etapas:', error);
      } else {
        // Filtrar en el frontend para mayor control
        const etapasFiltradas = filtroActivos 
          ? (data || []).filter(etapa => etapa.deleted_at === null)
          : (data || []).filter(etapa => etapa.deleted_at !== null);
        setEtapas(etapasFiltradas);
      }
    } catch (err) {
      console.error('Error:', err);
    }
    setLoading(false);
  };
  useEffect(() => {
    if (constructora?.id) {
      Promise.all([cargarTodosLosEtapas(), cargarEtapas()]);
    }
  }, [constructora?.id]);

  useEffect(() => {
    if (constructora?.id) {
      cargarEtapas();
    }
  }, [filtroActivos, constructora?.id]);

  useEffect(() => {
    if (constructora?.id) {
      cargarTodosLosEtapas();
    }
  }, [filtroActivos, constructora?.id]);

  const abrirModal = (etapa = null) => {
    if (etapa) {
      setEditandoEtapa(etapa);
      setFormData({
        nombre: etapa.nombre
      });
    } else {
      setEditandoEtapa(null);
      setFormData({
        nombre: ''
      });
    }
    setShowModal(true);
  };

  const guardarEtapa = async () => {
    if (!formData.nombre.trim()) {
      showCustomAlert('error', 'Error', 'El nombre es requerido');
      return;
    }

    setGuardando(true);

    try {
      if (editandoEtapa) {
        // Verificar si el nombre cambió
        if (formData.nombre !== editandoEtapa.nombre) {
          // Analizar impacto antes de editar
          const impactoResponse = await fetch('http://localhost:4001/api/etapas/analizar-impacto', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              etapa_id: editandoEtapa.id,
              accion: 'editar',
              cambios: { nombre: formData.nombre }
            })
          });

          const impactoData = await impactoResponse.json();
          console.log('🔍 Impacto data:', impactoData);

          if (impactoData.success && (
            impactoData.impacto.subetapas.total_subetapas > 0 ||
            impactoData.impacto.salidas.total_salidas > 0 ||
            impactoData.impacto.proyectos.total_proyectos > 0 ||
            (impactoData.impacto.presupuestos?.total_presupuestos || 0) > 0 ||
            (impactoData.impacto.presupuestos_iniciales?.total_presupuestos || 0) > 0
          )) {
            // Hay impacto, mostrar opciones
            setOpcionesEdicion({
              etapa_id: editandoEtapa.id,
              cambios: formData,
              impacto: impactoData.impacto
            });
            setShowModal3Opciones(true);
            setShowModal(false);
            setGuardando(false);
            return;
          }
        }

        // No hay impacto o no cambió el nombre, editar directamente
        const editarResponse = await fetch('http://localhost:4001/api/etapas/editar', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            etapa_id: editandoEtapa.id,
            cambios: formData,
            usuario_id: constructora.usuario_id
          })
        });

        if (!editarResponse.ok) {
          throw new Error('Error al editar etapa');
        }

        showCustomAlert('success', 'Éxito', 'Etapa actualizada correctamente');
      } else {
        // Crear nueva etapa
        const { data, error } = await supabase
          .from('etapas_obra')
          .insert({
            constructora_id: constructora.id,
            nombre: formData.nombre,
            activo: true,
            created_by: constructora.usuario_id,
            updated_by: constructora.usuario_id
          })
          .select()
          .single();

        if (error) {
          throw new Error('Error al crear etapa: ' + error.message);
        }

        showCustomAlert('success', 'Éxito', 'Etapa creada correctamente');
      }

      setShowModal(false);
      Promise.all([cargarEtapas(), cargarTodosLosEtapas()]);
    } catch (error) {
      console.error('Error:', error);
      showCustomAlert('error', 'Error', error.message);
    } finally {
      setGuardando(false);
    }
  };

  const ejecutarOpcionEdicion = async (opcion) => {
    setGuardando(true);

    try {
      if (opcion === 'incluir_pasados') {
        // Editar directamente (afecta registros pasados)
        const editarResponse = await fetch('http://localhost:4001/api/etapas/editar', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            etapa_id: opcionesEdicion.etapa_id,
            cambios: opcionesEdicion.cambios,
            opciones: { afectarRegistrosPasados: true },
            usuario_id: constructora.usuario_id
          })
        });

        if (!editarResponse.ok) {
          throw new Error('Error al editar etapa');
        }

        showCustomAlert('success', 'Éxito', 'Etapa actualizada (incluyendo registros pasados)');
      } else {
        // Solo futuros o crear nuevo - crear nueva etapa y desactivar la vieja
        const crearNuevoResponse = await fetch('http://localhost:4001/api/etapas/crear-nuevo', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            etapa_id_viejo: opcionesEdicion.etapa_id,
            nueva_etapa: {
              constructora_id: constructora.id,
              nombre: opcionesEdicion.cambios.nombre
            },
            usuario_id: constructora.usuario_id,
            mantener_vieja_activa: false
          })
        });

        if (!crearNuevoResponse.ok) {
          throw new Error('Error creando nueva etapa');
        }

        showCustomAlert('success', 'Éxito', 'Nueva etapa creada (solo para registros futuros)');
      }

      setShowModal3Opciones(false);
      Promise.all([cargarEtapas(), cargarTodosLosEtapas()]);
    } catch (error) {
      console.error('Error:', error);
      showCustomAlert('error', 'Error', error.message);
    } finally {
      setGuardando(false);
    }
  };

  const eliminarEtapa = async (id, nombre) => {
    try {
      // Analizar impacto antes de eliminar
      const impactoResponse = await fetch('http://localhost:4001/api/etapas/analizar-impacto', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          etapa_id: id,
          accion: 'eliminar'
        })
      });

      const impactoData = await impactoResponse.json();

      if (impactoData.success) {
        const { subetapas, salidas, proyectos, presupuestos_iniciales } = impactoData.impacto;
        let mensaje = `<strong>¿Estás seguro de eliminar la etapa "${nombre}"?</strong><br><br>`;
        
        if (subetapas.total_subetapas > 0) {
          mensaje += `<br>• Esta etapa tiene ${subetapas.total_subetapas} subetapa(s) asociada(s)`;
        }
        
        if (salidas.total_salidas > 0) {
          mensaje += `<br>• Esta etapa tiene ${salidas.total_salidas} salida(s) asociada(s)`;
        }
        
        if (proyectos.total_proyectos > 0) {
          mensaje += `<br>• Esta etapa tiene ${proyectos.total_proyectos} proyecto(s) asociado(s)`;
        }
        
        if (presupuestos_iniciales?.total_presupuestos > 0) {
          mensaje += `<br>• Hay ${presupuestos_iniciales.total_presupuestos} presupuesto(s) inicial(es) vinculados a través de salidas o directamente`;
        }
        
        mensaje += `<br>• La etapa se marcará como eliminada`;
        mensaje += `<br>• Las subetapas asociadas también se eliminarán`;
        mensaje += `<br>• Las salidas mantendrán su referencia pero perderán el nombre de la etapa`;
        mensaje += `<br>• Los proyectos mantendrán su referencia pero perderán el nombre de la etapa`;
        mensaje += `<br><br><strong>¿Deseas continuar con la eliminación?</strong>`;

        showCustomAlert('confirm', 'Confirmar Eliminación', mensaje, () => {
          ejecutarEliminacion(id, nombre);
        });
      } else {
        showCustomAlert('error', 'Error', 'Error al analizar el impacto');
      }
    } catch (error) {
      console.error('Error:', error);
      showCustomAlert('error', 'Error', 'Error al analizar el impacto');
    }
  };

  const ejecutarEliminacion = async (id, nombre) => {
    try {
      const response = await fetch('http://localhost:4001/api/etapas/eliminar', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          etapa_id: id,
          confirmar: true,
          usuario_id: constructora.usuario_id
        })
      });

      if (!response.ok) {
        throw new Error('Error al eliminar etapa');
      }

      showCustomAlert('success', 'Éxito', 'Etapa eliminada correctamente');
      Promise.all([cargarEtapas(), cargarTodosLosEtapas()]);
    } catch (error) {
      console.error('Error:', error);
      showCustomAlert('error', 'Error', error.message);
    }
  };

  const restaurarEtapa = async (id, nombre) => {
    try {
      const response = await fetch('http://localhost:4001/api/etapas/restaurar', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          etapa_id: id,
          usuario_id: constructora.usuario_id
        })
      });

      if (!response.ok) {
        throw new Error('Error al restaurar etapa');
      }

      showCustomAlert('success', 'Éxito', 'Etapa restaurada correctamente');
      Promise.all([cargarEtapas(), cargarTodosLosEtapas()]);
    } catch (error) {
      console.error('Error:', error);
      showCustomAlert('error', 'Error', error.message);
    }
  };
  const etapasActivos = todosLosEtapas.filter(etapa => etapa.deleted_at === null).length;
  const etapasEliminados = todosLosEtapas.filter(etapa => etapa.deleted_at !== null).length;
  return (
    <div style={{padding: isMobile ? '20px 16px' : '24px 32px'}}>
      {/* Header mejorado */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: '24px',
        flexWrap: 'wrap',
        gap: '16px'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1 }}>
          <div style={{
            width: '32px',
            height: '32px',
            background: '#10b98115',
            borderRadius: '8px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <Layers size={18} color="#10b981" />
          </div>
          <div>
            <h2 style={{
              fontSize: isMobile ? '18px' : '20px',
              fontWeight: '600',
              color: '#1e293b',
              margin: '0 0 4px 0'
            }}>
              Etapas de Obra
            </h2>
            <p style={{
              fontSize: '13px',
              color: '#64748b',
              margin: 0
            }}>
              Configura las etapas principales de construcción
            </p>
          </div>
        </div>
        
        <button 
          onClick={() => abrirModal()}
          style={{
            background: 'linear-gradient(135deg, #2563eb 0%, #2563eb 100%)',
            color: 'white',
            border: 'none',
            padding: isMobile ? '10px 16px' : '12px 20px',
            borderRadius: '10px',
            fontSize: isMobile ? '13px' : '14px',
            fontWeight: '600',
            cursor: 'pointer',
            boxShadow: '0 4px 16px rgba(16, 185, 129, 0.25)',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)'
          }}
          className="button-hover"
        >
          <Plus size={16} />
          Añadir etapa
        </button>
      </div>

      {/* Tabs */}
      <div style={{
        display: 'flex',
        borderBottom: '2px solid #e5e7eb',
        marginBottom: '24px',
        gap: '0',
        position: 'relative'
      }}>
        <button
          onClick={() => setFiltroActivos(true)}
          style={{
            background: 'transparent',
            border: 'none',
            padding: '12px 24px',
            fontSize: '14px',
            fontWeight: '600',
            cursor: 'pointer',
            color: filtroActivos ? '#10b981' : '#6b7280',
            borderBottom: filtroActivos ? '2px solid #10b981' : '2px solid transparent',
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            flex: 1,
            textAlign: 'center'
          }}
          onMouseEnter={(e) => !filtroActivos && (e.target.style.color = '#10b981')}
          onMouseLeave={(e) => !filtroActivos && (e.target.style.color = '#6b7280')}
        >
          Activos ({etapasActivos})
        </button>
        <button
          onClick={() => setFiltroActivos(false)}
          style={{
            background: 'transparent',
            border: 'none',
            padding: '12px 24px',
            fontSize: '14px',
            fontWeight: '600',
            cursor: 'pointer',
            color: !filtroActivos ? '#ef4444' : '#6b7280',
            borderBottom: !filtroActivos ? '2px solid #ef4444' : '2px solid transparent',
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            flex: 1,
            textAlign: 'center'
          }}
          onMouseEnter={(e) => filtroActivos && (e.target.style.color = '#ef4444')}
          onMouseLeave={(e) => filtroActivos && (e.target.style.color = '#6b7280')}
        >
          Eliminados ({etapasEliminados})
        </button>
      </div>



      {/* Tabla */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <div style={{
            border: '4px solid #f3f4f6',
            borderTop: '4px solid #3b82f6',
            borderRadius: '50%',
            width: '40px',
            height: '40px',
            animation: 'spin 1s linear infinite',
            margin: '0 auto 16px'
          }}></div>
          <p style={{ color: '#6b7280', margin: 0 }}>Cargando etapas...</p>
        </div>
      ) : (
        <div style={{
          background: 'white',
          borderRadius: '8px',
          border: '1px solid #e5e7eb',
          overflow: 'hidden'
        }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#f9fafb' }}>
                <th style={{ padding: '16px', textAlign: 'left', fontWeight: '600', color: '#374151', fontSize: '14px' }}>Nombre</th>
                <th style={{ padding: '16px', textAlign: 'left', fontWeight: '600', color: '#374151', fontSize: '14px' }}>Fecha</th>
                <th style={{ padding: '16px', textAlign: 'center', fontWeight: '600', color: '#374151', fontSize: '14px', width: '120px' }}>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {etapas
                .filter(etapa => filtroActivos ? etapa.deleted_at === null : etapa.deleted_at !== null)
                .map((etapa) => (
                <tr key={etapa.id} style={{ borderTop: '1px solid #e5e7eb' }}
                    onMouseEnter={(e) => e.target.closest('tr').style.background = '#f0fdf4'}
                    onMouseLeave={(e) => e.target.closest('tr').style.background = 'white'}>
                  <td style={{ padding: '16px', fontSize: '14px', fontWeight: '500', color: '#1f2937' }}>
                    {etapa.nombre}
                  </td>
                  <td style={{ padding: '16px', fontSize: '14px', color: '#6b7280' }}>
                    {new Date(etapa.created_at).toLocaleDateString('es-AR')}
                  </td>
                  <td style={{ padding: '16px', textAlign: 'center' }}>
                    <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
                      {filtroActivos ? (
                        <>
                          <button
                            onClick={() => abrirModal(etapa)}
                            style={{
                              background: '#eff6ff',
                              border: '1px solid #3b82f6',
                              color: '#3b82f6',
                              padding: '8px 12px',
                              borderRadius: '6px',
                              cursor: 'pointer',
                              fontSize: '12px',
                              fontWeight: '500',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '4px',
                              transition: 'all 0.2s ease'
                            }}
                            onMouseEnter={(e) => e.target.style.background = '#dbeafe'}
                            onMouseLeave={(e) => e.target.style.background = '#eff6ff'}
                          >
                            <Edit size={12} />
                            Editar
                          </button>
                          <button
                            onClick={() => eliminarEtapa(etapa.id, etapa.nombre)}
                            style={{
                              background: '#fef2f2',
                              border: '1px solid #ef4444',
                              color: '#ef4444',
                              padding: '8px 12px',
                              borderRadius: '6px',
                              cursor: 'pointer',
                              fontSize: '12px',
                              fontWeight: '500',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '4px',
                              transition: 'all 0.2s ease'
                            }}
                            onMouseEnter={(e) => e.target.style.background = '#fee2e2'}
                            onMouseLeave={(e) => e.target.style.background = '#fef2f2'}
                          >
                            <Trash2 size={12} />
                            Eliminar
                          </button>
                        </>
                      ) : (
                        <button
                          onClick={() => restaurarEtapa(etapa.id, etapa.nombre)}
                          style={{
                            background: '#f0fdf4',
                            border: '1px solid #22c55e',
                            color: '#22c55e',
                            padding: '8px 12px',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            fontSize: '12px',
                            fontWeight: '500',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px',
                            transition: 'all 0.2s ease'
                          }}
                          onMouseEnter={(e) => e.target.style.background = '#dcfce7'}
                          onMouseLeave={(e) => e.target.style.background = '#f0fdf4'}
                        >
                          <Package size={12} />
                          Restaurar
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {etapas.filter(etapa => filtroActivos ? etapa.deleted_at === null : etapa.deleted_at !== null).length === 0 && (
            <div style={{ padding: '40px', textAlign: 'center', color: '#6b7280' }}>
              {filtroActivos ? 'No hay etapas activas' : 'No hay etapas eliminadas'}
            </div>
          )}
        </div>
      )}

      {/* Modal de Edición/Creación */}
      {showModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '20px'
        }}>
          <div style={{
            background: 'white',
            borderRadius: '12px',
            padding: '24px',
            width: '100%',
            maxWidth: '500px',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
            border: '1px solid #e5e7eb'
          }}>
            <h3 style={{ margin: '0 0 20px 0', fontSize: '18px', fontWeight: '600', color: '#1f2937' }}>
              {editandoEtapa ? 'Editar Etapa' : 'Nueva Etapa'}
            </h3>
            
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '500', color: '#374151' }}>
                Nombre *
              </label>
              <input
                type="text"
                value={formData.nombre}
                onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '14px',
                  boxSizing: 'border-box'
                }}
                placeholder="Ej: Preliminares, Obra gris, etc."
              />
            </div>



            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setShowModal(false)}
                style={{
                  padding: '12px 24px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  background: 'white',
                  color: '#374151',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500'
                }}
                onMouseEnter={(e) => e.target.style.background = '#f9fafb'}
                onMouseLeave={(e) => e.target.style.background = 'white'}
              >
                Cancelar
              </button>
              <button
                onClick={guardarEtapa}
                disabled={guardando}
                style={{
                  padding: '12px 24px',
                  border: 'none',
                  borderRadius: '6px',
                  background: guardando ? '#9ca3af' : '#3b82f6',
                  color: 'white',
                  cursor: guardando ? 'not-allowed' : 'pointer',
                  fontSize: '14px',
                  fontWeight: '500',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}
                onMouseEnter={(e) => !guardando && (e.target.style.background = '#2563eb')}
                onMouseLeave={(e) => !guardando && (e.target.style.background = guardando ? '#9ca3af' : '#3b82f6')}
              >
                {guardando ? (
                  <>
                    <div style={{
                      border: '2px solid #ffffff',
                      borderTop: '2px solid transparent',
                      borderRadius: '50%',
                      width: '16px',
                      height: '16px',
                      animation: 'spin 1s linear infinite'
                    }}></div>
                    Guardando...
                  </>
                ) : (
                  <>
                    <CheckCircle2 size={16} />
                    {editandoEtapa ? 'Actualizar' : 'Crear'}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de 3 Opciones */}
      {showModal3Opciones && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '20px'
        }}>
          <div style={{
            background: 'white',
            borderRadius: '12px',
            padding: '24px',
            width: '100%',
            maxWidth: '500px',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
            border: '1px solid #e5e7eb'
          }}>
            <div style={{ textAlign: 'center', marginBottom: '24px' }}>
              <div style={{
                background: '#fef3c7',
                color: '#d97706',
                width: '48px',
                height: '48px',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 16px'
              }}>
                <AlertCircle size={24} />
              </div>
              <h3 style={{ margin: '0 0 8px 0', fontSize: '18px', fontWeight: '600', color: '#1f2937' }}>
                Cambio de nombre detectado
              </h3>
              <p style={{ margin: 0, color: '#6b7280', fontSize: '14px', lineHeight: '1.5' }}>
                Esta etapa tiene {opcionesEdicion?.impacto?.subetapas?.total_subetapas || 0} subetapa(s) y {opcionesEdicion?.impacto?.salidas?.total_salidas || 0} salida(s)
                {(opcionesEdicion?.impacto?.presupuestos?.total_presupuestos || 0) > 0 && (
                  <> y {opcionesEdicion?.impacto?.presupuestos?.total_presupuestos} presupuesto(s)</>
                )}
                {(opcionesEdicion?.impacto?.presupuestos_iniciales?.total_presupuestos || 0) > 0 && (
                  <> y {opcionesEdicion?.impacto?.presupuestos_iniciales?.total_presupuestos} presupuesto(s) inicial(es)</>
                )} asociada(s). ¿Cómo quieres aplicar el cambio?
              </p>
              {(opcionesEdicion?.impacto?.presupuestos?.total_presupuestos || 0) > 0 && (
                <div style={{
                  background: '#fef3c7',
                  border: '1px solid #f59e0b',
                  borderRadius: '8px',
                  padding: '12px',
                  marginTop: '16px'
                }}>
                  <div style={{ fontSize: '13px', color: '#92400e', lineHeight: '1.4', display:'flex', gap:'8px', alignItems:'flex-start' }}>
                    <AlertTriangle size={16} color="#d97706" />
                    <span>
                      <strong>Advertencia:</strong> Esta etapa está asociada a presupuestos. Cambios de nombre pueden afectar la identificación en presupuestos y reportes.
                    </span>
                  </div>
                </div>
              )}
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginBottom: '24px' }}>
              <button
                onClick={() => ejecutarOpcionEdicion('incluir_pasados')}
                disabled={guardando}
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  border: '1px solid #3b82f6',
                  borderRadius: '8px',
                  background: '#eff6ff',
                  color: '#3b82f6',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: guardando ? 'not-allowed' : 'pointer',
                  transition: 'all 0.2s',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px'
                }}
                onMouseEnter={(e) => !guardando && (e.target.style.background = '#dbeafe')}
                onMouseLeave={(e) => !guardando && (e.target.style.background = '#eff6ff')}
              >
                <CheckCircle2 size={16} />
                Incluir Pasados
              </button>
              
              <button
                onClick={() => ejecutarOpcionEdicion('solo_futuros')}
                disabled={guardando}
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  border: '1px solid #f59e0b',
                  borderRadius: '8px',
                  background: '#fffbeb',
                  color: '#f59e0b',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: guardando ? 'not-allowed' : 'pointer',
                  transition: 'all 0.2s',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px'
                }}
                onMouseEnter={(e) => !guardando && (e.target.style.background = '#fef3c7')}
                onMouseLeave={(e) => !guardando && (e.target.style.background = '#fffbeb')}
              >
                <AlertCircle size={16} />
                Solo Futuros
              </button>
              
              <button
                onClick={() => ejecutarOpcionEdicion('crear_nuevo')}
                disabled={guardando}
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  border: '1px solid #10b981',
                  borderRadius: '8px',
                  background: '#ecfdf5',
                  color: '#10b981',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: guardando ? 'not-allowed' : 'pointer',
                  transition: 'all 0.2s',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px'
                }}
                onMouseEnter={(e) => !guardando && (e.target.style.background = '#d1fae5')}
                onMouseLeave={(e) => !guardando && (e.target.style.background = '#ecfdf5')}
              >
                <Plus size={16} />
                Crear Nuevo
              </button>
            </div>

            <div style={{ display: 'flex', justifyContent: 'center' }}>
              <button
                onClick={() => setShowModal3Opciones(false)}
                disabled={guardando}
                style={{
                  padding: '8px 16px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  background: 'white',
                  color: '#6b7280',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: guardando ? 'not-allowed' : 'pointer',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => !guardando && (e.target.style.background = '#f9fafb')}
                onMouseLeave={(e) => !guardando && (e.target.style.background = 'white')}
              >
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
// Proveedores de Cambio ahora se importa desde `src/ProveedoresCambioTab.jsx`
// Subetapas - Depende de etapas
function SubetapasTab({ constructora, showCustomAlert }) {
  const [subetapas, setSubetapas] = useState([]);
  const [todosLosSubetapas, setTodosLosSubetapas] = useState([]);
  const [etapas, setEtapas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showModal3Opciones, setShowModal3Opciones] = useState(false);
  const [filtroActivos, setFiltroActivos] = useState(true);
  const [opcionesEdicion, setOpcionesEdicion] = useState(null);
  const [guardando, setGuardando] = useState(false);
  const [editandoSubetapa, setEditandoSubetapa] = useState(null);
  const [formData, setFormData] = useState({
    nombre: '',
    etapa_id: ''
  });
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Prevenir scroll del fondo cuando hay modales abiertos
  useEffect(() => {
    if (showModal || showModal3Opciones) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [showModal, showModal3Opciones]);

  const cargarTodosLosSubetapas = async () => {
    try {
      const { data, error } = await supabase
        .from('subetapas_obra')
        .select(`
          *,
          etapas_obra (
            nombre
          )
        `)
        .eq('constructora_id', constructora.id)
        .order('nombre');
      
      if (error) {
        console.error('Error cargando todas las subetapas:', error);
      } else {
        setTodosLosSubetapas(data || []);
      }
    } catch (err) {
      console.error('Error:', err);
    }
  };

  const cargarSubetapas = async () => {
    try {
      const { data, error } = await supabase
        .from('subetapas_obra')
        .select(`
          *,
          etapas_obra (
            nombre
          )
        `)
        .eq('constructora_id', constructora.id)
        .order('nombre');
      
      if (error) {
        console.error('Error cargando subetapas:', error);
      } else {
        // Filtrar en el frontend para mayor control
        const subetapasFiltradas = filtroActivos 
          ? (data || []).filter(subetapa => subetapa.deleted_at === null)
          : (data || []).filter(subetapa => subetapa.deleted_at !== null);
        setSubetapas(subetapasFiltradas);
      }
    } catch (err) {
      console.error('Error:', err);
    }
    setLoading(false);
  };

  const cargarEtapas = async () => {
    try {
      const { data: etapasData, error: etapasError } = await supabase
        .from('etapas_obra')
        .select('*')
        .eq('constructora_id', constructora.id)
        .eq('activo', true)
        .is('deleted_at', null)
        .order('nombre');
      
      if (etapasError) {
        console.error('Error cargando etapas:', etapasError);
      } else {
        setEtapas(etapasData || []);
      }
    } catch (err) {
      console.error('Error:', err);
    }
  };

  useEffect(() => {
    if (constructora?.id) {
      Promise.all([cargarTodosLosSubetapas(), cargarSubetapas(), cargarEtapas()]);
    }
  }, [constructora?.id]);

  useEffect(() => {
    if (constructora?.id) {
      cargarSubetapas();
    }
  }, [filtroActivos, constructora?.id]);

  useEffect(() => {
    if (constructora?.id) {
      cargarTodosLosSubetapas();
    }
  }, [filtroActivos, constructora?.id]);

  const abrirModal = (subetapa = null) => {
    if (subetapa) {
      setEditandoSubetapa(subetapa);
      setFormData({
        nombre: subetapa.nombre,
        etapa_id: subetapa.etapa_id
      });
    } else {
      setEditandoSubetapa(null);
      setFormData({
        nombre: '',
        etapa_id: ''
      });
    }
    setShowModal(true);
  };

  const guardarSubetapa = async () => {
    if (!formData.nombre.trim() || !formData.etapa_id) {
      showCustomAlert('error', 'Error', 'Por favor completa nombre y selecciona una etapa');
      return;
    }

    setGuardando(true);
      
    try {
      if (editandoSubetapa) {
        // Verificar si el nombre cambió
        if (formData.nombre !== editandoSubetapa.nombre) {
          // Analizar impacto antes de editar
          const impactoResponse = await fetch('http://localhost:4001/api/subetapas/analizar-impacto', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              subetapa_id: editandoSubetapa.id,
              accion: 'editar',
              cambios: { nombre: formData.nombre }
            })
          });

          const impactoData = await impactoResponse.json();
          console.log('🔍 Impacto data:', impactoData);

          if (impactoData.success && (
            impactoData.impacto.salidas.total_salidas > 0 ||
            (impactoData.impacto.presupuestos?.total_presupuestos || 0) > 0 ||
            (impactoData.impacto.presupuestos_iniciales?.total_presupuestos || 0) > 0
          )) {
            // Hay impacto, mostrar opciones
            setOpcionesEdicion({
              subetapa_id: editandoSubetapa.id,
              cambios: formData,
              impacto: impactoData.impacto
            });
            setShowModal3Opciones(true);
            setShowModal(false);
            setGuardando(false);
            return;
          }
        }

        // No hay impacto o no cambió el nombre, editar directamente
        const editarResponse = await fetch('http://localhost:4001/api/subetapas/editar', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            subetapa_id: editandoSubetapa.id,
            cambios: formData,
            usuario_id: constructora.usuario_id
          })
        });

        if (!editarResponse.ok) {
          throw new Error('Error al editar subetapa');
        }

        showCustomAlert('success', 'Éxito', 'Subetapa actualizada correctamente');
      } else {
        // Crear nueva subetapa
        const { data, error } = await supabase
          .from('subetapas_obra')
          .insert({
            constructora_id: constructora.id,
            etapa_id: formData.etapa_id,
            nombre: formData.nombre,
            activo: true,
            created_by: constructora.usuario_id,
            updated_by: constructora.usuario_id
          })
          .select()
          .single();

        if (error) {
          throw new Error('Error al crear subetapa: ' + error.message);
        }

        showCustomAlert('success', 'Éxito', 'Subetapa creada correctamente');
      }

      setShowModal(false);
      Promise.all([cargarSubetapas(), cargarTodosLosSubetapas()]);
    } catch (error) {
      console.error('Error:', error);
      showCustomAlert('error', 'Error', error.message);
    } finally {
      setGuardando(false);
    }
  };

  const ejecutarOpcionEdicion = async (opcion) => {
    setGuardando(true);

    try {
      if (opcion === 'incluir_pasados') {
        // Editar directamente (afecta registros pasados)
        const editarResponse = await fetch('http://localhost:4001/api/subetapas/editar', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            subetapa_id: opcionesEdicion.subetapa_id,
            cambios: opcionesEdicion.cambios,
            opciones: { afectarRegistrosPasados: true },
            usuario_id: constructora.usuario_id
          })
        });

        if (!editarResponse.ok) {
          throw new Error('Error al editar subetapa');
        }

        showCustomAlert('success', 'Éxito', 'Subetapa actualizada (incluyendo registros pasados)');
      } else {
        // Solo futuros o crear nuevo - crear nueva subetapa y desactivar la vieja
        const crearNuevoResponse = await fetch('http://localhost:4001/api/subetapas/crear-nuevo', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            subetapa_id_viejo: opcionesEdicion.subetapa_id,
            nueva_subetapa: {
              constructora_id: constructora.id,
              etapa_id: opcionesEdicion.cambios.etapa_id,
              nombre: opcionesEdicion.cambios.nombre
            },
            usuario_id: constructora.usuario_id,
            mantener_vieja_activa: false
          })
        });

        if (!crearNuevoResponse.ok) {
          throw new Error('Error creando nueva subetapa');
        }

        showCustomAlert('success', 'Éxito', 'Nueva subetapa creada (solo para registros futuros)');
      }

      setShowModal3Opciones(false);
      Promise.all([cargarSubetapas(), cargarTodosLosSubetapas()]);
    } catch (error) {
      console.error('Error:', error);
      showCustomAlert('error', 'Error', error.message);
    } finally {
    setGuardando(false);
    }
  };

  const eliminarSubetapa = async (id, nombre) => {
    try {
      // Analizar impacto antes de eliminar
      const impactoResponse = await fetch('http://localhost:4001/api/subetapas/analizar-impacto', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          subetapa_id: id,
          accion: 'eliminar'
        })
      });

      const impactoData = await impactoResponse.json();

      if (impactoData.success) {
        const { salidas, presupuestos_iniciales } = impactoData.impacto;
        let mensaje = `<strong>¿Estás seguro de eliminar la subetapa "${nombre}"?</strong><br><br>`;
        
        if (salidas.total_salidas > 0) {
          mensaje += `<br>• Esta subetapa tiene ${salidas.total_salidas} salida(s) asociada(s)`;
        }
        if (presupuestos_iniciales?.total_presupuestos > 0) {
          mensaje += `<br>• Hay ${presupuestos_iniciales.total_presupuestos} presupuesto(s) inicial(es) vinculados a través de salidas o directamente`;
        }
        
        mensaje += `<br>• La subetapa se marcará como eliminada`;
        mensaje += `<br>• Las salidas mantendrán su referencia pero perderán el nombre de la subetapa`;
        mensaje += `<br><br><strong>¿Deseas continuar con la eliminación?</strong>`;

        showCustomAlert('confirm', 'Confirmar Eliminación', mensaje, () => {
          ejecutarEliminacion(id, nombre);
        });
          } else {
        showCustomAlert('error', 'Error', 'Error al analizar el impacto');
      }
    } catch (error) {
      console.error('Error:', error);
      showCustomAlert('error', 'Error', 'Error al analizar el impacto');
    }
  };

  const ejecutarEliminacion = async (id, nombre) => {
    try {
      const response = await fetch('http://localhost:4001/api/subetapas/eliminar', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          subetapa_id: id,
          confirmar: true,
          usuario_id: constructora.usuario_id
        })
      });

      if (!response.ok) {
        throw new Error('Error al eliminar subetapa');
      }

      showCustomAlert('success', 'Éxito', 'Subetapa eliminada correctamente');
      Promise.all([cargarSubetapas(), cargarTodosLosSubetapas()]);
    } catch (error) {
      console.error('Error:', error);
      showCustomAlert('error', 'Error', error.message);
    }
  };

  const restaurarSubetapa = async (id, nombre) => {
    try {
      const response = await fetch('http://localhost:4001/api/subetapas/restaurar', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          subetapa_id: id,
          usuario_id: constructora.usuario_id
        })
      });

      if (!response.ok) {
        throw new Error('Error al restaurar subetapa');
      }

      showCustomAlert('success', 'Éxito', 'Subetapa restaurada correctamente');
      Promise.all([cargarSubetapas(), cargarTodosLosSubetapas()]);
    } catch (error) {
      console.error('Error:', error);
      showCustomAlert('error', 'Error', error.message);
    }
  };
  const subetapasActivos = todosLosSubetapas.filter(subetapa => subetapa.deleted_at === null).length;
  const subetapasEliminados = todosLosSubetapas.filter(subetapa => subetapa.deleted_at !== null).length;
  return (
    <div style={{padding: isMobile ? '20px 16px' : '24px 32px'}}>
      {/* Header mejorado */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: '24px',
        flexWrap: 'wrap',
        gap: '16px'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1 }}>
          <div style={{
            width: '32px',
            height: '32px',
            background: '#3b82f615',
            borderRadius: '8px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <Activity size={18} color="#3b82f6" />
          </div>
          <div>
            <h2 style={{
              fontSize: isMobile ? '18px' : '20px',
              fontWeight: '600',
              color: '#1e293b',
              margin: '0 0 4px 0'
            }}>
              Subetapas de Obra
            </h2>
            <p style={{
              fontSize: '13px',
              color: '#64748b',
              margin: 0
            }}>
              Detalla las subetapas dentro de cada etapa principal
            </p>
          </div>
        </div>
        
        <button 
          onClick={() => abrirModal()}
          disabled={etapas.length === 0}
          style={{
            background: etapas.length === 0 ? '#94a3b8' : 'linear-gradient(135deg, #2563eb 0%, #2563eb 100%)',
            color: 'white',
            border: 'none',
            padding: isMobile ? '10px 16px' : '12px 20px',
            borderRadius: '10px',
            fontSize: isMobile ? '13px' : '14px',
            fontWeight: '600',
            cursor: etapas.length === 0 ? 'not-allowed' : 'pointer',
            boxShadow: etapas.length === 0 ? 'none' : '0 4px 16px rgba(59, 130, 246, 0.25)',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)'
          }}
          className={etapas.length > 0 ? "button-hover" : ""}
        >
          <Plus size={16} />
          Añadir subetapa
        </button>
      </div>

      {/* Tabs */}
      <div style={{
        display: 'flex',
        borderBottom: '2px solid #e5e7eb',
        marginBottom: '24px',
        gap: '0',
        position: 'relative'
      }}>
        <button
          onClick={() => setFiltroActivos(true)}
          style={{
            background: 'transparent',
            border: 'none',
            padding: '12px 24px',
            fontSize: '14px',
            fontWeight: '600',
            cursor: 'pointer',
            color: filtroActivos ? '#10b981' : '#6b7280',
            borderBottom: filtroActivos ? '2px solid #10b981' : '2px solid transparent',
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            flex: 1,
            textAlign: 'center'
          }}
          onMouseEnter={(e) => !filtroActivos && (e.target.style.color = '#10b981')}
          onMouseLeave={(e) => !filtroActivos && (e.target.style.color = '#6b7280')}
        >
          Activos ({subetapasActivos})
        </button>
        <button
          onClick={() => setFiltroActivos(false)}
          style={{
            background: 'transparent',
            border: 'none',
            padding: '12px 24px',
            fontSize: '14px',
            fontWeight: '600',
            cursor: 'pointer',
            color: !filtroActivos ? '#ef4444' : '#6b7280',
            borderBottom: !filtroActivos ? '2px solid #ef4444' : '2px solid transparent',
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            flex: 1,
            textAlign: 'center'
          }}
          onMouseEnter={(e) => filtroActivos && (e.target.style.color = '#ef4444')}
          onMouseLeave={(e) => filtroActivos && (e.target.style.color = '#6b7280')}
        >
          Eliminados ({subetapasEliminados})
        </button>
      </div>

      {etapas.length === 0 ? (
        <div style={{
          background: '#fef3cd',
          borderRadius: '12px',
          padding: isMobile ? '40px 20px' : '60px 40px',
          textAlign: 'center',
          border: '2px solid #fbbf24'
        }}>
          <div style={{
            width: '64px',
            height: '64px',
            background: '#fed7aa',
            borderRadius: '32px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 20px'
          }}>
            <AlertCircle size={32} color="#d97706" />
          </div>
          <h3 style={{
            fontSize: '18px',
            fontWeight: '600',
            color: '#92400e',
            marginBottom: '8px'
          }}>
            Primero crea etapas
          </h3>
          <p style={{
            fontSize: '14px',
            color: '#d97706',
            margin: 0
          }}>
            Necesitas crear etapas de obra antes de poder agregar subetapas
          </p>
        </div>
      ) : loading ? (
        <div style={{ textAlign: 'center', padding: '40px' }}>
        <div style={{
            border: '4px solid #f3f4f6',
            borderTop: '4px solid #3b82f6',
            borderRadius: '50%',
            width: '40px',
            height: '40px',
            animation: 'spin 1s linear infinite',
            margin: '0 auto 16px'
          }}></div>
          <p style={{ color: '#6b7280', margin: 0 }}>Cargando subetapas...</p>
        </div>
      ) : (
        <div style={{
          background: 'white',
          borderRadius: '8px',
          border: '1px solid #e5e7eb',
          overflow: 'hidden'
        }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
              <tr style={{ background: '#f9fafb' }}>
                <th style={{ padding: '16px', textAlign: 'left', fontWeight: '600', color: '#374151', fontSize: '14px' }}>Nombre</th>
                <th style={{ padding: '16px', textAlign: 'left', fontWeight: '600', color: '#374151', fontSize: '14px' }}>Etapa</th>
                <th style={{ padding: '16px', textAlign: 'left', fontWeight: '600', color: '#374151', fontSize: '14px' }}>Fecha</th>
                <th style={{ padding: '16px', textAlign: 'center', fontWeight: '600', color: '#374151', fontSize: '14px', width: '120px' }}>Acciones</th>
                </tr>
              </thead>
              <tbody>
              {subetapas
                .filter(subetapa => filtroActivos ? subetapa.deleted_at === null : subetapa.deleted_at !== null)
                .map((subetapa) => (
                <tr key={subetapa.id} style={{ borderTop: '1px solid #e5e7eb' }}
                    onMouseEnter={(e) => e.target.closest('tr').style.background = '#f0fdf4'}
                    onMouseLeave={(e) => e.target.closest('tr').style.background = 'white'}>
                  <td style={{ padding: '16px', fontSize: '14px', fontWeight: '500', color: '#1f2937' }}>
                      {subetapa.nombre}
                    </td>
                  <td style={{ padding: '16px', fontSize: '14px', color: '#6b7280' }}>
                      <span style={{
                        background: '#dbeafe',
                        color: '#1d4ed8',
                        padding: '4px 8px',
                        borderRadius: '6px',
                      fontSize: '12px',
                        fontWeight: '500'
                      }}>
                        {subetapa.etapas_obra?.nombre || '-'}
                      </span>
                    </td>
                  <td style={{ padding: '16px', fontSize: '14px', color: '#6b7280' }}>
                    {new Date(subetapa.created_at).toLocaleDateString('es-AR')}
                  </td>
                  <td style={{ padding: '16px', textAlign: 'center' }}>
                    <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
                      {filtroActivos ? (
                        <>
                        <button 
                          onClick={() => abrirModal(subetapa)}
                          style={{
                            background: '#eff6ff',
                            border: '1px solid #3b82f6',
                            color: '#3b82f6',
                              padding: '8px 12px',
                              borderRadius: '6px',
                            cursor: 'pointer',
                              fontSize: '12px',
                              fontWeight: '500',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px',
                            transition: 'all 0.2s ease'
                          }}
                            onMouseEnter={(e) => e.target.style.background = '#dbeafe'}
                            onMouseLeave={(e) => e.target.style.background = '#eff6ff'}
                        >
                          <Edit size={12} />
                          Editar
                        </button>
                        <button 
                          onClick={() => eliminarSubetapa(subetapa.id, subetapa.nombre)}
                          style={{
                            background: '#fef2f2',
                            border: '1px solid #ef4444',
                            color: '#ef4444',
                              padding: '8px 12px',
                              borderRadius: '6px',
                            cursor: 'pointer',
                              fontSize: '12px',
                              fontWeight: '500',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px',
                            transition: 'all 0.2s ease'
                          }}
                            onMouseEnter={(e) => e.target.style.background = '#fee2e2'}
                            onMouseLeave={(e) => e.target.style.background = '#fef2f2'}
                        >
                          <Trash2 size={12} />
                          Eliminar
                        </button>
                        </>
                      ) : (
                        <button
                          onClick={() => restaurarSubetapa(subetapa.id, subetapa.nombre)}
                          style={{
                            background: '#f0fdf4',
                            border: '1px solid #22c55e',
                            color: '#22c55e',
                            padding: '8px 12px',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            fontSize: '12px',
                            fontWeight: '500',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px',
                            transition: 'all 0.2s ease'
                          }}
                          onMouseEnter={(e) => e.target.style.background = '#dcfce7'}
                          onMouseLeave={(e) => e.target.style.background = '#f0fdf4'}
                        >
                          <Package size={12} />
                          Restaurar
                        </button>
                      )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          {subetapas.filter(subetapa => filtroActivos ? subetapa.deleted_at === null : subetapa.deleted_at !== null).length === 0 && (
            <div style={{ padding: '40px', textAlign: 'center', color: '#6b7280' }}>
              {filtroActivos ? 'No hay subetapas activas' : 'No hay subetapas eliminadas'}
          </div>
          )}
        </div>
      )}

      {/* Modal de Edición/Creación */}
      {showModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '20px'
        }}>
          <div style={{
            background: 'white',
            borderRadius: '12px',
            padding: '24px',
            width: '100%',
            maxWidth: '500px',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
            border: '1px solid #e5e7eb'
          }}>
            <h3 style={{ margin: '0 0 20px 0', fontSize: '18px', fontWeight: '600', color: '#1f2937' }}>
              {editandoSubetapa ? 'Editar Subetapa' : 'Nueva Subetapa'}
              </h3>
            
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '500', color: '#374151' }}>
                Nombre *
                </label>
                <input
                  type="text"
                  value={formData.nombre}
                onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
                  style={{
                    width: '100%',
                  padding: '12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '14px',
                  boxSizing: 'border-box'
                }}
                placeholder="Ej: Cimientos, Estructura, etc."
                />
              </div>

            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '500', color: '#374151' }}>
                  Etapa Principal *
                </label>
                <select
                  value={formData.etapa_id}
                  onChange={(e) => setFormData({ ...formData, etapa_id: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '1px solid #d1d5db',
                    borderRadius: '8px',
                    fontSize: '14px',
                    boxSizing: 'border-box',
                    background: 'white',
                    WebkitAppearance: 'none',
                    appearance: 'none',
                    outline: 'none'
                  }}
                >
                  <option value="">Selecciona una etapa</option>
                  {etapas.map((etapa) => (
                    <option key={etapa.id} value={etapa.id}>
                      {etapa.nombre}
                    </option>
                  ))}
                </select>
            </div>
            
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setShowModal(false)}
                style={{
                  padding: '12px 24px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  background: 'white',
                  color: '#374151',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500'
                }}
                onMouseEnter={(e) => e.target.style.background = '#f9fafb'}
                onMouseLeave={(e) => e.target.style.background = 'white'}
              >
                Cancelar
              </button>
              <button
                onClick={guardarSubetapa}
                disabled={guardando}
                style={{
                  padding: '12px 24px',
                  border: 'none',
                  borderRadius: '6px',
                  background: guardando ? '#9ca3af' : '#3b82f6',
                  color: 'white',
                  cursor: guardando ? 'not-allowed' : 'pointer',
                  fontSize: '14px',
                  fontWeight: '500',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}
                onMouseEnter={(e) => !guardando && (e.target.style.background = '#2563eb')}
                onMouseLeave={(e) => !guardando && (e.target.style.background = guardando ? '#9ca3af' : '#3b82f6')}
              >
                {guardando ? (
                  <>
                    <div style={{
                      border: '2px solid #ffffff',
                      borderTop: '2px solid transparent',
                      borderRadius: '50%',
                      width: '16px',
                      height: '16px',
                      animation: 'spin 1s linear infinite'
                    }}></div>
                    Guardando...
                  </>
                ) : (
                  <>
                    <CheckCircle2 size={16} />
                    {editandoSubetapa ? 'Actualizar' : 'Crear'}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de 3 Opciones */}
      {showModal3Opciones && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '20px'
        }}>
          <div style={{
            background: 'white',
            borderRadius: '12px',
            padding: '24px',
            width: '100%',
            maxWidth: '500px',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
            border: '1px solid #e5e7eb'
          }}>
            <div style={{ textAlign: 'center', marginBottom: '24px' }}>
              <div style={{
                background: '#fef3c7',
                color: '#d97706',
                width: '48px',
                height: '48px',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 16px'
              }}>
                <AlertCircle size={24} />
              </div>
              <h3 style={{ margin: '0 0 8px 0', fontSize: '18px', fontWeight: '600', color: '#1f2937' }}>
                Cambio de nombre detectado
              </h3>
              <p style={{ margin: 0, color: '#6b7280', fontSize: '14px', lineHeight: '1.5' }}>
                {opcionesEdicion?.concepto_id ? (
                  <>
                    Este concepto tiene {opcionesEdicion?.impacto?.salidas?.total_salidas || 0} salida(s) asociada(s)
                    {(opcionesEdicion?.impacto?.presupuestos?.total_presupuestos || 0) > 0 && (
                      <>. Además, está asociado a {opcionesEdicion?.impacto?.presupuestos?.total_presupuestos || 0} presupuesto(s)</>
                    )}.
                    {' '}¿Cómo quieres aplicar el cambio?
                  </>
                ) : (
                  <>
                    Esta subetapa tiene {opcionesEdicion?.impacto?.salidas?.total_salidas || 0} salida(s) asociada(s)
                    {(opcionesEdicion?.impacto?.presupuestos?.total_presupuestos || 0) > 0 && (
                      <> y {opcionesEdicion?.impacto?.presupuestos?.total_presupuestos} presupuesto(s)</>
                    )}
                    {(opcionesEdicion?.impacto?.presupuestos_iniciales?.total_presupuestos || 0) > 0 && (
                      <> y {opcionesEdicion?.impacto?.presupuestos_iniciales?.total_presupuestos} presupuesto(s) inicial(es)</>
                    )}. ¿Cómo quieres aplicar el cambio?
                  </>
                )}
              </p>
              
              {/* Advertencia específica para conceptos de salida con presupuestos */}
              {opcionesEdicion?.concepto_id && (opcionesEdicion?.impacto?.presupuestos?.total_presupuestos || 0) > 0 && (
                <div style={{
                  background: '#fef3c7',
                  border: '1px solid #f59e0b',
                  borderRadius: '8px',
                  padding: '12px',
                  marginTop: '16px',
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '8px'
                }}>
                  <AlertTriangle size={16} color="#d97706" style={{ flexShrink: 0, marginTop: '2px' }} />
                  <div style={{ fontSize: '13px', color: '#92400e', lineHeight: '1.4' }}>
                    <strong>Advertencia:</strong> Si cambias el nombre del concepto, los presupuestos ya no podrán identificar correctamente los gastos asociados a este concepto. Esto puede afectar el seguimiento de gastos en los presupuestos.
                  </div>
                </div>
              )}
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginBottom: '24px' }}>
              <button
                onClick={() => ejecutarOpcionEdicion('incluir_pasados')}
                disabled={guardando}
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  border: '1px solid #3b82f6',
                  borderRadius: '8px',
                  background: '#eff6ff',
                  color: '#3b82f6',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: guardando ? 'not-allowed' : 'pointer',
                  transition: 'all 0.2s',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px'
                }}
                onMouseEnter={(e) => !guardando && (e.target.style.background = '#dbeafe')}
                onMouseLeave={(e) => !guardando && (e.target.style.background = '#eff6ff')}
              >
                <CheckCircle2 size={16} />
                Incluir Pasados
              </button>
              
              <button
                onClick={() => ejecutarOpcionEdicion('solo_futuros')}
                disabled={guardando}
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  border: '1px solid #f59e0b',
                  borderRadius: '8px',
                  background: '#fffbeb',
                  color: '#f59e0b',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: guardando ? 'not-allowed' : 'pointer',
                  transition: 'all 0.2s',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px'
                }}
                onMouseEnter={(e) => !guardando && (e.target.style.background = '#fef3c7')}
                onMouseLeave={(e) => !guardando && (e.target.style.background = '#fffbeb')}
              >
                <AlertCircle size={16} />
                Solo Futuros
              </button>
              
              <button
                onClick={() => ejecutarOpcionEdicion('crear_nuevo')}
                disabled={guardando}
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  border: '1px solid #10b981',
                  borderRadius: '8px',
                  background: '#ecfdf5',
                  color: '#10b981',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: guardando ? 'not-allowed' : 'pointer',
                  transition: 'all 0.2s',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px'
                }}
                onMouseEnter={(e) => !guardando && (e.target.style.background = '#d1fae5')}
                onMouseLeave={(e) => !guardando && (e.target.style.background = '#ecfdf5')}
              >
                <Plus size={16} />
                Crear Nuevo
              </button>
            </div>

            <div style={{ display: 'flex', justifyContent: 'center' }}>
              <button
                onClick={() => setShowModal3Opciones(false)}
                disabled={guardando}
                style={{
                  padding: '8px 16px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  background: 'white',
                  color: '#6b7280',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: guardando ? 'not-allowed' : 'pointer',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => !guardando && (e.target.style.background = '#f9fafb')}
                onMouseLeave={(e) => !guardando && (e.target.style.background = 'white')}
              >
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
// Proveedores Servicios - Con soft delete/edit
function ProveedoresServiciosTab({ constructora, showCustomAlert }) {
  const [proveedores, setProveedores] = useState([]);
  const [proveedoresEliminados, setProveedoresEliminados] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editandoProveedor, setEditandoProveedor] = useState(null);
  const [formData, setFormData] = useState({
    nombre: '',
    telefono: '',
    email: '',
    direccion: '',
    cuit: ''
  });
  const [guardando, setGuardando] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [activeTab, setActiveTab] = useState('activos');
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showModal3Opciones, setShowModal3Opciones] = useState(false);
  const [proveedorSeleccionado, setProveedorSeleccionado] = useState(null);
  const [analisisImpacto, setAnalisisImpacto] = useState(null);
  const [opcionesEdicion, setOpcionesEdicion] = useState(null);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Función para formatear CUIT
  const formatearCuit = (valor) => {
    const numeros = valor.replace(/\D/g, '');
    const limitado = numeros.substring(0, 11);
    
    if (limitado.length <= 2) {
      return limitado;
    } else if (limitado.length <= 10) {
      return `${limitado.substring(0, 2)}-${limitado.substring(2)}`;
    } else {
      return `${limitado.substring(0, 2)}-${limitado.substring(2, 10)}-${limitado.substring(10)}`;
    }
  };

  const handleCuitChange = (valor) => {
    const formateado = formatearCuit(valor);
    setFormData({...formData, cuit: formateado});
  };

  const cargarProveedores = async () => {
    try {
      // Cargar proveedores activos
      const { data: activos, error: errorActivos } = await supabase
        .from('proveedores_servicios')
        .select('*')
        .eq('constructora_id', constructora.id)
        .eq('activo', true)
        .is('deleted_at', null)
        .order('nombre');
      
      if (errorActivos) {
        console.error('Error cargando proveedores activos:', errorActivos);
      } else {
        setProveedores(activos || []);
      }

      // Cargar proveedores eliminados
      const { data: eliminados, error: errorEliminados } = await supabase
        .from('proveedores_servicios')
        .select('*')
        .eq('constructora_id', constructora.id)
        .eq('activo', false)
        .not('deleted_at', 'is', null)
        .order('nombre');
      
      if (errorEliminados) {
        console.error('Error cargando proveedores eliminados:', errorEliminados);
      } else {
        setProveedoresEliminados(eliminados || []);
      }
    } catch (err) {
      console.error('Error:', err);
    }
    setLoading(false);
  };

  useEffect(() => {
    if (constructora?.id) {
      cargarProveedores();
    }
  }, [constructora]);

  // ESC handler para modales
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        if (showModal) setShowModal(false);
        if (showDeleteModal) setShowDeleteModal(false);
        if (showModal3Opciones) setShowModal3Opciones(false);
      }
    };

    if (showModal || showDeleteModal || showModal3Opciones) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [showModal, showDeleteModal, showModal3Opciones]);

  const abrirModal = (proveedor = null) => {
    if (proveedor) {
      setEditandoProveedor(proveedor);
      setFormData({
        nombre: proveedor.nombre,
        telefono: proveedor.telefono || '',
        email: proveedor.email || '',
        direccion: proveedor.direccion || '',
        cuit: proveedor.cuit || ''
      });
    } else {
      setEditandoProveedor(null);
      setFormData({
        nombre: '',
        telefono: '',
        email: '',
        direccion: '',
        cuit: ''
      });
    }
    setShowModal(true);
  };

  const guardarProveedor = async () => {
    if (!formData.nombre.trim()) {
      showCustomAlert('error', 'Error', 'Por favor completa el nombre del proveedor');
      return;
    }

    setGuardando(true);
    try {
      let result;
      
      if (editandoProveedor) {
        // Si estamos editando, analizar impacto primero
        const analisisResponse = await fetch('http://localhost:4001/api/proveedores/analizar-impacto', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            proveedor_id: editandoProveedor.id,
            accion: 'editar',
            cambios: formData
          })
        });

        const analisis = await analisisResponse.json();
        
                          console.log('🔍 Analisis completo:', analisis);
         console.log('🔍 Total salidas:', analisis.impacto?.salidas?.total_salidas);
         console.log('🔍 Total presupuestos:', analisis.impacto?.presupuestos?.total_presupuestos);
         
         if (analisis.success && (analisis.impacto.salidas.total_salidas > 0 || analisis.impacto.presupuestos.total_presupuestos > 0)) {
           // Hay registros asociados, mostrar modal de opciones
           console.log('🔍 Mostrando modal de opciones');
           setOpcionesEdicion({
             proveedor_id: editandoProveedor.id,
             cambios: formData,
             impacto: analisis.impacto
           });
           setShowModal3Opciones(true);
           setShowModal(false);
           setGuardando(false);
           return;
         }
      }

      // Si no hay registros asociados o es un nuevo proveedor, proceder normalmente
      if (editandoProveedor) {
        result = await supabase
          .from('proveedores_servicios')
          .update(formData)
          .eq('id', editandoProveedor.id)
          .select();
      } else {
        result = await supabase
          .from('proveedores_servicios')
          .insert([{
            constructora_id: constructora.id,
            ...formData,
            activo: true
          }])
          .select();
      }

      const { data, error } = result;
      
      if (error) {
        console.error('Error guardando proveedor:', error);
        showCustomAlert('error', 'Error', 'Error al guardar: ' + error.message);
      } else {
        setFormData({
          nombre: '',
          telefono: '',
          email: '',
          direccion: '',
          cuit: ''
        });
        setEditandoProveedor(null);
        setShowModal(false);
        cargarProveedores();
        showCustomAlert('success', 'Éxito', editandoProveedor ? 'Proveedor actualizado correctamente' : 'Proveedor creado correctamente');
      }
    } catch (err) {
      console.error('Error:', err);
      showCustomAlert('error', 'Error', 'Error: ' + err.message);
    }
    setGuardando(false);
  };

  const ejecutarOpcionEdicion = async (opcion) => {
    setGuardando(true);
    try {
      let resultado;
      
      if (opcion === 'incluir_pasados') {
        // Editar el proveedor existente
        const response = await fetch('http://localhost:4001/api/proveedores/editar', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            proveedor_id: opcionesEdicion.proveedor_id,
            cambios: opcionesEdicion.cambios,
            usuario_id: constructora.usuario_id
          })
        });
        resultado = await response.json();
      } else if (opcion === 'solo_futuros') {
        // Crear nuevo proveedor y desactivar el viejo
        const response = await fetch('http://localhost:4001/api/proveedores/crear-nuevo', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            proveedor_id_viejo: opcionesEdicion.proveedor_id,
            nuevo_proveedor: {
              ...opcionesEdicion.cambios,
              constructora_id: constructora.id
            },
            usuario_id: constructora.usuario_id
          })
        });
        resultado = await response.json();
      } else if (opcion === 'crear_nuevo') {
        // Crear nuevo proveedor sin desactivar el viejo
        const response = await fetch('http://localhost:4001/api/proveedores/crear-nuevo', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            proveedor_id_viejo: opcionesEdicion.proveedor_id,
            nuevo_proveedor: {
              ...opcionesEdicion.cambios,
              constructora_id: constructora.id
            },
            usuario_id: constructora.usuario_id
          })
        });
        resultado = await response.json();
      }
      
      if (resultado.success) {
        setShowModal3Opciones(false);
        setOpcionesEdicion(null);
        setFormData({
          nombre: '',
          telefono: '',
          email: '',
          direccion: '',
          cuit: ''
        });
        cargarProveedores();
        showCustomAlert('success', 'Éxito', 'Proveedor actualizado correctamente');
      } else {
        showCustomAlert('error', 'Error', resultado.error || 'Error al actualizar proveedor');
      }
    } catch (error) {
      console.error('Error ejecutando opción de edición:', error);
      showCustomAlert('error', 'Error', 'Error: ' + error.message);
    }
    setGuardando(false);
  };

  const eliminarProveedor = async (id, nombre) => {
    try {
      // Analizar impacto primero
      const analisisResponse = await fetch('http://localhost:4001/api/proveedores/eliminar', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          proveedor_id: id,
          confirmar: false
        })
      });

      const analisis = await analisisResponse.json();
      
      if (analisis.success && analisis.tipo === 'analisis') {
        setAnalisisImpacto(analisis.data.impacto);
        setProveedorSeleccionado({ id, nombre });
        setShowDeleteModal(true);
          } else {
        showCustomAlert('error', 'Error', analisis.error || 'Error analizando impacto');
      }
    } catch (error) {
      console.error('Error analizando impacto:', error);
      showCustomAlert('error', 'Error', 'Error: ' + error.message);
    }
  };

  const ejecutarEliminacion = async () => {
    setGuardando(true);
    try {
      const response = await fetch('http://localhost:4001/api/proveedores/eliminar', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          proveedor_id: proveedorSeleccionado.id,
          confirmar: true,
          usuario_id: constructora.usuario_id
        })
      });

      const resultado = await response.json();
      
      if (resultado.success) {
        setShowDeleteModal(false);
        setProveedorSeleccionado(null);
        setAnalisisImpacto(null);
            cargarProveedores();
            showCustomAlert('success', 'Éxito', 'Proveedor eliminado correctamente');
      } else {
        showCustomAlert('error', 'Error', resultado.error || 'Error al eliminar proveedor');
      }
    } catch (error) {
      console.error('Error ejecutando eliminación:', error);
      showCustomAlert('error', 'Error', 'Error: ' + error.message);
    }
    setGuardando(false);
  };

  const restaurarProveedor = async (id, nombre) => {
    try {
      const response = await fetch('http://localhost:4001/api/proveedores/restaurar', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          proveedor_id: id,
          usuario_id: constructora.usuario_id
        })
      });

      const resultado = await response.json();
      
      if (resultado.success) {
        cargarProveedores();
        showCustomAlert('success', 'Éxito', 'Proveedor restaurado correctamente');
      } else {
        showCustomAlert('error', 'Error', resultado.error || 'Error al restaurar proveedor');
      }
    } catch (error) {
      console.error('Error restaurando proveedor:', error);
      showCustomAlert('error', 'Error', 'Error: ' + error.message);
    }
  };
  return (
    <div style={{padding: isMobile ? '20px 16px' : '24px 32px'}}>
      {/* Header mejorado */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: '24px',
        flexWrap: 'wrap',
        gap: '16px'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1 }}>
          <div style={{
            width: '32px',
            height: '32px',
            background: '#8b5cf615',
            borderRadius: '8px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <Wrench size={18} color="#8b5cf6" />
          </div>
          <div>
            <h2 style={{
              fontSize: isMobile ? '18px' : '20px',
              fontWeight: '600',
              color: '#1e293b',
              margin: '0 0 4px 0'
            }}>
              Proveedores de Servicios
            </h2>
            <p style={{
              fontSize: '13px',
              color: '#64748b',
              margin: 0
            }}>
              Gestiona el directorio de proveedores y servicios
            </p>
          </div>
        </div>
        
        <button 
          onClick={() => abrirModal()}
          style={{
            background: 'linear-gradient(135deg, #2563eb 0%, #2563eb 100%)',
            color: 'white',
            border: 'none',
            padding: isMobile ? '10px 16px' : '12px 20px',
            borderRadius: '10px',
            fontSize: isMobile ? '13px' : '14px',
            fontWeight: '600',
            cursor: 'pointer',
            boxShadow: '0 4px 16px rgba(37, 99, 235, 0.25)',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)'
          }}
          className="button-hover"
        >
          <Plus size={16} />
          Añadir proveedor
        </button>
      </div>


      
      {loading ? (
        <div style={{
          textAlign: 'center',
          padding: '60px 20px',
          color: '#64748b'
        }}>
          <div style={{
            display: 'inline-block',
            width: '32px',
            height: '32px',
            border: '3px solid #e2e8f0',
            borderTop: '3px solid #8b5cf6',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            marginBottom: '16px'
          }}></div>
          <p style={{ fontSize: '15px', margin: 0 }}>Cargando proveedores...</p>
        </div>
      ) : activeTab === 'activos' && proveedores.length === 0 ? (
        <div style={{
          background: '#f8fafc',
          borderRadius: '12px',
          padding: isMobile ? '40px 20px' : '60px 40px',
          textAlign: 'center',
          border: '2px dashed #cbd5e1'
        }}>
          <div style={{
            width: '64px',
            height: '64px',
            background: '#e2e8f0',
            borderRadius: '32px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 20px'
          }}>
            <Wrench size={32} color="#64748b" />
          </div>
          <h3 style={{
            fontSize: '18px',
            fontWeight: '600',
            color: '#1e293b',
            marginBottom: '8px'
          }}>
            No hay proveedores registrados
          </h3>
          <p style={{
            fontSize: '14px',
            color: '#64748b',
            marginBottom: '24px'
          }}>
            Comienza agregando tu primer proveedor de servicios
          </p>
                      <button
              onClick={() => abrirModal()}
              style={{
                background: 'linear-gradient(135deg, #2563eb 0%, #2563eb 100%)',
                color: 'white',
                border: 'none',
                padding: '12px 24px',
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: '600',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                margin: '0 auto'
              }}
              className="button-hover"
            >
              <Plus size={16} />
              Añadir primero
            </button>
        </div>
      ) : activeTab === 'eliminados' && proveedoresEliminados.length === 0 ? (
        <div style={{
          background: '#f8fafc',
          borderRadius: '12px',
          padding: isMobile ? '40px 20px' : '60px 40px',
          textAlign: 'center',
          border: '2px dashed #cbd5e1'
        }}>
          <div style={{
            width: '64px',
            height: '64px',
            background: '#e2e8f0',
            borderRadius: '32px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 20px'
          }}>
            <Trash2 size={32} color="#64748b" />
          </div>
          <h3 style={{
            fontSize: '18px',
            fontWeight: '600',
            color: '#1e293b',
            marginBottom: '8px'
          }}>
            No hay proveedores eliminados
          </h3>
          <p style={{
            fontSize: '14px',
            color: '#64748b',
            margin: 0
          }}>
            Los proveedores eliminados aparecerán aquí
          </p>
        </div>
      ) : (
        <>
          {/* Tabs */}
          <div style={{
            display: 'flex',
            borderBottom: '2px solid #e5e7eb',
            marginBottom: '24px',
            gap: '0',
            position: 'relative'
          }}>
            <button
              onClick={() => setActiveTab('activos')}
              style={{
                background: 'transparent',
                border: 'none',
                padding: '12px 24px',
                fontSize: '14px',
                fontWeight: '600',
                cursor: 'pointer',
                color: activeTab === 'activos' ? '#10b981' : '#6b7280',
                borderBottom: activeTab === 'activos' ? '2px solid #10b981' : '2px solid transparent',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                flex: 1,
                textAlign: 'center',
                position: 'relative',
                zIndex: 2
              }}
              onMouseEnter={(e) => {
                if (activeTab !== 'activos') {
                  e.target.style.color = '#10b981';
                  e.target.style.borderBottom = '2px solid #10b981';
                }
              }}
              onMouseLeave={(e) => {
                if (activeTab !== 'activos') {
                  e.target.style.color = '#6b7280';
                  e.target.style.borderBottom = '2px solid transparent';
                }
              }}
            >
              Activos ({proveedores.length})
            </button>
            <button
              onClick={() => setActiveTab('eliminados')}
              style={{
                background: 'transparent',
                border: 'none',
                padding: '12px 24px',
                fontSize: '14px',
                fontWeight: '600',
                cursor: 'pointer',
                color: activeTab === 'eliminados' ? '#ef4444' : '#6b7280',
                borderBottom: activeTab === 'eliminados' ? '2px solid #ef4444' : '2px solid transparent',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                flex: 1,
                textAlign: 'center',
                position: 'relative',
                zIndex: 2
              }}
              onMouseEnter={(e) => {
                if (activeTab !== 'eliminados') {
                  e.target.style.color = '#ef4444';
                  e.target.style.borderBottom = '2px solid #ef4444';
                }
              }}
              onMouseLeave={(e) => {
                if (activeTab !== 'eliminados') {
                  e.target.style.color = '#6b7280';
                  e.target.style.borderBottom = '2px solid transparent';
                }
              }}
            >
              Eliminados ({proveedoresEliminados.length})
            </button>
          </div>

          <div style={{
            background: 'white',
            border: '1px solid #f1f5f9',
            borderRadius: '12px',
            overflow: 'hidden'
          }}>
            <div style={{overflowX: 'auto'}}>
            <table style={{
              width: '100%',
              borderCollapse: 'collapse',
              minWidth: isMobile ? '600px' : '900px'
            }}>
              <thead>
                <tr style={{
                  background: '#f8fafc',
                  borderBottom: '1px solid #e2e8f0'
                }}>
                  <th style={{
                    padding: isMobile ? '12px 16px' : '16px 20px',
                    textAlign: 'left',
                    fontWeight: '600',
                    color: '#374151',
                    fontSize: isMobile ? '12px' : '14px'
                  }}>
                    Nombre
                  </th>
                  <th style={{
                    padding: isMobile ? '12px 16px' : '16px 20px',
                    textAlign: 'left',
                    fontWeight: '600',
                    color: '#374151',
                    fontSize: isMobile ? '12px' : '14px'
                  }}>
                    Contacto
                  </th>
                  {!isMobile && (
                    <th style={{
                      padding: '16px 20px',
                      textAlign: 'left',
                      fontWeight: '600',
                      color: '#374151',
                      fontSize: '14px'
                    }}>
                      CUIT
                    </th>
                  )}
                  <th style={{
                    padding: isMobile ? '12px 16px' : '16px 20px',
                    textAlign: 'center',
                    fontWeight: '600',
                    color: '#374151',
                    fontSize: isMobile ? '12px' : '14px',
                    width: isMobile ? '120px' : '160px'
                  }}>
                    Acciones
                  </th>
                </tr>
              </thead>
              <tbody>
                {(activeTab === 'activos' ? proveedores : proveedoresEliminados).map((proveedor, index) => (
                  <tr key={proveedor.id} style={{
                    borderBottom: index < (activeTab === 'activos' ? proveedores : proveedoresEliminados).length - 1 ? '1px solid #f1f5f9' : 'none',
                    transition: 'background-color 0.2s ease'
                  }}
                  onMouseEnter={(e) => {
                    if (!isMobile) e.target.parentElement.style.backgroundColor = '#f8fafc';
                  }}
                  onMouseLeave={(e) => {
                    if (!isMobile) e.target.parentElement.style.backgroundColor = 'transparent';
                  }}
                  >
                    <td style={{
                      padding: isMobile ? '12px 16px' : '16px 20px',
                      color: '#1e293b',
                      fontSize: isMobile ? '13px' : '14px',
                      fontWeight: '600'
                    }}>
                      <div>
                        <div>{proveedor.nombre}</div>
                        {proveedor.direccion && (
                          <div style={{
                            fontSize: '11px',
                            color: '#64748b',
                            fontWeight: '400',
                            marginTop: '2px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px'
                          }}>
                            <MapPin size={10} />
                            {proveedor.direccion}
                          </div>
                        )}
                      </div>
                    </td>
                    <td style={{
                      padding: isMobile ? '12px 16px' : '16px 20px',
                      color: '#64748b',
                      fontSize: isMobile ? '11px' : '12px'
                    }}>
                      <div style={{ display: 'grid', gap: '4px' }}>
                        {proveedor.telefono && (
                          <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px'
                          }}>
                            <Phone size={10} />
                            {proveedor.telefono}
                          </div>
                        )}
                        {proveedor.email && (
                          <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px'
                          }}>
                            <Mail size={10} />
                            {proveedor.email}
                          </div>
                        )}
                        {!proveedor.telefono && !proveedor.email && (
                          <span style={{ color: '#94a3b8' }}>Sin contacto</span>
                        )}
                      </div>
                    </td>
                    {!isMobile && (
                      <td style={{
                        padding: '16px 20px',
                        color: '#64748b',
                        fontSize: '12px'
                      }}>
                        {proveedor.cuit ? (
                          <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px'
                          }}>
                            <CreditCard size={10} />
                            {proveedor.cuit}
                          </div>
                        ) : (
                          <span style={{ color: '#94a3b8' }}>-</span>
                        )}
                      </td>
                    )}
                    <td style={{
                      padding: isMobile ? '12px 16px' : '16px 20px',
                      textAlign: 'center'
                    }}>
                      <div style={{
                        display: 'flex',
                        gap: '8px',
                        justifyContent: 'center'
                      }}>
                        {activeTab === 'activos' ? (
                          <>
                        <button 
                          onClick={() => abrirModal(proveedor)}
                          style={{
                            background: '#eff6ff',
                            border: '1px solid #3b82f6',
                            borderRadius: '6px',
                            padding: isMobile ? '6px 8px' : '8px 12px',
                            color: '#3b82f6',
                            fontSize: isMobile ? '11px' : '12px',
                            fontWeight: '500',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px',
                            transition: 'all 0.2s ease'
                          }}
                          onMouseEnter={(e) => {
                            e.target.style.background = '#dbeafe';
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.background = '#eff6ff';
                          }}
                        >
                          <Edit size={12} />
                          Editar
                        </button>
                        <button 
                          onClick={() => eliminarProveedor(proveedor.id, proveedor.nombre)}
                          style={{
                            background: '#fef2f2',
                            border: '1px solid #ef4444',
                            borderRadius: '6px',
                            padding: isMobile ? '6px 8px' : '8px 12px',
                            color: '#ef4444',
                            fontSize: isMobile ? '11px' : '12px',
                            fontWeight: '500',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px',
                            transition: 'all 0.2s ease'
                          }}
                          onMouseEnter={(e) => {
                            e.target.style.background = '#fee2e2';
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.background = '#fef2f2';
                          }}
                        >
                          <Trash2 size={12} />
                          Eliminar
                        </button>
                          </>
                        ) : (
                          <button 
                            onClick={() => restaurarProveedor(proveedor.id, proveedor.nombre)}
                            style={{
                              background: '#f0fdf4',
                              border: '1px solid #22c55e',
                              borderRadius: '6px',
                              padding: isMobile ? '6px 8px' : '8px 12px',
                              color: '#22c55e',
                              fontSize: isMobile ? '11px' : '12px',
                              fontWeight: '500',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '4px',
                              transition: 'all 0.2s ease'
                            }}
                            onMouseEnter={(e) => {
                              e.target.style.background = '#dcfce7';
                            }}
                            onMouseLeave={(e) => {
                              e.target.style.background = '#f0fdf4';
                            }}
                          >
                            <RotateCcw size={12} />
                            Restaurar
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        </>
      )}
      {/* Modal para proveedores */}
      {showModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.6)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: isMobile ? '20px' : '0',
          animation: 'fadeIn 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
        }}>
          <div style={{
            background: 'white',
            borderRadius: '16px',
            padding: isMobile ? '20px' : '24px',
            width: isMobile ? '100%' : '500px',
            maxWidth: '90vw',
            maxHeight: '90vh',
            overflowY: 'auto',
            boxShadow: '0 25px 80px rgba(0,0,0,0.3)',
            animation: 'scaleIn 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              marginBottom: '20px'
            }}>
              <div style={{
                width: '32px',
                height: '32px',
                background: '#8b5cf615',
                borderRadius: '8px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <Wrench size={18} color="#8b5cf6" />
              </div>
              <h3 style={{
                margin: 0,
                fontSize: isMobile ? '18px' : '20px',
                fontWeight: '700',
                color: '#1e293b'
              }}>
                {editandoProveedor ? 'Editar' : 'Nuevo'} Proveedor
              </h3>
            </div>
            
            <div style={{
              display: 'grid',
              gap: '16px'
            }}>
              <div>
                <label style={{
                  display: 'block',
                  marginBottom: '8px',
                  fontSize: '14px',
                  fontWeight: '600',
                  color: '#374151'
                }}>
                  Nombre del Proveedor *
                </label>
                <input
                  type="text"
                  placeholder="Ej: Constructora ABC"
                  value={formData.nombre}
                  onChange={(e) => setFormData({...formData, nombre: e.target.value})}
                  style={{
                    width: '100%',
                    padding: '14px 16px',
                    border: '2px solid #f1f5f9',
                    borderRadius: '10px',
                    fontSize: '16px',
                    boxSizing: 'border-box',
                    outline: 'none',
                    transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                    touchAction: 'manipulation'
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = '#8b5cf6';
                    e.target.style.boxShadow = '0 0 0 3px rgba(139, 92, 246, 0.1)';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = '#f1f5f9';
                    e.target.style.boxShadow = 'none';
                  }}
                />
              </div>
              
              <div style={{
                display: 'grid',
                gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
                gap: '16px'
              }}>
                <div>
                  <label style={{
                    display: 'block',
                    marginBottom: '8px',
                    fontSize: '14px',
                    fontWeight: '600',
                    color: '#374151'
                  }}>
                    Teléfono
                  </label>
                  <input
                    type="text"
                    placeholder="11-1234-5678"
                    value={formData.telefono}
                    onChange={(e) => setFormData({...formData, telefono: e.target.value})}
                    style={{
                      width: '100%',
                      padding: '14px 16px',
                      border: '2px solid #f1f5f9',
                      borderRadius: '10px',
                      fontSize: '16px',
                      boxSizing: 'border-box',
                      outline: 'none',
                      transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                      touchAction: 'manipulation'
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = '#8b5cf6';
                      e.target.style.boxShadow = '0 0 0 3px rgba(139, 92, 246, 0.1)';
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = '#f1f5f9';
                      e.target.style.boxShadow = 'none';
                    }}
                  />
                </div>
                
                <div>
                  <label style={{
                    display: 'block',
                    marginBottom: '8px',
                    fontSize: '14px',
                    fontWeight: '600',
                    color: '#374151'
                  }}>
                    Email
                  </label>
                  <input
                    type="email"
                    placeholder="contacto@proveedor.com"
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    style={{
                      width: '100%',
                      padding: '14px 16px',
                      border: '2px solid #f1f5f9',
                      borderRadius: '10px',
                      fontSize: '16px',
                      boxSizing: 'border-box',
                      outline: 'none',
                      transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                      touchAction: 'manipulation'
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = '#8b5cf6';
                      e.target.style.boxShadow = '0 0 0 3px rgba(139, 92, 246, 0.1)';
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = '#f1f5f9';
                      e.target.style.boxShadow = 'none';
                    }}
                  />
                </div>
              </div>
              
              <div>
                <label style={{
                  display: 'block',
                  marginBottom: '8px',
                  fontSize: '14px',
                  fontWeight: '600',
                  color: '#374151'
                }}>
                  CUIT
                </label>
                <input
                  type="text"
                  placeholder="20-12345678-9"
                  value={formData.cuit}
                  onChange={(e) => handleCuitChange(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '14px 16px',
                    border: '2px solid #f1f5f9',
                    borderRadius: '10px',
                    fontSize: '16px',
                    boxSizing: 'border-box',
                    outline: 'none',
                    transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                    touchAction: 'manipulation'
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = '#8b5cf6';
                    e.target.style.boxShadow = '0 0 0 3px rgba(139, 92, 246, 0.1)';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = '#f1f5f9';
                    e.target.style.boxShadow = 'none';
                  }}
                />
              </div>
              
              <div>
                <label style={{
                  display: 'block',
                  marginBottom: '8px',
                  fontSize: '14px',
                  fontWeight: '600',
                  color: '#374151'
                }}>
                  Dirección
                </label>
                <textarea
                  placeholder="Dirección completa..."
                  value={formData.direccion}
                  onChange={(e) => setFormData({...formData, direccion: e.target.value})}
                  rows="3"
                  style={{
                    width: '100%',
                    padding: '14px 16px',
                    border: '2px solid #f1f5f9',
                    borderRadius: '10px',
                    fontSize: '16px',
                    boxSizing: 'border-box',
                    outline: 'none',
                    resize: 'vertical',
                    transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                    touchAction: 'manipulation'
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = '#8b5cf6';
                    e.target.style.boxShadow = '0 0 0 3px rgba(139, 92, 246, 0.1)';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = '#f1f5f9';
                    e.target.style.boxShadow = 'none';
                  }}
                />
              </div>
            </div>
            
            <div style={{
              display: 'flex',
              gap: '12px',
              justifyContent: 'flex-end',
              marginTop: '24px'
            }}>
              <button
                onClick={() => {
                  setShowModal(false);
                  setEditandoProveedor(null);
                  setFormData({
                    nombre: '',
                    telefono: '',
                    email: '',
                    direccion: '',
                    cuit: ''
                  });
                }}
                style={{
                  padding: '10px 20px',
                  border: '1px solid #d1d5db',
                  borderRadius: '8px',
                  background: 'white',
                  color: '#64748b',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)'
                }}
                className="button-hover"
              >
                Cancelar
              </button>
              <button
                onClick={guardarProveedor}
                disabled={guardando}
                style={{
                  padding: '10px 20px',
                  border: 'none',
                  borderRadius: '8px',
                  background: guardando ? '#94a3b8' : 'linear-gradient(135deg, #2563eb 0%, #2563eb 100%)',
                  color: 'white',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: guardando ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)'
                }}
                className={!guardando ? "button-hover" : ""}
              >
                {guardando ? (
                  <>
                    <div className="loading-pulse" style={{
                      width: '16px',
                      height: '16px',
                      border: '2px solid rgba(255,255,255,0.3)',
                      borderTop: '2px solid white',
                      borderRadius: '50%',
                      animation: 'spin 1s linear infinite'
                    }}></div>
                    Guardando...
                  </>
                ) : (
                  <>
                    <Save size={16} />
                    {editandoProveedor ? 'Actualizar' : 'Guardar'}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
              )}
      {/* Modal de 3 Opciones */}
      {showModal3Opciones && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '20px'
        }}>
          <div style={{
            background: 'white',
            borderRadius: '12px',
            padding: '24px',
            width: '100%',
            maxWidth: '500px',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
            border: '1px solid #e5e7eb'
          }}>
            <div style={{ textAlign: 'center', marginBottom: '24px' }}>
              <div style={{
                background: '#fef3c7',
                color: '#d97706',
                width: '48px',
                height: '48px',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 16px'
              }}>
                <AlertCircle size={24} />
              </div>
              <h3 style={{ margin: '0 0 8px 0', fontSize: '18px', fontWeight: '600', color: '#1f2937' }}>
                Cambio de nombre detectado
              </h3>
              <p style={{ margin: 0, color: '#6b7280', fontSize: '14px', lineHeight: '1.5' }}>
                Este proveedor tiene {opcionesEdicion?.impacto?.salidas?.total_salidas || 0} salida(s) asociada(s)
                {(opcionesEdicion?.impacto?.presupuestos?.total_presupuestos || 0) > 0 && (
                  <> y {opcionesEdicion?.impacto?.presupuestos?.total_presupuestos} presupuesto(s)</>
                )}
                {(opcionesEdicion?.impacto?.presupuestos_iniciales?.total_presupuestos || 0) > 0 && (
                  <> y {opcionesEdicion?.impacto?.presupuestos_iniciales?.total_presupuestos} presupuesto(s) inicial(es)</>
                )}. ¿Cómo quieres aplicar el cambio?
              </p>
              
              {(opcionesEdicion?.impacto?.presupuestos?.total_presupuestos || 0) > 0 && (
                <div style={{
                  background: '#fef3c7',
                  border: '1px solid #f59e0b',
                  borderRadius: '8px',
                  padding: '12px',
                  marginTop: '16px',
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '8px'
                }}>
                  <AlertTriangle size={16} color="#d97706" style={{ flexShrink: 0, marginTop: '2px' }} />
                  <div style={{ fontSize: '13px', color: '#92400e', lineHeight: '1.4' }}>
                    <strong>Advertencia:</strong> Si cambias el nombre del proveedor, los presupuestos ya no podrán identificar correctamente los pagos asociados a este proveedor. Esto puede afectar el seguimiento de gastos en los presupuestos.
                  </div>
                </div>
              )}
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginBottom: '24px' }}>
              <button
                onClick={() => ejecutarOpcionEdicion('incluir_pasados')}
                disabled={guardando}
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  border: '1px solid #3b82f6',
                  borderRadius: '8px',
                  background: '#eff6ff',
                  color: '#3b82f6',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: guardando ? 'not-allowed' : 'pointer',
                  transition: 'all 0.2s',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px'
                }}
                onMouseEnter={(e) => !guardando && (e.target.style.background = '#dbeafe')}
                onMouseLeave={(e) => !guardando && (e.target.style.background = '#eff6ff')}
              >
                <CheckCircle2 size={16} />
                Incluir Pasados
              </button>
              
              <button
                onClick={() => ejecutarOpcionEdicion('solo_futuros')}
                disabled={guardando}
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  border: '1px solid #f59e0b',
                  borderRadius: '8px',
                  background: '#fffbeb',
                  color: '#f59e0b',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: guardando ? 'not-allowed' : 'pointer',
                  transition: 'all 0.2s',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px'
                }}
                onMouseEnter={(e) => !guardando && (e.target.style.background = '#fef3c7')}
                onMouseLeave={(e) => !guardando && (e.target.style.background = '#fffbeb')}
              >
                <AlertCircle size={16} />
                Solo Futuros
              </button>
              
              <button
                onClick={() => ejecutarOpcionEdicion('crear_nuevo')}
                disabled={guardando}
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  border: '1px solid #10b981',
                  borderRadius: '8px',
                  background: '#ecfdf5',
                  color: '#10b981',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: guardando ? 'not-allowed' : 'pointer',
                  transition: 'all 0.2s',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px'
                }}
                onMouseEnter={(e) => !guardando && (e.target.style.background = '#d1fae5')}
                onMouseLeave={(e) => !guardando && (e.target.style.background = '#ecfdf5')}
              >
                <Plus size={16} />
                Crear Nuevo
              </button>
            </div>

            <div style={{ display: 'flex', justifyContent: 'center' }}>
              <button
                onClick={() => setShowModal3Opciones(false)}
                disabled={guardando}
                style={{
                  padding: '8px 16px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  background: 'white',
                  color: '#6b7280',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: guardando ? 'not-allowed' : 'pointer',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => !guardando && (e.target.style.background = '#f9fafb')}
                onMouseLeave={(e) => !guardando && (e.target.style.background = 'white')}
              >
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}

        {/* Modal de Confirmación de Eliminación */}
      {showDeleteModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '20px'
        }}>
          <div style={{
            background: 'white',
            borderRadius: '12px',
            padding: '24px',
            width: '100%',
            maxWidth: '500px'
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '24px'
            }}>
              <h3 style={{
                fontSize: '18px',
                fontWeight: '600',
                color: '#1e293b',
                margin: 0
              }}>
                Confirmar Eliminación
              </h3>
              <button
                onClick={() => setShowDeleteModal(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '20px',
                  cursor: 'pointer',
                  color: '#64748b'
                }}
              >
                ×
              </button>
            </div>

            <div style={{ marginBottom: '20px' }}>
              <p style={{
                fontSize: '14px',
                color: '#64748b',
                marginBottom: '16px'
              }}>
                ¿Estás seguro de eliminar el proveedor "{proveedorSeleccionado?.nombre}"?
              </p>
              
              <div style={{
                background: '#fef2f2',
                borderRadius: '8px',
                padding: '16px',
                marginBottom: '16px',
                border: '1px solid #fecaca'
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  marginBottom: '12px'
                }}>
                  <AlertTriangle size={16} color="#ef4444" />
                  <span style={{ fontSize: '14px', fontWeight: '600', color: '#ef4444' }}>
                    Registros asociados:
                  </span>
                </div>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  marginBottom: '8px'
                }}>
                  <span style={{ fontSize: '14px', color: '#64748b' }}>Salidas:</span>
                  <span style={{ fontSize: '14px', fontWeight: '600', color: '#1e293b' }}>
                    {analisisImpacto?.salidas.total_salidas || 0}
                  </span>
                </div>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between'
                }}>
                  <span style={{ fontSize: '14px', color: '#64748b' }}>Entradas:</span>
                  <span style={{ fontSize: '14px', fontWeight: '600', color: '#1e293b' }}>
                    {analisisImpacto?.entradas.total_entradas || 0}
                  </span>
                </div>
              </div>

              <p style={{
                fontSize: '14px',
                color: '#64748b'
              }}>
                Los registros mantendrán la referencia al proveedor, pero no se podrán crear nuevos registros con este proveedor.
              </p>
            </div>

            <div style={{
              display: 'flex',
              gap: '12px',
              justifyContent: 'flex-end'
            }}>
              <button
                onClick={() => setShowDeleteModal(false)}
                style={{
                  background: '#f1f5f9',
                  border: 'none',
                  padding: '10px 20px',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#64748b',
                  cursor: 'pointer'
                }}
              >
                Cancelar
              </button>
              <button
                onClick={ejecutarEliminacion}
                disabled={guardando}
                style={{
                  background: guardando ? '#cbd5e1' : '#ef4444',
                  border: 'none',
                  padding: '10px 20px',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: 'white',
                  cursor: guardando ? 'not-allowed' : 'pointer'
                }}
              >
                {guardando ? 'Eliminando...' : 'Eliminar'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
// Trabajadores - Con edición y CUIT/CUIL formateado mejorado
function TrabajadoresTab({ constructora, showCustomAlert }) {
  const [trabajadores, setTrabajadores] = useState([]);
  const [trabajadoresEliminados, setTrabajadoresEliminados] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editandoTrabajador, setEditandoTrabajador] = useState(null);
  const [formData, setFormData] = useState({
    nombre: '',
    apellido: '',
    email: '',
    puesto: '',
    tipo: 'empleado',
    telefono: '',
    cuit: ''
  });
  const [guardando, setGuardando] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [activeTab, setActiveTab] = useState('activos');

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Función para formatear CUIT/CUIL
  const formatearCuit = (valor) => {
    const numeros = valor.replace(/\D/g, '');
    const limitado = numeros.substring(0, 11);
    
    if (limitado.length <= 2) {
      return limitado;
    } else if (limitado.length <= 10) {
      return `${limitado.substring(0, 2)}-${limitado.substring(2)}`;
    } else {
      return `${limitado.substring(0, 2)}-${limitado.substring(2, 10)}-${limitado.substring(10)}`;
    }
  };

  const handleCuitChange = (valor) => {
    const formateado = formatearCuit(valor);
    setFormData({...formData, cuit: formateado});
  };

  const cargarTrabajadores = async () => {
    try {
      // Activos
      const { data: activos, error: errorActivos } = await supabase
        .from('trabajadores')
        .select('*')
        .eq('constructora_id', constructora.id)
        .eq('activo', true)
        .order('apellido');

      if (errorActivos) {
        console.error('Error cargando trabajadores activos:', errorActivos);
      } else {
        setTrabajadores(activos || []);
      }

      // Eliminados (inactivos)
      const { data: eliminados, error: errorEliminados } = await supabase
        .from('trabajadores')
        .select('*')
        .eq('constructora_id', constructora.id)
        .eq('activo', false)
        .order('apellido');

      if (errorEliminados) {
        console.error('Error cargando trabajadores eliminados:', errorEliminados);
      } else {
        setTrabajadoresEliminados(eliminados || []);
      }
    } catch (err) {
      console.error('Error:', err);
    }
    setLoading(false);
  };

  useEffect(() => {
    if (constructora?.id) {
      cargarTrabajadores();
    }
  }, [constructora]);

  const abrirModal = (trabajador = null) => {
    if (trabajador) {
      setEditandoTrabajador(trabajador);
      setFormData({
        nombre: trabajador.nombre,
        apellido: trabajador.apellido,
        email: trabajador.email || '',
        puesto: trabajador.puesto || '',
        tipo: trabajador.tipo || 'empleado',
        telefono: trabajador.telefono || '',
        cuit: trabajador.cuit || ''
      });
    } else {
      setEditandoTrabajador(null);
      setFormData({
        nombre: '',
        apellido: '',
        email: '',
        puesto: '',
        tipo: 'empleado',
        telefono: '',
        cuit: ''
      });
    }
    setShowModal(true);
  };

  const guardarTrabajador = async () => {
    if (!formData.nombre.trim() || !formData.apellido.trim() || !formData.email.trim()) {
      showCustomAlert('error', 'Error', 'Por favor completa nombre, apellido y email');
      return;
    }

    // Validar formato de email
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      showCustomAlert('error', 'Error', 'Por favor ingresa un email válido');
      return;
    }

    // Normalizador de CUIT para comparar correctamente
    const normalize = (v) => (v || '').toString().replace(/[^0-9]/g, '');

    // Si estamos editando y cambió el CUIT, primero analizamos impacto y pedimos confirmación
    if (editandoTrabajador && normalize(editandoTrabajador.cuit) !== normalize(formData.cuit)) {
      console.log('🔍 Detectado cambio de CUIT:', {
        cuitViejo: editandoTrabajador.cuit,
        cuitNuevo: formData.cuit,
        trabajadorId: editandoTrabajador.id
      });
      
      try {
        setGuardando(true);
        const requestBody = {
          trabajador_id: editandoTrabajador.id,
          constructora_id: constructora.id,
          accion: 'editar',
          cambios: { cuit: formData.cuit || '' }
        };
        console.log('📤 Enviando request:', requestBody);
        
        const resp = await fetch('http://localhost:4001/api/trabajadores/analizar-impacto', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(requestBody)
        });
        const data = await resp.json();
        console.log('📥 Respuesta del backend:', data);
        setGuardando(false);

        if (data && data.success) {
          const totCobros = data.impacto?.cobros?.total_cobros || 0;
          const totEntradas = data.impacto?.entradas_directas?.total_entradas || 0;
          const totDocs = data.impacto?.documentos_historicos?.total_entradas_con_cuit_viejo || 0;

          const mensaje = `Vas a cambiar el CUIT del trabajador.\n\n` +
            `- Cobros vinculados: ${totCobros}\n` +
            `- Entradas con pago directo: ${totEntradas}\n` +
            (totDocs > 0 ? `- Documentos históricos con CUIT anterior: ${totDocs}\n\n` : '\n') +
            `Este cambio NO actualizará documentos históricos que guardaron el CUIT anterior. ` +
            `En la vista de Facturas/Comprobantes, esos CUIT podrían no resolverse como nombre.`;

          console.log('⚠️ Mostrando modal de advertencia con mensaje:', mensaje);
          
          // Confirmación con el mismo patrón visual del sistema
          return showCustomAlert(
            'confirm',
            'Cambio de CUIT detectado',
            mensaje,
            async () => {
              // Si confirma, procedemos con el guardado normal
              await realizarGuardadoTrabajador();
            }
          );
        }
      } catch (e) {
        console.error('❌ Error analizando impacto de trabajador:', e);
        setGuardando(false);
      }
      // Si hubo error en análisis, no bloquear el guardado, pero continuar normalmente
    }

    await realizarGuardadoTrabajador();
  };

  const realizarGuardadoTrabajador = async () => {
    setGuardando(true);
    try {
      let result;
      
      if (editandoTrabajador) {
        result = await supabase
          .from('trabajadores')
          .update(formData)
          .eq('id', editandoTrabajador.id)
          .select();
      } else {
        result = await supabase
          .from('trabajadores')
          .insert([{
            constructora_id: constructora.id,
            ...formData,
            activo: true
          }])
          .select();
      }

      const { data, error } = result;
      
      if (error) {
        console.error('Error guardando trabajador:', error);
        
        if (error.code === '23505' && error.details?.includes('trabajadores_email_constructora_unique')) {
          showCustomAlert('error', 'Error', 'Ya existe un trabajador con este email en la constructora');
        } else {
          showCustomAlert('error', 'Error', 'Error al guardar: ' + error.message);
        }
      } else {
        setFormData({
          nombre: '',
          apellido: '',
          email: '',
          puesto: '',
          tipo: 'empleado',
          telefono: '',
          cuit: ''
        });
        setEditandoTrabajador(null);
        setShowModal(false);
        cargarTrabajadores();
        
        const mensaje = editandoTrabajador 
          ? 'Trabajador actualizado correctamente' 
          : 'Trabajador creado correctamente. Ahora puede usar "Primera vez Empleado" en el login.';
        
        showCustomAlert('success', 'Éxito', mensaje);
      }
    } catch (err) {
      console.error('Error:', err);
      showCustomAlert('error', 'Error', 'Error: ' + err.message);
    }
    setGuardando(false);
  };

  const eliminarTrabajador = async (id, nombre, apellido) => {
    try {
      // Analizar impacto antes de confirmar
      const resp = await fetch('http://localhost:4001/api/trabajadores/analizar-impacto', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ trabajador_id: id, constructora_id: constructora.id, accion: 'eliminar' })
      });
      const data = await resp.json();

      const totCobros = data?.impacto?.cobros?.total_cobros || 0;
      const totEntradas = data?.impacto?.entradas_directas?.total_entradas || 0;

      const mensaje = `¿Estás seguro de eliminar a "${nombre} ${apellido}"?\n\n` +
        `El trabajador quedará inactivo y no podrá seleccionarse en nuevos registros.\n` +
        `Los registros históricos no cambian y mantienen su referencia.\n\n` +
        `Registros asociados:\n` +
        `- Cobros: ${totCobros}\n` +
        `- Entradas con pago directo: ${totEntradas}`;

      showCustomAlert('confirm', '¿Eliminar trabajador?', mensaje, async () => {
        try {
          const { error } = await supabase
            .from('trabajadores')
            .update({ activo: false })
            .eq('id', id);
          
          if (error) {
            console.error('Error eliminando trabajador:', error);
            showCustomAlert('error', 'Error', 'Error al eliminar: ' + error.message);
          } else {
            cargarTrabajadores();
            showCustomAlert('success', 'Éxito', 'Trabajador eliminado correctamente');
          }
        } catch (err) {
          console.error('Error:', err);
          showCustomAlert('error', 'Error', 'Error: ' + err.message);
        }
      });
    } catch (err) {
      console.error('Error analizando impacto:', err);
      // Fallback a confirmación simple
      showCustomAlert('confirm', '¿Eliminar trabajador?', `¿Estás seguro de eliminar a "${nombre} ${apellido}"?`, async () => {
        const { error } = await supabase
          .from('trabajadores')
          .update({ activo: false })
          .eq('id', id);
        if (!error) cargarTrabajadores();
      });
    }
  };

  const restaurarTrabajador = async (id) => {
    showCustomAlert('confirm', '¿Restaurar trabajador?',
      'El trabajador volverá a estar activo y podrá seleccionarse en nuevos registros.',
      async () => {
        try {
          const { error } = await supabase
            .from('trabajadores')
            .update({ activo: true })
            .eq('id', id);
          if (error) {
            console.error('Error restaurando trabajador:', error);
            showCustomAlert('error', 'Error', 'No se pudo restaurar: ' + error.message);
          } else {
            await cargarTrabajadores();
            showCustomAlert('success', 'Éxito', 'Trabajador restaurado correctamente');
          }
        } catch (err) {
          console.error('Error:', err);
          showCustomAlert('error', 'Error', 'Error: ' + err.message);
        }
      }
    );
  };
  return (
    <div style={{padding: isMobile ? '20px 16px' : '24px 32px'}}>
      {/* Header mejorado */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: '24px',
        flexWrap: 'wrap',
        gap: '16px'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1 }}>
          <div style={{
            width: '32px',
            height: '32px',
            background: '#f59e0b15',
            borderRadius: '8px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <UserCheck size={18} color="#f59e0b" />
          </div>
          <div>
            <h2 style={{
              fontSize: isMobile ? '18px' : '20px',
              fontWeight: '600',
              color: '#1e293b',
              margin: '0 0 4px 0'
            }}>
              Trabajadores
            </h2>
            <p style={{
              fontSize: '13px',
              color: '#64748b',
              margin: 0
            }}>
              Los trabajadores con email pueden usar "Primera vez Empleado" en el login
            </p>
          </div>
        </div>
        
        <button 
          onClick={() => abrirModal()}
          style={{
            background: 'linear-gradient(135deg, #2563eb 0%, #2563eb 100%)',
            color: 'white',
            border: 'none',
            padding: isMobile ? '10px 16px' : '12px 20px',
            borderRadius: '10px',
            fontSize: isMobile ? '13px' : '14px',
            fontWeight: '600',
            cursor: 'pointer',
            boxShadow: '0 4px 16px rgba(37, 99, 235, 0.25)',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)'
          }}
          className="button-hover"
        >
          <Plus size={16} />
          Añadir trabajador
        </button>
      </div>
      
      {loading ? (
        <div style={{
          textAlign: 'center',
          padding: '60px 20px',
          color: '#64748b'
        }}>
          <div style={{
            display: 'inline-block',
            width: '32px',
            height: '32px',
            border: '3px solid #e2e8f0',
            borderTop: '3px solid #f59e0b',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            marginBottom: '16px'
          }}></div>
          <p style={{ fontSize: '15px', margin: 0 }}>Cargando trabajadores...</p>
        </div>
      ) : trabajadores.length === 0 ? (
        <div style={{
          background: '#fefbf3',
          borderRadius: '12px',
          padding: isMobile ? '40px 20px' : '60px 40px',
          textAlign: 'center',
          border: '2px dashed #fed7aa'
        }}>
          <div style={{
            width: '64px',
            height: '64px',
            background: '#fed7aa',
            borderRadius: '32px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 20px'
          }}>
            <UserCheck size={32} color="#f59e0b" />
          </div>
          <h3 style={{
            fontSize: '18px',
            fontWeight: '600',
            color: '#92400e',
            marginBottom: '8px'
          }}>
            No hay trabajadores registrados
          </h3>
          <p style={{
            fontSize: '14px',
            color: '#d97706',
            marginBottom: '24px'
          }}>
            Comienza agregando tu primer trabajador al equipo
          </p>
                      <button
              onClick={() => abrirModal()}
              style={{
                background: 'linear-gradient(135deg, #2563eb 0%, #2563eb 100%)',
                color: 'white',
                border: 'none',
                padding: '12px 24px',
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: '600',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                margin: '0 auto'
              }}
              className="button-hover"
            >
              <Plus size={16} />
              Añadir primero
            </button>
        </div>
      ) : (
        <>
          {/* Tabs */}
          <div style={{
            display: 'flex',
            borderBottom: '2px solid #e5e7eb',
            marginBottom: '24px',
            gap: '0',
            position: 'relative'
          }}>
            <button
              onClick={() => setActiveTab('activos')}
              style={{
                background: 'transparent',
                border: 'none',
                padding: '12px 24px',
                fontSize: '14px',
                fontWeight: '600',
                cursor: 'pointer',
                color: activeTab === 'activos' ? '#10b981' : '#6b7280',
                borderBottom: activeTab === 'activos' ? '2px solid #10b981' : '2px solid transparent',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                flex: 1,
                textAlign: 'center',
                position: 'relative',
                zIndex: 2
              }}
              onMouseEnter={(e) => {
                if (activeTab !== 'activos') {
                  e.target.style.color = '#10b981';
                  e.target.style.borderBottom = '2px solid #10b981';
                }
              }}
              onMouseLeave={(e) => {
                if (activeTab !== 'activos') {
                  e.target.style.color = '#6b7280';
                  e.target.style.borderBottom = '2px solid transparent';
                }
              }}
            >
              Activos ({trabajadores.length})
            </button>
            <button
              onClick={() => setActiveTab('eliminados')}
              style={{
                background: 'transparent',
                border: 'none',
                padding: '12px 24px',
                fontSize: '14px',
                fontWeight: '600',
                cursor: 'pointer',
                color: activeTab === 'eliminados' ? '#ef4444' : '#6b7280',
                borderBottom: activeTab === 'eliminados' ? '2px solid #ef4444' : '2px solid transparent',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                flex: 1,
                textAlign: 'center',
                position: 'relative',
                zIndex: 2
              }}
              onMouseEnter={(e) => {
                if (activeTab !== 'eliminados') {
                  e.target.style.color = '#ef4444';
                  e.target.style.borderBottom = '2px solid #ef4444';
                }
              }}
              onMouseLeave={(e) => {
                if (activeTab !== 'eliminados') {
                  e.target.style.color = '#6b7280';
                  e.target.style.borderBottom = '2px solid transparent';
                }
              }}
            >
              Eliminados ({trabajadoresEliminados.length})
            </button>
          </div>

          <div style={{
            background: 'white',
            border: '1px solid #f1f5f9',
            borderRadius: '12px',
            overflow: 'hidden'
          }}>
            <div style={{overflowX: 'auto'}}>
            <table style={{
              width: '100%',
              borderCollapse: 'collapse',
              minWidth: isMobile ? '600px' : '900px'
            }}>
              <thead>
                <tr style={{
                  background: '#f8fafc',
                  borderBottom: '1px solid #e2e8f0'
                }}>
                  <th style={{
                    padding: isMobile ? '12px 16px' : '16px 20px',
                    textAlign: 'left',
                    fontWeight: '600',
                    color: '#374151',
                    fontSize: isMobile ? '12px' : '14px'
                  }}>
                    Trabajador
                  </th>
                  <th style={{
                    padding: isMobile ? '12px 16px' : '16px 20px',
                    textAlign: 'left',
                    fontWeight: '600',
                    color: '#374151',
                    fontSize: isMobile ? '12px' : '14px'
                  }}>
                    Email / Acceso
                  </th>
                  <th style={{
                    padding: isMobile ? '12px 16px' : '16px 20px',
                    textAlign: 'left',
                    fontWeight: '600',
                    color: '#374151',
                    fontSize: isMobile ? '12px' : '14px'
                  }}>
                    Puesto
                  </th>
                  <th style={{
                    padding: isMobile ? '12px 16px' : '16px 20px',
                    textAlign: 'left',
                    fontWeight: '600',
                    color: '#374151',
                    fontSize: isMobile ? '12px' : '14px'
                  }}>
                    Tipo
                  </th>
                  {!isMobile && (
                    <th style={{
                      padding: '16px 20px',
                      textAlign: 'left',
                      fontWeight: '600',
                      color: '#374151',
                      fontSize: '14px'
                    }}>
                      Contacto
                    </th>
                  )}
                  <th style={{
                    padding: isMobile ? '12px 16px' : '16px 20px',
                    textAlign: 'center',
                    fontWeight: '600',
                    color: '#374151',
                    fontSize: isMobile ? '12px' : '14px',
                    width: isMobile ? '120px' : '160px'
                  }}>
                    Acciones
                  </th>
                </tr>
              </thead>
              <tbody>
                {(activeTab === 'activos' ? trabajadores : trabajadoresEliminados).map((trabajador, index) => (
                  <tr key={trabajador.id} style={{
                    borderBottom: index < (activeTab === 'activos' ? trabajadores.length : trabajadoresEliminados.length) - 1 ? '1px solid #f1f5f9' : 'none',
                    transition: 'background-color 0.2s ease'
                  }}
                  onMouseEnter={(e) => {
                    if (!isMobile) e.target.parentElement.style.backgroundColor = '#f8fafc';
                  }}
                  onMouseLeave={(e) => {
                    if (!isMobile) e.target.parentElement.style.backgroundColor = 'transparent';
                  }}
                  >
                    <td style={{
                      padding: isMobile ? '12px 16px' : '16px 20px',
                      color: '#1e293b',
                      fontSize: isMobile ? '13px' : '14px',
                      fontWeight: '600'
                    }}>
                      <div>
                        <div>{trabajador.nombre} {trabajador.apellido}</div>
                        {trabajador.cuit && (
                          <div style={{
                            fontSize: '11px',
                            color: '#64748b',
                            fontWeight: '400',
                            marginTop: '2px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px'
                          }}>
                            <CreditCard size={10} />
                            {trabajador.cuit}
                          </div>
                        )}
                      </div>
                    </td>
                    <td style={{
                      padding: isMobile ? '12px 16px' : '16px 20px',
                      fontSize: isMobile ? '11px' : '12px'
                    }}>
                      {trabajador.email ? (
                        <div>
                          <div style={{
                            color: '#059669',
                            fontWeight: '500',
                            marginBottom: '4px'
                          }}>
                            {trabajador.email}
                          </div>
                          <div style={{
                            fontSize: '10px',
                            color: '#059669',
                            fontWeight: '500',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px'
                          }}>
                            <CheckCircle2 size={10} />
                            Puede hacer login
                          </div>
                        </div>
                      ) : (
                        <div>
                          <div style={{
                            color: '#ef4444',
                            fontWeight: '500',
                            marginBottom: '4px'
                          }}>
                            Sin email
                          </div>
                          <div style={{
                            fontSize: '10px',
                            color: '#ef4444',
                            fontWeight: '500',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px'
                          }}>
                            <XCircle size={10} />
                            No puede hacer login
                          </div>
                        </div>
                      )}
                    </td>
                    <td style={{
                      padding: isMobile ? '12px 16px' : '16px 20px',
                      color: '#64748b',
                      fontSize: isMobile ? '12px' : '13px'
                    }}>
                      {trabajador.puesto || (
                        <span style={{ color: '#94a3b8', fontStyle: 'italic' }}>Sin puesto</span>
                      )}
                    </td>
                    <td style={{
                      padding: isMobile ? '12px 16px' : '16px 20px',
                      fontSize: isMobile ? '12px' : '13px'
                    }}>
                      <span style={{
                        background: trabajador.tipo === 'socio' ? '#dcfce7' : '#f1f5f9',
                        color: trabajador.tipo === 'socio' ? '#166534' : '#475569',
                        padding: '4px 8px',
                        borderRadius: '6px',
                        fontSize: isMobile ? '11px' : '12px',
                        fontWeight: '500',
                        textTransform: 'capitalize'
                      }}>
                        {trabajador.tipo}
                      </span>
                    </td>
                    {!isMobile && (
                      <td style={{
                        padding: '16px 20px',
                        color: '#64748b',
                        fontSize: '12px'
                      }}>
                        {trabajador.telefono ? (
                          <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px'
                          }}>
                            <Phone size={10} />
                            {trabajador.telefono}
                          </div>
                        ) : (
                          <span style={{ color: '#94a3b8', fontStyle: 'italic' }}>Sin teléfono</span>
                        )}
                      </td>
                    )}
                    <td style={{
                      padding: isMobile ? '12px 16px' : '16px 20px',
                      textAlign: 'center'
                    }}>
                      <div style={{
                        display: 'flex',
                        gap: '8px',
                        justifyContent: 'center'
                      }}>
                        {activeTab === 'activos' ? (
                          <>
                            <button 
                              onClick={() => abrirModal(trabajador)}
                              style={{
                                background: '#eff6ff',
                                border: '1px solid #3b82f6',
                                borderRadius: '6px',
                                padding: isMobile ? '6px 8px' : '8px 12px',
                                color: '#3b82f6',
                                fontSize: isMobile ? '11px' : '12px',
                                fontWeight: '500',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '4px',
                                transition: 'all 0.2s ease'
                              }}
                              onMouseEnter={(e) => {
                                e.target.style.background = '#dbeafe';
                              }}
                              onMouseLeave={(e) => {
                                e.target.style.background = '#eff6ff';
                              }}
                            >
                              <Edit size={12} />
                              Editar
                            </button>
                            <button 
                              onClick={() => eliminarTrabajador(trabajador.id, trabajador.nombre, trabajador.apellido)}
                              style={{
                                background: '#fef2f2',
                                border: '1px solid #ef4444',
                                borderRadius: '6px',
                                padding: isMobile ? '6px 8px' : '8px 12px',
                                color: '#ef4444',
                                fontSize: isMobile ? '11px' : '12px',
                                fontWeight: '500',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '4px',
                                transition: 'all 0.2s ease'
                              }}
                              onMouseEnter={(e) => {
                                e.target.style.background = '#fee2e2';
                              }}
                              onMouseLeave={(e) => {
                                e.target.style.background = '#fef2f2';
                              }}
                            >
                              <Trash2 size={12} />
                              Eliminar
                            </button>
                          </>
                        ) : (
                          <button 
                            onClick={() => restaurarTrabajador(trabajador.id)}
                            style={{
                              background: '#ecfdf5',
                              border: '1px solid #10b981',
                              borderRadius: '6px',
                              padding: isMobile ? '6px 8px' : '8px 12px',
                              color: '#10b981',
                              fontSize: isMobile ? '11px' : '12px',
                              fontWeight: '500',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '4px',
                              transition: 'all 0.2s ease'
                            }}
                            onMouseEnter={(e) => {
                              e.target.style.background = '#d1fae5';
                            }}
                            onMouseLeave={(e) => {
                              e.target.style.background = '#ecfdf5';
                            }}
                          >
                            <CheckCircle2 size={12} />
                            Restaurar
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        </>
      )}
      {/* Modal para trabajadores */}
      {showModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.6)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: isMobile ? '20px' : '0',
          animation: 'fadeIn 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
        }}>
          <div style={{
            background: 'white',
            borderRadius: '16px',
            padding: isMobile ? '20px' : '24px',
            width: isMobile ? '100%' : '500px',
            maxWidth: '90vw',
            maxHeight: '90vh',
            overflowY: 'auto',
            boxShadow: '0 25px 80px rgba(0,0,0,0.3)',
            animation: 'scaleIn 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              marginBottom: '20px'
            }}>
              <div style={{
                width: '32px',
                height: '32px',
                background: '#f59e0b15',
                borderRadius: '8px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <UserCheck size={18} color="#f59e0b" />
              </div>
              <h3 style={{
                margin: 0,
                fontSize: isMobile ? '18px' : '20px',
                fontWeight: '700',
                color: '#1e293b'
              }}>
                {editandoTrabajador ? 'Editar' : 'Nuevo'} Trabajador
              </h3>
            </div>
            
            <div style={{
              display: 'grid',
              gap: '16px'
            }}>
              <div style={{
                display: 'grid',
                gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
                gap: '16px'
              }}>
                <div>
                  <label style={{
                    display: 'block',
                    marginBottom: '8px',
                    fontSize: '14px',
                    fontWeight: '600',
                    color: '#374151'
                  }}>
                    Nombre *
                  </label>
                  <input
                    type="text"
                    placeholder="Juan"
                    value={formData.nombre}
                    onChange={(e) => setFormData({...formData, nombre: e.target.value})}
                    style={{
                      width: '100%',
                      padding: '14px 16px',
                      border: '2px solid #f1f5f9',
                      borderRadius: '10px',
                      fontSize: '16px',
                      boxSizing: 'border-box',
                      outline: 'none',
                      transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                      touchAction: 'manipulation'
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = '#f59e0b';
                      e.target.style.boxShadow = '0 0 0 3px rgba(245, 158, 11, 0.1)';
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = '#f1f5f9';
                      e.target.style.boxShadow = 'none';
                    }}
                  />
                </div>
                
                <div>
                  <label style={{
                    display: 'block',
                    marginBottom: '8px',
                    fontSize: '14px',
                    fontWeight: '600',
                    color: '#374151'
                  }}>
                    Apellido *
                  </label>
                  <input
                    type="text"
                    placeholder="Pérez"
                    value={formData.apellido}
                    onChange={(e) => setFormData({...formData, apellido: e.target.value})}
                    style={{
                      width: '100%',
                      padding: '14px 16px',
                      border: '2px solid #f1f5f9',
                      borderRadius: '10px',
                      fontSize: '16px',
                      boxSizing: 'border-box',
                      outline: 'none',
                      transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                      touchAction: 'manipulation'
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = '#f59e0b';
                      e.target.style.boxShadow = '0 0 0 3px rgba(245, 158, 11, 0.1)';
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = '#f1f5f9';
                      e.target.style.boxShadow = 'none';
                    }}
                  />
                </div>
              </div>

              <div>
                <label style={{
                  display: 'block',
                  marginBottom: '8px',
                  fontSize: '14px',
                  fontWeight: '600',
                  color: '#374151'
                }}>
                  Email * (para login al sistema)
                </label>
                <input
                  type="email"
                  placeholder="juan@constructora.com"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  style={{
                    width: '100%',
                    padding: '14px 16px',
                    border: '2px solid #f1f5f9',
                    borderRadius: '10px',
                    fontSize: '16px',
                    boxSizing: 'border-box',
                    outline: 'none',
                    transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                    touchAction: 'manipulation'
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = '#f59e0b';
                    e.target.style.boxShadow = '0 0 0 3px rgba(245, 158, 11, 0.1)';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = '#f1f5f9';
                    e.target.style.boxShadow = 'none';
                  }}
                />
                <p style={{
                  fontSize: '12px',
                  color: '#64748b',
                  margin: '4px 0 0 0',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px'
                }}>
                  <Info size={12} />
                  Con este email podrá usar "Primera vez Empleado" en el login
                </p>
              </div>
              
              <div style={{
                display: 'grid',
                gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
                gap: '16px'
              }}>
                <div>
                  <label style={{
                    display: 'block',
                    marginBottom: '8px',
                    fontSize: '14px',
                    fontWeight: '600',
                    color: '#374151'
                  }}>
                    Puesto
                  </label>
                  <input
                    type="text"
                    placeholder="Director General"
                    value={formData.puesto}
                    onChange={(e) => setFormData({...formData, puesto: e.target.value})}
                    style={{
                      width: '100%',
                      padding: '14px 16px',
                      border: '2px solid #f1f5f9',
                      borderRadius: '10px',
                      fontSize: '16px',
                      boxSizing: 'border-box',
                      outline: 'none',
                      transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                      touchAction: 'manipulation'
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = '#f59e0b';
                      e.target.style.boxShadow = '0 0 0 3px rgba(245, 158, 11, 0.1)';
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = '#f1f5f9';
                      e.target.style.boxShadow = 'none';
                    }}
                  />
                </div>
                
                <div>
                  <label style={{
                    display: 'block',
                    marginBottom: '8px',
                    fontSize: '14px',
                    fontWeight: '600',
                    color: '#374151'
                  }}>
                    Tipo
                  </label>
                  <AdminDropdown
                    value={formData.tipo}
                    onChange={(value) => setFormData({ ...formData, tipo: value })}
                    options={[
                      { value: 'empleado', label: 'Empleado' },
                      { value: 'socio', label: 'Socio' }
                    ]}
                    placeholder="Selecciona el tipo"
                  />
                </div>
              </div>
              
              <div style={{
                display: 'grid',
                gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
                gap: '16px'
              }}>
                <div>
                  <label style={{
                    display: 'block',
                    marginBottom: '8px',
                    fontSize: '14px',
                    fontWeight: '600',
                    color: '#374151'
                  }}>
                    Teléfono
                  </label>
                  <input
                    type="text"
                    placeholder="11-1234-5678"
                    value={formData.telefono}
                    onChange={(e) => setFormData({...formData, telefono: e.target.value})}
                    style={{
                      width: '100%',
                      padding: '14px 16px',
                      border: '2px solid #f1f5f9',
                      borderRadius: '10px',
                      fontSize: '16px',
                      boxSizing: 'border-box',
                      outline: 'none',
                      transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                      touchAction: 'manipulation'
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = '#f59e0b';
                      e.target.style.boxShadow = '0 0 0 3px rgba(245, 158, 11, 0.1)';
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = '#f1f5f9';
                      e.target.style.boxShadow = 'none';
                    }}
                  />
                </div>
                
                <div>
                  <label style={{
                    display: 'block',
                    marginBottom: '8px',
                    fontSize: '14px',
                    fontWeight: '600',
                    color: '#374151'
                  }}>
                    CUIT/CUIL
                  </label>
                  <input
                    type="text"
                    placeholder="20-12345678-9"
                    value={formData.cuit}
                    onChange={(e) => handleCuitChange(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '14px 16px',
                      border: '2px solid #f1f5f9',
                      borderRadius: '10px',
                      fontSize: '16px',
                      boxSizing: 'border-box',
                      outline: 'none',
                      transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                      touchAction: 'manipulation'
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = '#f59e0b';
                      e.target.style.boxShadow = '0 0 0 3px rgba(245, 158, 11, 0.1)';
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = '#f1f5f9';
                      e.target.style.boxShadow = 'none';
                    }}
                  />
                </div>
              </div>
            </div>
            
            <div style={{
              display: 'flex',
              gap: '12px',
              justifyContent: 'flex-end',
              marginTop: '24px'
            }}>
              <button
                onClick={() => {
                  setShowModal(false);
                  setEditandoTrabajador(null);
                  setFormData({
                    nombre: '',
                    apellido: '',
                    email: '',
                    puesto: '',
                    tipo: 'empleado',
                    telefono: '',
                    cuit: ''
                  });
                }}
                style={{
                  padding: '10px 20px',
                  border: '1px solid #d1d5db',
                  borderRadius: '8px',
                  background: 'white',
                  color: '#64748b',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)'
                }}
                className="button-hover"
              >
                Cancelar
              </button>
              <button
                onClick={guardarTrabajador}
                disabled={guardando}
                style={{
                  padding: '10px 20px',
                  border: 'none',
                  borderRadius: '8px',
                  background: guardando ? '#94a3b8' : 'linear-gradient(135deg, #2563eb 0%, #2563eb 100%)',
                  color: 'white',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: guardando ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)'
                }}
                className={!guardando ? "button-hover" : ""}
              >
                {guardando ? (
                  <>
                    <div className="loading-pulse" style={{
                      width: '16px',
                      height: '16px',
                      border: '2px solid rgba(255,255,255,0.3)',
                      borderTop: '2px solid white',
                      borderRadius: '50%',
                      animation: 'spin 1s linear infinite'
                    }}></div>
                    Guardando...
                  </>
                ) : (
                  <>
                    <Save size={16} />
                    {editandoTrabajador ? 'Actualizar' : 'Guardar'}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
// Datos Constructora - Con edición y upload de logo mejorado
function DatosConstructoraTab({ constructora, showCustomAlert }) {
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [editando, setEditando] = useState(false);
  const [formData, setFormData] = useState({
    nombre: '',
    email: '',
    cuit: ''
  });
  const [guardando, setGuardando] = useState(false);
  const [subiendoLogo, setSubiendoLogo] = useState(false);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (constructora) {
      setFormData({
        nombre: constructora.nombre || '',
        email: constructora.email || '',
        cuit: constructora.cuit || ''
      });
    }
  }, [constructora]);

  const guardarCambios = async () => {
    if (!formData.nombre.trim() || !formData.email.trim()) {
      showCustomAlert('error', 'Error', 'Por favor completa nombre y email');
      return;
    }

    setGuardando(true);
    try {
      const { data, error } = await supabase
        .from('constructoras')
        .update({
          nombre: formData.nombre.trim(),
          email: formData.email.trim(),
          cuit: formData.cuit.trim() || null,
          updated_at: new Date().toISOString()
        })
        .eq('id', constructora.id)
        .select();
      
      if (error) {
        console.error('Error actualizando constructora:', error);
        showCustomAlert('error', 'Error', 'Error al guardar: ' + error.message);
      } else {
        console.log('Constructora actualizada:', data);
        setEditando(false);
        showCustomAlert('success', 'Éxito', 'Datos actualizados correctamente');
      }
    } catch (err) {
      console.error('Error:', err);
      showCustomAlert('error', 'Error', 'Error: ' + err.message);
    }
    setGuardando(false);
  };

  const handleLogoUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      showCustomAlert('error', 'Error', 'Por favor selecciona un archivo de imagen');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      showCustomAlert('error', 'Error', 'El archivo es muy grande. Máximo 5MB');
      return;
    }

    setSubiendoLogo(true);
    
    try {
      const fileExt = file.name.split('.').pop();
      const fileName = `${constructora.id}-logo.${fileExt}`;
      
      console.log('Subiendo archivo:', fileName);
      
      const { data: uploadData, error: uploadError } = await supabase.storage
        .from('logos')
        .upload(fileName, file, { 
          upsert: true,
          contentType: file.type
        });

      if (uploadError) {
        throw uploadError;
      }

      console.log('Archivo subido:', uploadData);

      const { data: urlData } = supabase.storage
        .from('logos')
        .getPublicUrl(fileName);

      const logoUrl = urlData.publicUrl;
      console.log('URL generada:', logoUrl);

      const testImg = new Image();
      testImg.onload = async () => {
        console.log('Imagen verificada como válida');
        
        const { data, error } = await supabase
          .from('constructoras')
          .update({
            logo_url: logoUrl,
            updated_at: new Date().toISOString()
          })
          .eq('id', constructora.id)
          .select();

        if (error) {
          throw error;
        }

        console.log('Logo actualizado en DB:', logoUrl);
        showCustomAlert('success', 'Éxito', 'Logo actualizado correctamente');
        
        window.location.reload();
      };
      
      testImg.onerror = () => {
        console.error('La imagen no es accesible:', logoUrl);
        showCustomAlert('error', 'Error', 'Error: La imagen no es accesible. Verifica las políticas de Supabase.');
      };
      
      testImg.src = logoUrl;

    } catch (err) {
      console.error('Error subiendo logo:', err);
      showCustomAlert('error', 'Error', 'Error al subir logo: ' + err.message);
    }
    
    setSubiendoLogo(false);
  };

  return (
    <div style={{padding: isMobile ? '20px 16px' : '24px 32px'}}>
      {/* Header mejorado */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: '24px',
        flexWrap: 'wrap',
        gap: '16px'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1 }}>
          <div style={{
            width: '32px',
            height: '32px',
            background: '#64748b15',
            borderRadius: '8px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <Shield size={18} color="#64748b" />
          </div>
          <div>
            <h2 style={{
              fontSize: isMobile ? '18px' : '20px',
              fontWeight: '600',
              color: '#1e293b',
              margin: '0 0 4px 0'
            }}>
              Datos de la Constructora
            </h2>
            <p style={{
              fontSize: '13px',
              color: '#64748b',
              margin: 0
            }}>
              Información corporativa y configuración del logo
            </p>
          </div>
        </div>
        
        {!editando && (
          <button 
            onClick={() => setEditando(true)}
            style={{
              background: 'linear-gradient(135deg, #2563eb 0%, #2563eb 100%)',
              color: 'white',
              border: 'none',
              padding: isMobile ? '10px 16px' : '12px 20px',
              borderRadius: '10px',
              fontSize: isMobile ? '13px' : '14px',
              fontWeight: '600',
              cursor: 'pointer',
              boxShadow: '0 4px 16px rgba(37, 99, 235, 0.25)',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)'
            }}
            className="button-hover"
          >
            <Edit size={16} />
            Editar datos
          </button>
        )}
      </div>

      {editando ? (
        <div style={{
          background: 'white',
          border: '1px solid #f1f5f9',
          borderRadius: '16px',
          padding: isMobile ? '20px' : '24px'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            marginBottom: '24px'
          }}>
            <div style={{
              width: '32px',
              height: '32px',
              background: '#64748b15',
              borderRadius: '8px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <Edit size={18} color="#64748b" />
            </div>
            <h3 style={{
              margin: 0,
              fontSize: '18px',
              fontWeight: '600',
              color: '#1e293b'
            }}>
              Editar Información
            </h3>
          </div>
          
          <div style={{
            display: 'grid',
            gap: '20px'
          }}>
            <div style={{
              display: 'grid',
              gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
              gap: '20px'
            }}>
              <div>
                <label style={{
                  display: 'block',
                  marginBottom: '8px',
                  fontSize: '14px',
                  fontWeight: '600',
                  color: '#374151'
                }}>
                  Nombre de la Constructora *
                </label>
                <input
                  type="text"
                  value={formData.nombre}
                  onChange={(e) => setFormData({...formData, nombre: e.target.value})}
                  style={{
                    width: '100%',
                    padding: '14px 16px',
                    border: '2px solid #f1f5f9',
                    borderRadius: '10px',
                    fontSize: '16px',
                    boxSizing: 'border-box',
                    outline: 'none',
transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                    touchAction: 'manipulation'
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = '#64748b';
                    e.target.style.boxShadow = '0 0 0 3px rgba(100, 116, 139, 0.1)';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = '#f1f5f9';
                    e.target.style.boxShadow = 'none';
                  }}
                />
              </div>
              
              <div>
                <label style={{
                  display: 'block',
                  marginBottom: '8px',
                  fontSize: '14px',
                  fontWeight: '600',
                  color: '#374151'
                }}>
                  Email *
                </label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  style={{
                    width: '100%',
                    padding: '14px 16px',
                    border: '2px solid #f1f5f9',
                    borderRadius: '10px',
                    fontSize: '16px',
                    boxSizing: 'border-box',
                    outline: 'none',
                    transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                    touchAction: 'manipulation'
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = '#64748b';
                    e.target.style.boxShadow = '0 0 0 3px rgba(100, 116, 139, 0.1)';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = '#f1f5f9';
                    e.target.style.boxShadow = 'none';
                  }}
                />
              </div>
            </div>

            <div>
              <label style={{
                display: 'block',
                marginBottom: '8px',
                fontSize: '14px',
                fontWeight: '600',
                color: '#374151'
              }}>
                CUIT
              </label>
              <input
                type="text"
                value={formData.cuit}
                onChange={(e) => setFormData({...formData, cuit: e.target.value})}
                placeholder="XX-XXXXXXXX-X"
                style={{
                  width: '100%',
                  padding: '14px 16px',
                  border: '2px solid #f1f5f9',
                  borderRadius: '10px',
                  fontSize: '16px',
                  boxSizing: 'border-box',
                  outline: 'none',
                  transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                  touchAction: 'manipulation'
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = '#64748b';
                  e.target.style.boxShadow = '0 0 0 3px rgba(100, 116, 139, 0.1)';
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = '#f1f5f9';
                  e.target.style.boxShadow = 'none';
                }}
              />
            </div>

            {/* Sección del Logo */}
            <div>
              <label style={{
                display: 'block',
                marginBottom: '12px',
                fontSize: '14px',
                fontWeight: '600',
                color: '#374151'
              }}>
                Logo de la Constructora
              </label>
              
              <div style={{
                display: 'grid',
                gap: '16px',
                gridTemplateColumns: isMobile ? '1fr' : 'auto 1fr',
                alignItems: 'start'
              }}>
                <div style={{
                  width: isMobile ? '120px' : '140px',
                  height: isMobile ? '120px' : '140px',
                  border: '2px dashed #cbd5e1',
                  borderRadius: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: '#f8fafc',
                  overflow: 'hidden',
                  justifySelf: isMobile ? 'center' : 'start'
                }}>
                  {constructora?.logo_url ? (
                    <img 
                      src={constructora.logo_url} 
                      alt="Logo"
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'contain'
                      }}
                    />
                  ) : (
                    <div style={{
                      textAlign: 'center',
                      color: '#64748b',
                      fontSize: '13px'
                    }}>
                      <div style={{fontSize: '32px', marginBottom: '8px'}}>🏢</div>
                      Sin logo
                    </div>
                  )}
                </div>

                <div>
                  <input
                    type="file"
                    id="logo-upload"
                    accept="image/*"
                    onChange={handleLogoUpload}
                    style={{display: 'none'}}
                    disabled={subiendoLogo}
                  />
                  
                  <label
                    htmlFor="logo-upload"
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '8px',
                      padding: '12px 20px',
                      background: subiendoLogo 
                        ? '#94a3b8' 
                        : 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                      color: 'white',
                      borderRadius: '8px',
                      fontSize: '14px',
                      fontWeight: '600',
                      cursor: subiendoLogo ? 'not-allowed' : 'pointer',
                      marginBottom: '12px',
                      transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)'
                    }}
                    className={!subiendoLogo ? "button-hover" : ""}
                  >
                    {subiendoLogo ? (
                      <>
                        <div style={{
                          width: '16px',
                          height: '16px',
                          border: '2px solid rgba(255,255,255,0.3)',
                          borderTop: '2px solid white',
                          borderRadius: '50%',
                          animation: 'spin 1s linear infinite'
                        }}></div>
                        Subiendo...
                      </>
                    ) : (
                      <>
                        <Upload size={16} />
                        Seleccionar Logo
                      </>
                    )}
                  </label>

                  <div style={{
                    fontSize: '12px',
                    color: '#64748b',
                    lineHeight: '1.4'
                  }}>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px',
                      marginBottom: '4px'
                    }}>
                      <Info size={12} />
                      <span style={{ fontWeight: '500' }}>Requisitos:</span>
                    </div>
                    <ul style={{
                      margin: '0',
                      paddingLeft: '16px',
                      listStyle: 'disc'
                    }}>
                      <li>Formatos: PNG, JPG, SVG</li>
                      <li>Tamaño máximo: 5MB</li>
                      <li>Recomendado: 200x200px</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          <div style={{
            display: 'flex',
            gap: '12px',
            justifyContent: 'flex-end',
            marginTop: '32px',
            paddingTop: '24px',
            borderTop: '1px solid #f1f5f9'
          }}>
            <button
              onClick={() => {
                setEditando(false);
                setFormData({
                  nombre: constructora.nombre || '',
                  email: constructora.email || '',
                  cuit: constructora.cuit || ''
                });
              }}
              style={{
                padding: '10px 20px',
                border: '1px solid #d1d5db',
                borderRadius: '8px',
                background: 'white',
                color: '#64748b',
                fontSize: '14px',
                fontWeight: '500',
                cursor: 'pointer',
                transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)'
              }}
              className="button-hover"
            >
              Cancelar
            </button>
            <button
              onClick={guardarCambios}
              disabled={guardando || subiendoLogo}
              style={{
                padding: '10px 20px',
                border: 'none',
                borderRadius: '8px',
                background: (guardando || subiendoLogo)
                  ? '#94a3b8'
                  : 'linear-gradient(135deg, #64748b 0%, #475569 100%)',
                color: 'white',
                fontSize: '14px',
                fontWeight: '600',
                cursor: (guardando || subiendoLogo) ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)'
              }}
              className={!(guardando || subiendoLogo) ? "button-hover" : ""}
            >
              {guardando ? (
                <>
                  <div style={{
                    width: '16px',
                    height: '16px',
                    border: '2px solid rgba(255,255,255,0.3)',
                    borderTop: '2px solid white',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite'
                  }}></div>
                  Guardando...
                </>
              ) : (
                <>
                  <Save size={16} />
                  Guardar Cambios
                </>
              )}
            </button>
          </div>
        </div>
      ) : (
        <div style={{
          background: 'white',
          borderRadius: '16px',
          border: '1px solid #f1f5f9',
          overflow: 'hidden'
        }}>
          {/* Header de la información actual */}
          <div style={{
            background: 'linear-gradient(135deg, #64748b 0%, #475569 100%)',
            color: 'white',
            padding: isMobile ? '20px' : '24px',
            display: 'flex',
            alignItems: 'center',
            gap: '12px'
          }}>
            <div style={{
              width: '32px',
              height: '32px',
              background: 'rgba(255,255,255,0.2)',
              borderRadius: '8px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <Shield size={18} />
            </div>
            <div>
              <h3 style={{
                margin: 0,
                fontSize: '18px',
                fontWeight: '600'
              }}>
                Información Actual
              </h3>
              <p style={{
                margin: 0,
                fontSize: '13px',
                opacity: 0.9
              }}>
                Datos corporativos registrados
              </p>
            </div>
          </div>

          <div style={{
            padding: isMobile ? '20px' : '24px'
          }}>
            <div style={{
              display: 'grid',
              gap: '24px',
              gridTemplateColumns: isMobile ? '1fr' : 'auto 1fr',
              alignItems: 'start'
            }}>
              {/* Logo */}
              <div style={{
                width: '100px',
                height: '100px',
                border: '1px solid #e2e8f0',
                borderRadius: '12px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: '#f8fafc',
                overflow: 'hidden',
                justifySelf: isMobile ? 'center' : 'start'
              }}>
                {constructora?.logo_url ? (
                  <img 
                    src={constructora.logo_url} 
                    alt="Logo"
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'contain'
                    }}
                  />
                ) : (
                  <div style={{
                    textAlign: 'center',
                    color: '#64748b',
                    fontSize: '12px'
                  }}>
                    <div style={{fontSize: '24px', marginBottom: '4px'}}>🏢</div>
                    Sin logo
                  </div>
                )}
              </div>

              {/* Datos */}
              <div style={{
                display: 'grid',
                gap: '16px'
              }}>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)',
                  gap: '16px'
                }}>
                  <div style={{
                    background: '#f8fafc',
                    padding: '16px',
                    borderRadius: '10px',
                    border: '1px solid #e2e8f0'
                  }}>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      marginBottom: '8px'
                    }}>
                      <Building2 size={16} color="#64748b" />
                      <span style={{
                        fontSize: '12px',
                        fontWeight: '600',
                        color: '#64748b',
                        textTransform: 'uppercase'
                      }}>
                        Nombre
                      </span>
                    </div>
                    <p style={{
                      margin: 0,
                      fontSize: '16px',
                      fontWeight: '600',
                      color: '#1e293b'
                    }}>
                      {constructora?.nombre}
                    </p>
                  </div>

                  <div style={{
                    background: '#f8fafc',
                    padding: '16px',
                    borderRadius: '10px',
                    border: '1px solid #e2e8f0'
                  }}>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      marginBottom: '8px'
                    }}>
                      <Mail size={16} color="#64748b" />
                      <span style={{
                        fontSize: '12px',
                        fontWeight: '600',
                        color: '#64748b',
                        textTransform: 'uppercase'
                      }}>
                        Email
                      </span>
                    </div>
                    <p style={{
                      margin: 0,
                      fontSize: '16px',
                      fontWeight: '600',
                      color: '#1e293b'
                    }}>
                      {constructora?.email}
                    </p>
                  </div>
                </div>

                <div style={{
                  display: 'grid',
                  gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)',
                  gap: '16px'
                }}>
                  <div style={{
                    background: '#f8fafc',
                    padding: '16px',
                    borderRadius: '10px',
                    border: '1px solid #e2e8f0'
                  }}>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      marginBottom: '8px'
                    }}>
                      <CreditCard size={16} color="#64748b" />
                      <span style={{
                        fontSize: '12px',
                        fontWeight: '600',
                        color: '#64748b',
                        textTransform: 'uppercase'
                      }}>
                        CUIT
                      </span>
                    </div>
                    <p style={{
                      margin: 0,
                      fontSize: '16px',
                      fontWeight: '600',
                      color: '#1e293b'
                    }}>
                      {constructora?.cuit || 'Sin especificar'}
                    </p>
                  </div>

                  <div style={{
                    background: '#f8fafc',
                    padding: '16px',
                    borderRadius: '10px',
                    border: '1px solid #e2e8f0'
                  }}>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      marginBottom: '8px'
                    }}>
                      <Calendar size={16} color="#64748b" />
                      <span style={{
                        fontSize: '12px',
                        fontWeight: '600',
                        color: '#64748b',
                        textTransform: 'uppercase'
                      }}>
                        Creado
                      </span>
                    </div>
                    <p style={{
                      margin: 0,
                      fontSize: '16px',
                      fontWeight: '600',
                      color: '#1e293b'
                    }}>
                      {new Date(constructora?.created_at).toLocaleDateString('es-ES')}
                    </p>
                  </div>
                </div>

                {/* Estado del logo */}
                <div style={{
                  background: constructora?.logo_url ? '#f0fdf4' : '#fef2f2',
                  padding: '16px',
                  borderRadius: '10px',
                  border: `1px solid ${constructora?.logo_url ? '#bbf7d0' : '#fecaca'}`,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px'
                }}>
                  <div style={{
                    width: '24px',
                    height: '24px',
                    borderRadius: '12px',
                    background: constructora?.logo_url ? '#22c55e' : '#ef4444',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    {constructora?.logo_url ? (
                      <CheckCircle2 size={14} color="white" />
                    ) : (
                      <XCircle size={14} color="white" />
                    )}
                  </div>
                  <div>
                    <p style={{
                      margin: 0,
                      fontSize: '14px',
                      fontWeight: '600',
                      color: constructora?.logo_url ? '#166534' : '#991b1b'
                    }}>
                      Logo {constructora?.logo_url ? 'Configurado' : 'No Configurado'}
                    </p>
                    <p style={{
                      margin: 0,
                      fontSize: '12px',
                      color: constructora?.logo_url ? '#059669' : '#dc2626'
                    }}>
                      {constructora?.logo_url 
                        ? 'El logo se muestra en el sidebar' 
                        : 'Configura un logo para personalizar la aplicación'
                      }
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
// Índices CAC - Mejorado
function IndicesCacTab({ constructora, showCustomAlert }) {
  const [indices, setIndices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editandoIndice, setEditandoIndice] = useState(null);
  const [formData, setFormData] = useState({
    fecha: '',
    cac_general: '',
    cac_materiales: '',
    cac_mano_obra: '',
    observaciones: ''
  });
  const [guardando, setGuardando] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const cargarIndices = async () => {
    try {
      const { data, error } = await supabase
        .from('indices_cac')
        .select('*')
        .order('fecha', { ascending: false });
      
      if (error) {
        console.error('Error cargando índices CAC:', error);
      } else {
        setIndices(data || []);
      }
    } catch (err) {
      console.error('Error:', err);
    }
    setLoading(false);
  };

  useEffect(() => {
    cargarIndices();
  }, []);

  const abrirModal = (indice = null) => {
    if (indice) {
      setEditandoIndice(indice);
      const fechaParaInput = indice.fecha.substring(0, 7);
      setFormData({
        fecha: fechaParaInput,
        cac_general: indice.cac_general.toString(),
        cac_materiales: indice.cac_materiales.toString(),
        cac_mano_obra: indice.cac_mano_obra.toString(),
        observaciones: indice.observaciones || ''
      });
    } else {
      setEditandoIndice(null);
      setFormData({
        fecha: '',
        cac_general: '',
        cac_materiales: '',
        cac_mano_obra: '',
        observaciones: ''
      });
    }
    setShowModal(true);
  };

  const guardarIndice = async () => {
    if (!formData.fecha || !formData.cac_general || !formData.cac_materiales || !formData.cac_mano_obra) {
      showCustomAlert('error', 'Error', 'Por favor completa todos los campos obligatorios');
      return;
    }

    const cacGeneral = parseFloat(formData.cac_general);
    const cacMateriales = parseFloat(formData.cac_materiales);
    const cacManoObra = parseFloat(formData.cac_mano_obra);

    if (isNaN(cacGeneral) || isNaN(cacMateriales) || isNaN(cacManoObra)) {
      showCustomAlert('error', 'Error', 'Los valores CAC deben ser números válidos');
      return;
    }

    if (cacGeneral <= 0 || cacMateriales <= 0 || cacManoObra <= 0) {
      showCustomAlert('error', 'Error', 'Los valores CAC deben ser mayores a 0');
      return;
    }

    setGuardando(true);
    try {
      const fechaCompleta = `${formData.fecha}-01`;

      const dataToSave = {
        fecha: fechaCompleta,
        cac_general: cacGeneral,
        cac_materiales: cacMateriales,
        cac_mano_obra: cacManoObra,
        observaciones: formData.observaciones.trim() || null
      };

      let result;
      if (editandoIndice) {
        result = await supabase
          .from('indices_cac')
          .update(dataToSave)
          .eq('id', editandoIndice.id)
          .select();
      } else {
        result = await supabase
          .from('indices_cac')
          .insert([dataToSave])
          .select();
      }

      const { data, error } = result;

      if (error) {
        console.error('Error guardando índice:', error);
        if (error.code === '23505') {
          showCustomAlert('error', 'Error', 'Ya existe un índice CAC para esta fecha');
        } else {
          showCustomAlert('error', 'Error', 'Error al guardar: ' + error.message);
        }
      } else {
        showCustomAlert('success', 'Éxito', editandoIndice ? 'Índice actualizado correctamente' : 'Índice creado correctamente');
        setShowModal(false);
        setEditandoIndice(null);
        setFormData({
          fecha: '',
          cac_general: '',
          cac_materiales: '',
          cac_mano_obra: '',
          observaciones: ''
        });
        cargarIndices();
      }
    } catch (err) {
      console.error('Error:', err);
      showCustomAlert('error', 'Error', 'Error: ' + err.message);
    }
    setGuardando(false);
  };

  const eliminarIndice = async (id, fecha) => {
    showCustomAlert('confirm', 
      '¿Eliminar índice CAC?', 
      `¿Estás seguro de eliminar el índice CAC de ${formatDateForDisplay(fecha)}?\n\nEsta acción no se puede deshacer.`,
      async () => {
        try {
          const { error } = await supabase
            .from('indices_cac')
            .delete()
            .eq('id', id);
          
          if (error) {
            console.error('Error eliminando índice:', error);
            showCustomAlert('error', 'Error', 'Error al eliminar: ' + error.message);
          } else {
            showCustomAlert('success', 'Éxito', 'Índice eliminado correctamente');
            cargarIndices();
          }
        } catch (err) {
          console.error('Error:', err);
          showCustomAlert('error', 'Error', 'Error: ' + err.message);
        }
      }
    );
  };

  const formatDateForDisplay = (dateString) => {
    if (!dateString) return '';
    const [year, month] = dateString.split('-');
    const monthNames = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];
    return `${monthNames[parseInt(month) - 1]} ${year}`;
  };

  

  return (
    <div style={{padding: isMobile ? '20px 16px' : '24px 32px'}}>
      {/* Header mejorado */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: '24px',
        flexWrap: 'wrap',
        gap: '16px'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1 }}>
          <div style={{
            width: '32px',
            height: '32px',
            background: '#ef444415',
            borderRadius: '8px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <TrendingUp size={18} color="#ef4444" />
          </div>
          <div>
            <h2 style={{
              fontSize: isMobile ? '18px' : '20px',
              fontWeight: '600',
              color: '#1e293b',
              margin: '0 0 4px 0'
            }}>
              Índices CAC
            </h2>
            <p style={{
              fontSize: '13px',
              color: '#64748b',
              margin: 0
            }}>
              Coeficientes de Ajuste de Costos mensuales de CAMARCO
            </p>
          </div>
        </div>
        
        <button 
          onClick={() => abrirModal()}
          style={{
            background: 'linear-gradient(135deg, #2563eb 0%, #2563eb 100%)',
            color: 'white',
            border: 'none',
            padding: isMobile ? '10px 16px' : '12px 20px',
            borderRadius: '10px',
            fontSize: isMobile ? '13px' : '14px',
            fontWeight: '600',
            cursor: 'pointer',
            boxShadow: '0 4px 16px rgba(37, 99, 235, 0.25)',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)'
          }}
          className="button-hover"
        >
          <Plus size={16} />
          Añadir índice
        </button>
      </div>

      {loading ? (
        <div style={{
          textAlign: 'center',
          padding: '60px 20px',
          color: '#64748b'
        }}>
          <div style={{
            display: 'inline-block',
            width: '32px',
            height: '32px',
            border: '3px solid #e2e8f0',
            borderTop: '3px solid #ef4444',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            marginBottom: '16px'
          }}></div>
          <p style={{ fontSize: '15px', margin: 0 }}>Cargando índices...</p>
        </div>
      ) : indices.length === 0 ? (
        <div style={{
          background: '#fef2f2',
          borderRadius: '12px',
          padding: isMobile ? '40px 20px' : '60px 40px',
          textAlign: 'center',
          border: '2px dashed #fecaca'
        }}>
          <div style={{
            width: '64px',
            height: '64px',
            background: '#fecaca',
            borderRadius: '32px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 20px'
          }}>
            <TrendingUp size={32} color="#ef4444" />
          </div>
          <div style={{
            fontSize: '18px',
            fontWeight: '600',
            color: '#991b1b',
            marginBottom: '8px'
          }}>
            No hay índices CAC cargados
          </div>    ← CORREGIDO
          <p style={{
            fontSize: '14px',
            color: '#dc2626',
            marginBottom: '24px'
          }}>
            Los índices CAC se publican mensualmente por CAMARCO para actualizar contratos
          </p>
          <button
            onClick={() => abrirModal()}
            style={{
              background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
              color: 'white',
              border: 'none',
              padding: '12px 24px',
              borderRadius: '8px',
              fontSize: '14px',
              fontWeight: '600',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              margin: '0 auto'
            }}
            className="button-hover"
          >
            <Plus size={16} />
            Cargar Primer Índice
          </button>
        </div>
      ) : (
        <div style={{
          background: 'white',
          border: '1px solid #f1f5f9',
          borderRadius: '12px',
          overflow: 'hidden'
        }}>
          <div style={{overflowX: 'auto'}}>
            <table style={{
              width: '100%',
              borderCollapse: 'collapse',
              minWidth: isMobile ? '700px' : '900px'
            }}>
              <thead>
                <tr style={{
                  background: '#f8fafc',
                  borderBottom: '1px solid #e2e8f0'
                }}>
                  <th style={{
                    padding: isMobile ? '12px 16px' : '16px 20px',
                    textAlign: 'left',
                    fontWeight: '600',
                    color: '#374151',
                    fontSize: isMobile ? '12px' : '14px'
                  }}>
                    Mes/Año
                  </th>
                  <th style={{
                    padding: isMobile ? '12px 16px' : '16px 20px',
                    textAlign: 'center',
                    fontWeight: '600',
                    color: '#374151',
                    fontSize: isMobile ? '12px' : '14px'
                  }}>
                    CAC General
                  </th>
                  <th style={{
                    padding: isMobile ? '12px 16px' : '16px 20px',
                    textAlign: 'center',
                    fontWeight: '600',
                    color: '#374151',
                    fontSize: isMobile ? '12px' : '14px'
                  }}>
                    CAC Materiales
                  </th>
                  <th style={{
                    padding: isMobile ? '12px 16px' : '16px 20px',
                    textAlign: 'center',
                    fontWeight: '600',
                    color: '#374151',
                    fontSize: isMobile ? '12px' : '14px'
                  }}>
                    CAC Mano Obra
                  </th>
                  {!isMobile && (
                    <th style={{
                      padding: '16px 20px',
                      textAlign: 'left',
                      fontWeight: '600',
                      color: '#374151',
                      fontSize: '14px'
                    }}>
                      Observaciones
                    </th>
                  )}
                  <th style={{
                    padding: isMobile ? '12px 16px' : '16px 20px',
                    textAlign: 'center',
                    fontWeight: '600',
                    color: '#374151',
                    fontSize: isMobile ? '12px' : '14px',
                    width: isMobile ? '120px' : '160px'
                  }}>
                    Acciones
                  </th>
                </tr>
              </thead>
              <tbody>
                {indices.map((indice, index) => (
                  <tr key={indice.id} style={{
                    borderBottom: index < indices.length - 1 ? '1px solid #f1f5f9' : 'none',
                    transition: 'background-color 0.2s ease'
                  }}
                  onMouseEnter={(e) => {
                    if (!isMobile) e.target.parentElement.style.backgroundColor = '#f8fafc';
                  }}
                  onMouseLeave={(e) => {
                    if (!isMobile) e.target.parentElement.style.backgroundColor = 'transparent';
                  }}
                  >
                    <td style={{
                      padding: isMobile ? '12px 16px' : '16px 20px',
                      color: '#1e293b',
                      fontSize: isMobile ? '13px' : '14px',
                      fontWeight: '600'
                    }}>
                      {formatDateForDisplay(indice.fecha)}
                    </td>
                    <td style={{
                      padding: isMobile ? '12px 16px' : '16px 20px',
                      textAlign: 'center',
                      fontSize: isMobile ? '12px' : '13px'
                    }}>
                      <span style={{
                        background: '#dcfce7',
                        color: '#166534',
                        padding: '4px 8px',
                        borderRadius: '6px',
                        fontSize: isMobile ? '11px' : '12px',
                        fontWeight: '600'
                      }}>
                        {Number(indice.cac_general).toLocaleString('es-AR', {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2
                        })}
                      </span>
                    </td>
                    <td style={{
                      padding: isMobile ? '12px 16px' : '16px 20px',
                      textAlign: 'center',
                      fontSize: isMobile ? '12px' : '13px'
                    }}>
                      <span style={{
                        background: '#dbeafe',
                        color: '#1d4ed8',
                        padding: '4px 8px',
                        borderRadius: '6px',
                        fontSize: isMobile ? '11px' : '12px',
                        fontWeight: '600'
                      }}>
                        {Number(indice.cac_materiales).toLocaleString('es-AR', {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2
                        })}
                      </span>
                    </td>
                    <td style={{
                      padding: isMobile ? '12px 16px' : '16px 20px',
                      textAlign: 'center',
                      fontSize: isMobile ? '12px' : '13px'
                    }}>
                      <span style={{
                        background: '#f3e8ff',
                        color: '#7c3aed',
                        padding: '4px 8px',
                        borderRadius: '6px',
                        fontSize: isMobile ? '11px' : '12px',
                        fontWeight: '600'
                      }}>
                        {Number(indice.cac_mano_obra).toLocaleString('es-AR', {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2
                        })}
                      </span>
                    </td>
                    {!isMobile && (
                      <td style={{
                        padding: '16px 20px',
                        color: '#64748b',
                        fontSize: '12px',
                        maxWidth: '200px'
                      }}>
                        <div style={{
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}>
                          {indice.observaciones || '-'}
                        </div>
                      </td>
                    )}
                    <td style={{
                      padding: isMobile ? '12px 16px' : '16px 20px',
                      textAlign: 'center'
                    }}>
                      <div style={{
                        display: 'flex',
                        gap: '8px',
                        justifyContent: 'center'
                      }}>
                        <button
                          onClick={() => abrirModal(indice)}
                          style={{
                            background: '#eff6ff',
                            border: '1px solid #3b82f6',
                            borderRadius: '6px',
                            padding: isMobile ? '6px 8px' : '8px 12px',
                            color: '#3b82f6',
                            fontSize: isMobile ? '11px' : '12px',
                            fontWeight: '500',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px',
                            transition: 'all 0.2s ease'
                          }}
                          onMouseEnter={(e) => {
                            e.target.style.background = '#dbeafe';
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.background = '#eff6ff';
                          }}
                        >
                          <Edit size={12} />
                          Editar
                        </button>
                        <button
                          onClick={() => eliminarIndice(indice.id, indice.fecha)}
                          style={{
                            background: '#fef2f2',
                            border: '1px solid #ef4444',
                            borderRadius: '6px',
                            padding: isMobile ? '6px 8px' : '8px 12px',
                            color: '#ef4444',
                            fontSize: isMobile ? '11px' : '12px',
                            fontWeight: '500',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px',
                            transition: 'all 0.2s ease'
                          }}
                          onMouseEnter={(e) => {
                            e.target.style.background = '#fee2e2';
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.background = '#fef2f2';
                          }}
                        >
                          <Trash2 size={12} />
                          Eliminar
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
      {/* Modal para crear/editar índice */}
      {showModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.6)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: isMobile ? '20px' : '0',
          animation: 'fadeIn 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
        }}>
          <div style={{
            background: 'white',
            borderRadius: '16px',
            padding: isMobile ? '20px' : '24px',
            width: isMobile ? '100%' : '500px',
            maxWidth: '90vw',
            maxHeight: '90vh',
            overflowY: 'auto',
            boxShadow: '0 25px 80px rgba(0,0,0,0.3)',
            animation: 'scaleIn 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              marginBottom: '20px'
            }}>
              <div style={{
                width: '32px',
                height: '32px',
                background: '#ef444415',
                borderRadius: '8px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <TrendingUp size={18} color="#ef4444" />
              </div>
              <h3 style={{
                margin: 0,
                fontSize: isMobile ? '18px' : '20px',
                fontWeight: '700',
                color: '#1e293b'
              }}>
                {editandoIndice ? 'Editar' : 'Nuevo'} Índice CAC
              </h3>
            </div>
            
            <div style={{ display: 'grid', gap: '16px' }}>
              <div>
                <label style={{
                  display: 'block',
                  marginBottom: '8px',
                  fontSize: '14px',
                  fontWeight: '600',
                  color: '#374151'
                }}>
                  Mes/Año *
                </label>
                <input
                  type="month"
                  value={formData.fecha}
                  onChange={(e) => setFormData({...formData, fecha: e.target.value})}
                  style={{
                    width: '100%',
                    padding: '14px 16px',
                    border: '2px solid #f1f5f9',
                    borderRadius: '10px',
                    fontSize: '16px',
                    boxSizing: 'border-box',
                    outline: 'none',
                    transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                    touchAction: 'manipulation'
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = '#ef4444';
                    e.target.style.boxShadow = '0 0 0 3px rgba(239, 68, 68, 0.1)';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = '#f1f5f9';
                    e.target.style.boxShadow = 'none';
                  }}
                />
              </div>

              <div style={{
                display: 'grid',
                gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)',
                gap: '12px'
              }}>
                <div>
                  <label style={{
                    display: 'block',
                    marginBottom: '8px',
                    fontSize: '14px',
                    fontWeight: '600',
                    color: '#374151'
                  }}>
                    CAC General *
                  </label>
                  <input
                    type="number"
                    step="0.0001"
                    value={formData.cac_general}
                    onChange={(e) => setFormData({...formData, cac_general: e.target.value})}
                    placeholder="1450.5000"
                    style={{
                      width: '100%',
                      padding: '14px 16px',
                      border: '2px solid #f1f5f9',
                      borderRadius: '10px',
                      fontSize: '16px',
                      boxSizing: 'border-box',
                      outline: 'none',
                      transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                      touchAction: 'manipulation'
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = '#ef4444';
                      e.target.style.boxShadow = '0 0 0 3px rgba(239, 68, 68, 0.1)';
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = '#f1f5f9';
                      e.target.style.boxShadow = 'none';
                    }}
                  />
                </div>

                <div>
                  <label style={{
                    display: 'block',
                    marginBottom: '8px',
                    fontSize: '14px',
                    fontWeight: '600',
                    color: '#374151'
                  }}>
                    CAC Materiales *
                  </label>
                  <input
                    type="number"
                    step="0.0001"
                    value={formData.cac_materiales}
                    onChange={(e) => setFormData({...formData, cac_materiales: e.target.value})}
                    placeholder="1425.2000"
                    style={{
                      width: '100%',
                      padding: '14px 16px',
                      border: '2px solid #f1f5f9',
                      borderRadius: '10px',
                      fontSize: '16px',
                      boxSizing: 'border-box',
                      outline: 'none',
                      transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                      touchAction: 'manipulation'
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = '#ef4444';
                      e.target.style.boxShadow = '0 0 0 3px rgba(239, 68, 68, 0.1)';
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = '#f1f5f9';
                      e.target.style.boxShadow = 'none';
                    }}
                  />
                </div>

                <div>
                  <label style={{
                    display: 'block',
                    marginBottom: '8px',
                    fontSize: '14px',
                    fontWeight: '600',
                    color: '#374151'
                  }}>
                    CAC Mano Obra *
                  </label>
                  <input
                    type="number"
                    step="0.0001"
                    value={formData.cac_mano_obra}
                    onChange={(e) => setFormData({...formData, cac_mano_obra: e.target.value})}
                    placeholder="1475.8000"
                    style={{
                      width: '100%',
                      padding: '14px 16px',
                      border: '2px solid #f1f5f9',
                      borderRadius: '10px',
                      fontSize: '16px',
                      boxSizing: 'border-box',
                      outline: 'none',
                      transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                      touchAction: 'manipulation'
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = '#ef4444';
                      e.target.style.boxShadow = '0 0 0 3px rgba(239, 68, 68, 0.1)';
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = '#f1f5f9';
                      e.target.style.boxShadow = 'none';
                    }}
                  />
                </div>
              </div>

              <div>
                <label style={{
                  display: 'block',
                  marginBottom: '8px',
                  fontSize: '14px',
                  fontWeight: '600',
                  color: '#374151'
                }}>
                  Observaciones
                </label>
                <textarea
                  value={formData.observaciones}
                  onChange={(e) => setFormData({...formData, observaciones: e.target.value})}
                  placeholder="Ej: Incremento por paritarias UOCRA..."
                  rows="3"
                  style={{
                    width: '100%',
                    padding: '14px 16px',
                    border: '2px solid #f1f5f9',
                    borderRadius: '10px',
                    fontSize: '16px',
                    boxSizing: 'border-box',
                    outline: 'none',
                    resize: 'vertical',
                    transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                    touchAction: 'manipulation'
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = '#ef4444';
                    e.target.style.boxShadow = '0 0 0 3px rgba(239, 68, 68, 0.1)';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = '#f1f5f9';
                    e.target.style.boxShadow = 'none';
                  }}
                />
              </div>

              <div style={{
                background: '#fef2f2',
                padding: '16px',
                borderRadius: '10px',
                border: '1px solid #fecaca'
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  marginBottom: '8px'
                }}>
                  <Info size={16} color="#dc2626" />
                  <span style={{
                    fontSize: '14px',
                    fontWeight: '600',
                    color: '#dc2626'
                  }}>
                    Información CAC
                  </span>
                </div>
                <p style={{
                  fontSize: '13px',
                  color: '#991b1b',
                  margin: 0,
                  lineHeight: '1.4'
                }}>
                  Los índices CAC son publicados mensualmente por CAMARCO y se utilizan para 
                  actualizar contratos de obra por inflación según la Ley de Obra Pública.
                </p>
              </div>
            </div>
            
            <div style={{
              display: 'flex',
              gap: '12px',
              justifyContent: 'flex-end',
              marginTop: '24px'
            }}>
              <button
                onClick={() => {
                  setShowModal(false);
                  setEditandoIndice(null);
                  setFormData({
                    fecha: '',
                    cac_general: '',
                    cac_materiales: '',
                    cac_mano_obra: '',
                    observaciones: ''
                  });
                }}
                style={{
                  padding: '10px 20px',
                  border: '1px solid #d1d5db',
                  borderRadius: '8px',
                  background: 'white',
                  color: '#64748b',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)'
                }}
                className="button-hover"
              >
                Cancelar
              </button>
              <button
                onClick={guardarIndice}
                disabled={guardando}
                style={{
                  padding: '10px 20px',
                  border: 'none',
                  borderRadius: '8px',
                  background: guardando ? '#94a3b8' : 'linear-gradient(135deg, #2563eb 0%, #2563eb 100%)',
                  color: 'white',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: guardando ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)'
                }}
                className={!guardando ? "button-hover" : ""}
              >
                {guardando ? (
                  <>
                    <div style={{
                      width: '16px',
                      height: '16px',
                      border: '2px solid rgba(255,255,255,0.3)',
                      borderTop: '2px solid white',
                      borderRadius: '50%',
                      animation: 'spin 1s linear infinite'
                    }}></div>
                    Guardando...
                  </>
                ) : (
                  <>
                    <Save size={16} />
                    {editandoIndice ? 'Actualizar' : 'Guardar'}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      </div>
  );
}

export default Admin;